import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  ChevronDown,
  ChevronRight,
  Copy,
  CreditCard,
  Download,
  Eye,
  EyeOff,
  FileClock,
  FileText,
  Headphones,
  Infinity,
  KeyRound,
  LayoutDashboard,
  LockKeyhole,
  LogOut,
  Mail,
  MessageCircle,
  Pencil,
  Phone,
  QrCode,
  Search,
  RefreshCw,
  Settings,
  ShieldCheck,
  Trash2,
  UserRound,
  UsersRound,
  X,
  Zap,
} from "lucide-react";
import {
  getSiteDownloadRelease,
  siteAttachEmail,
  siteCancelPayment,
  siteCreateModerator,
  siteCreatePayment,
  siteCreateProject,
  siteDashboard as fetchSiteDashboard,
  siteDeveloperDashboard as fetchSiteDeveloperDashboard,
  siteDeveloperCreatePromoCode,
  siteDeveloperListPromoCodes,
  siteDeveloperRequestVerification,
  siteDeveloperUpdatePromoCode,
  siteDeveloperVerify,
  siteDownloadStats,
  siteExchangePasswordReset,
  siteForgotPassword,
  siteLogin,
  siteLogout,
  siteModeratorCancelCheck,
  siteModeratorCreateCheck,
  siteModeratorDashboard as fetchSiteModeratorDashboard,
  siteModeratorFinishCheck,
  siteOwnerCancelCheck,
  siteOwnerCreateCheck,
  siteOwnerFinishCheck,
  siteOwnerModeratorStats,
  siteGetSecuritySettings,
  siteTwoFactorDisable,
  siteCompleteTwoFactorLogin,
  siteTwoFactorLoginSetupConfirm,
  siteTwoFactorLoginSetupStart,
  siteTwoFactorSetupConfirm,
  siteTwoFactorSetupStart,
  siteOAuthComplete,
  siteOAuthStart,
  siteRemoveModerator,
  siteRenameModerator,
  siteRegister,
  siteResendVerification,
  siteResetPassword,
  siteSession,
  sitePaymentConfig as fetchSitePaymentConfig,
  sitePaymentStatus,
  sitePromoPreflight,
  siteRequestProjectDeletion,
  siteConfirmProjectDeletion,
  siteSteamComplete,
  siteSteamStart,
  siteUpdateLanguage,
  siteUpdateProjectInfo,
  type LoaderRelease,
  type SiteDashboard,
  type SiteDeveloperDashboard,
  type SiteDeveloperPromoCode,
  type SiteModeratorDashboard,
  type SiteModeratorCheckResult,
  type SiteOwnerModeratorStats,
  type SitePaymentConfig,
  type SitePaymentOrder,
  type SitePromoPreflight,
  type SiteSubscriptionLifecycle,
  type SiteSubscriptionPlan,
  type SiteSecuritySettings,
  type SubscriptionPlanId,
  type SiteTwoFactorSetup,
  type SiteUser,
} from "./api";
import { getDailyBars } from "./dailyBars";
import { MAX_MODERATOR_NAME_LENGTH, limitModeratorName, moderatorNameError } from "./moderatorName";

type AuthMode = "login" | "register" | "forgot" | "reset";
type AuthProvider = "google" | "yandex";
type Lang = "ru" | "en";

const FALLBACK_SUBSCRIPTION_PLANS: SiteSubscriptionPlan[] = [
  { id: "monthly", months: 1, amount: 2499, listAmount: 2999, monthlyEquivalent: 2499, savings: 500, discountPercent: 17, maxModerators: 5, labels: { ru: "1 месяц", en: "1 month" }, recommended: false, active: true },
  { id: "quarterly", months: 3, amount: 6799, listAmount: 7499, monthlyEquivalent: 2266.33, savings: 700, discountPercent: 10, maxModerators: 7, labels: { ru: "3 месяца", en: "3 months" }, recommended: false, active: true },
  { id: "annual", months: 12, amount: 22499, listAmount: 29999, monthlyEquivalent: 1874.92, savings: 7500, discountPercent: 25, maxModerators: 9, labels: { ru: "12 месяцев", en: "12 months" }, recommended: true, active: true },
];

const FALLBACK_SUBSCRIPTION_PLANS_EN: SiteSubscriptionPlan[] = [
  { id: "monthly", months: 1, amount: 35, currency: "USD", monthlyEquivalent: 35, savings: 0, discountPercent: 17, maxModerators: 5, labels: { ru: "1 месяц", en: "1 month" }, recommended: false, active: true },
  { id: "quarterly", months: 3, amount: 95, currency: "USD", monthlyEquivalent: 31.67, savings: 10, discountPercent: 10, maxModerators: 7, labels: { ru: "3 месяца", en: "3 months" }, recommended: false, active: true },
  { id: "annual", months: 12, amount: 299, currency: "USD", monthlyEquivalent: 24.92, savings: 121, discountPercent: 25, maxModerators: 9, labels: { ru: "12 месяцев", en: "12 months" }, recommended: true, active: true },
];

type AuthForm = {
  email: string;
  password: string;
};

type TwoFactorStepUpRequest = { resolve: (code: string | null) => void };
type TwoFactorLoginChallenge =
  | { kind: "email" }
  | { kind: "steam_login"; twoFactorLoginSession: string }
  | { kind: "steam_setup"; twoFactorLoginSession: string };

type PurchaseForm = {
  displayName: string;
  game: "DayZ" | "Rust";
  ownerDisplayName: string;
};

type ModeratorForm = {
  name: string;
  steamId64: string;
};

type ConfirmationRequest = {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  icon: "logout" | "delete";
  resolve: (confirmed: boolean) => void;
};

type RenameRequest = {
  title: string;
  message: string;
  inputLabel: string;
  confirmLabel: string;
  cancelLabel: string;
  initialValue: string;
  resolve: (value: string | null) => void;
};

type OAuthCallback =
  | {
      ok: true;
      provider: AuthProvider;
      code: string;
      state: string;
      redirectUri: string;
    }
  | {
      ok: false;
      provider: AuthProvider;
      reason: string;
    }
  | null;

const SITE_TOKEN_KEY = "ccbe_site_token";
const LANG_KEY = "ccbe_site_lang";
const USER_CACHE_KEY = "ccbe_site_user_cache";
const DASHBOARD_CACHE_KEY = "ccbe_site_dashboard_cache";
const PAYMENT_CONFIG_CACHE_PREFIX = "ccbe_site_payment_config_cache";
const SELECTED_PLAN_KEY = "ccbe_site_selected_plan";
const RELEASE_CACHE_KEY = "ccbe_site_release_cache";
const DOWNLOAD_COUNT_CACHE_KEY = "ccbe_site_download_count_cache";
const ACCOUNT_TAB_KEY = "ccbe_site_account_tab";
const PAYMENT_RETURN_KEY = "ccbe_site_payment_return_invoice";
const CHECKOUT_EMAIL_RESUME_KEY = "ccbe_site_checkout_email_resume";
const PASSWORD_RESET_SESSION_KEY = "ccbe_site_password_reset_session";
const STEAM_CALLBACK_ERROR_KEY = "ccbe_site_steam_callback_error";
const CANONICAL_STEAM_CALLBACK_URI = "https://cheatchecker.ru/auth/callback/steam";

function steamCallbackUri() {
  const hostname = window.location.hostname.toLowerCase();
  if (hostname === "cheatchecker.ru" || hostname === "www.cheatchecker.ru") {
    return CANONICAL_STEAM_CALLBACK_URI;
  }
  return `${window.location.origin}/auth/callback/steam`;
}
const GAME_OPTIONS: Array<PurchaseForm["game"]> = ["DayZ", "Rust"];

const fallbackRelease: LoaderRelease = {
  ok: true,
  status: "fallback",
  fileName: "CheatChecker.zip",
};

// Start the read-only counter request while the app bundle is still evaluating.
// The hero keeps its dash until this fresh value arrives.
const initialDownloadStatsRequest = siteDownloadStats();

const siteCopy = {
  ru: {
    langName: "RU",
    navLabel: "Основная навигация",
    contacts: "Контакты",
    account: "Кабинет",
    login: "Войти",
    close: "Закрыть",
    providerNames: {
      google: "Google",
      yandex: "Яндекс",
    },
    download: {
      preparing: "Готовим скачивание...",
      unavailable: "Скачивание недоступно",
      ready: "Скачать CheatChecker.zip",
      errorUnavailable: "Скачивание пока недоступно. Попробуйте позже.",
      errorFailed: "Не удалось подготовить скачивание. Попробуйте ещё раз.",
      count: "Скачиваний",
    },
    hero: {
      title: "Время — Деньги.\nСамое время стать богатым.",
      aria: "Что даёт CheatChecker",
      points: [
        "Сэкономьте время проверок до 5-10 минут. Быстрая проверка со встроенным искусственным интеллектом, способная находить любые следы от читов за несколько секунд.",
        "Все нужные функции в одном месте. Забудьте про массивные архивы с кучей программ и батников.",
        "Полный контроль напрямую из сайта: мгновенный доступ к личному кабинету, проверкам и управлению модераторами.",
      ],
      buy: "Купить ключ",
    },
    footer: {
      text: "Время — деньги.\nСамое время стать богатым.",
      contactsLabel: "Контакты",
      support: "Поддержка: support@cheatchecker.ru",
      phone: "Телефон: +7 (967) 120-00-76",
      legal: "Романец Юрий Юрьевич · ИНН 971518207358",
      docsLabel: "Документы",
      terms: "Публичная оферта",
      privacy: "Политика конфиденциальности",
    },
    legalContact: {
      title: "Связаться с нами",
      support: "Поддержка: support@cheatchecker.ru",
    },
    loading: {
      title: "Открываем кабинет",
      text: "Проверяем сохранённый вход. Страница останется на месте.",
    },
    legalDocuments: {
  "/terms": {
    title: "Публичная оферта и условия использования",
    lead: "Эта оферта определяет условия покупки и использования лицензии CheatChecker. Оплачивая заказ, пользователь принимает её условия.",
    updated: "Редакция от 14 июля 2026 года",
    sections: [
          {
            title: "1. Продавец и контакты",
            text: "Продавец: самозанятый Романец Юрий Юрьевич, ИНН 971518207358. По вопросам заказа, доступа и возврата: support@cheatchecker.ru, +7 (967) 120-00-76.",
      },
      {
        title: "2. Предмет и срок лицензии",
        text: "CheatChecker предоставляет доступ к функциональности сервиса и приложению для организации проверок игроков внутри проекта. Базовая лицензия предоставляется на 30 календарных дней для одного аккаунта и проекта; передача доступа третьим лицам запрещена.",
      },
      {
        title: "3. Цена и оплата",
        text: "Стартовая цена базовой лицензии — 2 490 рублей за 30 дней. В дальнейшем базовый тариф может составлять от 2 990 рублей за 30 дней; актуальная цена всегда показывается до оплаты. Корпоративные решения и расширенные условия согласуются отдельно. Оплата принимается картой и через СБП с использованием Robokassa. Доступ выдаётся после подтверждения оплаты.",
      },
      {
        title: "4. Возврат средств",
        text: "По обращению в поддержку в течение первых трёх календарных дней с момента выдачи доступа возвращается полная стоимость лицензии. После трёх дней возвращается стоимость неиспользованного периода, рассчитанная пропорционально оставшимся дням лицензии. Для обращения укажите email аккаунта и номер заказа.",
      },
      {
        title: "5. Правила использования",
        text: "Пользователь отвечает за сохранность email, пароля и действий в аккаунте. Сервис нельзя использовать для незаконного доступа к данным, вредоносных действий, спама или обхода технических ограничений. Скачивайте CheatChecker.exe только с официального сайта.",
      },
      {
        title: "6. Изменение условий",
        text: "Мы можем обновлять сервис и его интерфейс для безопасности и стабильности. Изменение стоимости применяется к новым оплатам; уже оплаченный срок лицензии сохраняется. Вопросы по условиям направляйте на support@cheatchecker.ru.",
      },
    ],
  },
  "/privacy": {
    title: "Политика конфиденциальности",
    lead: "Политика объясняет, какие данные обрабатываются для работы CheatChecker, покупки лицензии и поддержки.",
    updated: "Редакция от 14 июля 2026 года",
    sections: [
          {
            title: "1. Оператор и контакты",
            text: "Оператор данных: самозанятый Романец Юрий Юрьевич, ИНН 971518207358. По вопросам данных и поддержки: support@cheatchecker.ru, +7 (967) 120-00-76.",
      },
      {
        title: "2. Какие данные используются",
        text: "Для аккаунта могут использоваться email, имя, способ входа и технические данные сессии. При оплате мы обрабатываем номер заказа, сумму, статус и способ оплаты, но не получаем данные банковской карты. При входе через Google, Яндекс или Steam используются данные, которые провайдер передаёт для авторизации.",
      },
      {
        title: "3. Зачем это нужно",
        text: "Данные нужны, чтобы создать аккаунт, выполнить вход, поддерживать сессию, выдать лицензию после оплаты, показывать состояние аккаунта и отвечать на обращения в поддержку.",
      },
      {
        title: "4. Передача третьим лицам",
        text: "Мы не продаём пользовательские данные. Для оплаты используется Robokassa, для авторизации — Google, Яндекс и Steam при выборе пользователем соответствующего способа, а для инфраструктуры — необходимые провайдеры хостинга. Каждая передача ограничена работой соответствующей функции.",
      },
      {
        title: "5. Запросы пользователя",
        text: "Вы можете запросить удаление аккаунта или уточнить, какие данные связаны с вашим email. Для этого напишите на support@cheatchecker.ru. Сведения о заказах могут сохраняться в объёме, требуемом для исполнения договора, возврата и учёта.",
      },
          {
            title: "6. Контакты",
            text: "Почта для вопросов по данным и поддержке: support@cheatchecker.ru. Телефон: +7 (967) 120-00-76.",
      },
    ],
  },
    },
    authErrors: {
      already_has_project: "Проект уже привязан к аккаунту.",
      confirmation_already_used: "Эта ссылка на удаление уже использована.",
      confirmation_expired: "Срок действия ссылки на удаление истёк. Запросите новое письмо.",
      confirmation_invalid: "Ссылка на удаление недействительна. Запросите новое письмо.",
      email_already_registered: "Такой email уже зарегистрирован.",
      email_not_verified: "Сначала подтвердите почту. Мы отправили письмо на ваш email.",
      expired_email_verification: "Ссылка подтверждения устарела. Запросите письмо ещё раз.",
      expired_password_reset: "Ссылка восстановления устарела.",
      expired_site_session: "Сессия истекла. Войдите снова.",
      invalid_site_session: "Сессия недействительна. Войдите снова.",
      invalid_credentials: "Неверный email или пароль.",
      invalid_email: "Введите корректный email.",
      invalid_email_verification: "Ссылка подтверждения некорректна.",
      invalid_game: "Выберите DayZ или Rust.",
      invalid_promo_code: "Введите корректный промокод.",
      invalid_promo_discount: "Проверьте тип и размер скидки.",
      invalid_oauth_callback: "Вход через провайдера не завершён. Попробуйте ещё раз.",
      invalid_oauth_state: "Не удалось подтвердить вход. Попробуйте ещё раз.",
      invalid_password_reset: "Ссылка восстановления некорректна.",
      invalid_two_factor_code: "Код из приложения неверный. Проверьте его и попробуйте снова.",
      active_check_exists: "Сначала завершите или отмените активную проверку.",
      missing_project_profile: "Заполните название проекта и имя владельца.",
      moderator_mismatch: "Этот модератор не относится к текущему проекту.",
      moderator_not_found: "Модератор не найден.",
      no_project: "Сначала создайте проект.",
      not_owner: "Это действие доступно только владельцу проекта.",
      network_error: "Не удалось подключиться к сервису. Проверьте интернет и попробуйте ещё раз.",
      network_timeout: "Сервис не ответил вовремя. Проверьте интернет и попробуйте ещё раз.",
      invalid_steam_callback: "Steam не вернул нужные данные для входа.",
      invalid_steam_return_to: "Steam вернул вход не на тот адрес. Попробуйте ещё раз.",
      invalid_steam_state: "Сессия входа через Steam истекла. Попробуйте ещё раз.",
      oauth_not_configured: "Этот способ входа пока недоступен.",
      oauth_profile_failed: "Не удалось получить профиль. Попробуйте другой способ входа.",
      oauth_token_failed: "Вход через провайдера не завершён.",
      payment_url_missing: "Не удалось открыть страницу оплаты.",
      invalid_payment_plan: "Выбранный тариф больше недоступен. Выберите срок ещё раз.",
      active_payment_exists: "Есть незавершённая оплата с другими условиями. Завершите или отмените её перед новой попыткой.",
      project_access_inactive: "Доступ к проекту сейчас не активен.",
      project_access_frozen: "Проект заморожен. Продлите подписку, чтобы снова управлять им.",
      moderator_limit_reached: "Достигнут лимит модераторов по текущему тарифу.",
      invalid_payment_purpose: "Этот способ оплаты сейчас недоступен для проекта.",
      reauthentication_required: "Для этого действия войдите ещё раз по паролю.",
      payment_already_pending: "У вас уже есть заказ, ожидающий подтверждения.",
      payment_not_found: "Заявка на оплату больше не найдена. Обновите кабинет и создайте новую при необходимости.",
      payment_expired: "Срок этой заявки истёк. Создайте новую заявку на оплату.",
      payment_required: "Оплата ещё не подтверждена. Подождите немного, обновите кабинет и попробуйте снова.",
      promo_code_exists: "Такой промокод уже существует.",
      promo_invalid: "Промокод недоступен, истёк или исчерпал лимит.",
      promo_not_found: "Промокод не найден.",
      payment_legal_not_configured: "Реквизиты продавца и документы для оплаты ещё не настроены.",
      payments_not_configured: "Онлайн-оплата пока не настроена.",
      password_too_long: "Пароль слишком длинный.",
      smtp_not_configured: "Почта для отправки писем ещё не настроена.",
      smtp_send_failed: "Не удалось отправить письмо. Проверьте почтовый пароль.",
      steam_already_linked: "Этот Steam уже привязан к другому аккаунту.",
      steam_openid_rejected: "Steam не подтвердил вход. Попробуйте ещё раз.",
      two_factor_not_configured: "Настройка двухфакторной аутентификации не найдена. Начните настройку заново.",
      unknown_operation: "Этот способ входа пока недоступен.",
      unsupported_payment_method: "Выбранный способ оплаты недоступен.",
      weak_password: "Пароль должен быть не короче 8 символов.",
      fallback: "Не удалось выполнить вход. Попробуйте ещё раз.",
    },
    steamIdErrors: {
      length: "SteamID должен состоять из 17 цифр.",
      prefix: "SteamID должен начинаться с 7656.",
    },
  },
  en: {
    langName: "EN",
    navLabel: "Main navigation",
    contacts: "Contacts",
    account: "Account",
    login: "Sign in",
    close: "Close",
    providerNames: {
      google: "Google",
      yandex: "Yandex",
    },
    download: {
      preparing: "Preparing download...",
      unavailable: "Download unavailable",
      ready: "Download CheatChecker.zip",
      errorUnavailable: "Download is not available yet. Try again later.",
      errorFailed: "Could not start the download. Try again.",
      count: "Downloads",
    },
    hero: {
      title: "Time is money.\nTime to get richer.",
      aria: "What CheatChecker gives you",
      points: [
        "Save up to 5-10 minutes per check. Fast scanning with built-in AI helps find cheat traces in seconds.",
        "All needed tools in one place. No huge archives full of separate programs and batch files.",
        "Full control directly from the website: instant access to your account, checks, and moderator management.",
      ],
      buy: "Buy a key",
    },
    footer: {
      text: "Time is money.\nTime to get rich.",
      contactsLabel: "Contacts",
      support: "Support: support@cheatchecker.ru",
      phone: "Phone: +7 (967) 120-00-76",
      legal: "Романец Юрий Юрьевич · ИНН 971518207358",
      docsLabel: "Documents",
      terms: "Public offer",
      privacy: "Privacy Policy",
    },
    legalContact: {
      title: "Contact us",
      support: "Support: support@cheatchecker.ru",
    },
    loading: {
      title: "Opening account",
      text: "Checking the saved session. The page will stay here.",
    },
    legalDocuments: {
      "/terms": {
        title: "Public offer and terms of use",
        lead: "This offer describes the purchase and use of a CheatChecker license. By paying for an order, the user accepts these terms.",
        updated: "Version from July 14, 2026",
        sections: [
          {
            title: "1. Seller and contacts",
            text: "Seller: self-employed Romanets Yuri Yuryevich, Tax ID 971518207358. For order, access and refund questions: support@cheatchecker.ru, +7 (967) 120-00-76.",
          },
          {
            title: "2. License scope and term",
            text: "CheatChecker provides access to the service and app for organizing player checks inside a project. A basic license is issued for 30 calendar days for one account and project; sharing access with third parties is prohibited.",
          },
          {
            title: "3. Price and payment",
            text: "The introductory basic-license price is 2,490 RUB for 30 days. The regular basic plan may cost from 2,990 RUB for 30 days; the current price is always displayed before payment. Corporate and expanded arrangements are agreed separately. Card and SBP payments use Robokassa. Access is issued after payment confirmation.",
          },
          {
            title: "4. Refunds",
            text: "On a request to support made within the first three calendar days after access is issued, the full license price is refunded. After three days, the unused period is refunded pro rata by remaining license days. Include the account email and order number in the request.",
          },
          {
            title: "5. Acceptable use",
            text: "The user is responsible for account credentials and activity. The service must not be used for unlawful data access, malicious activity, spam or bypassing technical limits. Download CheatChecker.exe only from the official site.",
          },
          {
            title: "6. Changes to the terms",
            text: "We may update the service and its interface for safety and stability. Price changes apply to new payments only; an already-paid license term remains valid. Questions about these terms: support@cheatchecker.ru.",
          },
        ],
      },
      "/privacy": {
        title: "Privacy Policy",
        lead: "This policy explains the data used to operate CheatChecker, sell licenses and provide support.",
        updated: "Version from July 14, 2026",
        sections: [
          {
            title: "1. Data controller and contacts",
            text: "Data controller: self-employed Romanets Yuri Yuryevich, Tax ID 971518207358. Data and support questions: support@cheatchecker.ru, +7 (967) 120-00-76.",
          },
          {
            title: "2. Data we use",
            text: "An account may use email, name, sign-in method and session data. For payment we process the order number, amount, status and payment method, but never bank-card details. Google, Yandex and Steam sign-in uses the data their provider supplies for authorization.",
          },
          {
            title: "3. Why it is needed",
            text: "The data is needed to create an account, sign in, keep the session active, issue a license after payment, show account state and answer support requests.",
          },
          {
            title: "4. Third parties",
            text: "We do not sell user data. Robokassa is used for payment, Google, Yandex and Steam for optional authorization, and necessary hosting providers for infrastructure. Each disclosure is limited to the corresponding function.",
          },
          {
            title: "5. User requests",
            text: "You can request account deletion or ask which data is connected to your email. Write to support@cheatchecker.ru. Order information may be retained as needed to perform the agreement, process a refund and keep records.",
          },
          {
            title: "6. Contacts",
            text: "Email for data and support questions: support@cheatchecker.ru. Phone: +7 (967) 120-00-76.",
          },
        ],
      },
    },
    authErrors: {
      already_has_project: "A project is already linked to this account.",
      confirmation_already_used: "This deletion link has already been used.",
      confirmation_expired: "This deletion link has expired. Request a new email.",
      confirmation_invalid: "This deletion link is invalid. Request a new email.",
      email_already_registered: "This email is already registered.",
      email_not_verified: "Please confirm your email first. We sent a message to your inbox.",
      expired_email_verification: "The confirmation link has expired. Request a new email.",
      expired_password_reset: "The password reset link has expired.",
      expired_site_session: "Session expired. Sign in again.",
      invalid_site_session: "The session is no longer valid. Sign in again.",
      invalid_credentials: "Wrong email or password.",
      invalid_email: "Enter a valid email.",
      invalid_email_verification: "The confirmation link is invalid.",
      invalid_game: "Choose DayZ or Rust.",
      invalid_promo_code: "Enter a valid promo code.",
      invalid_promo_discount: "Check the discount type and value.",
      invalid_oauth_callback: "Provider sign-in was not completed. Try again.",
      invalid_oauth_state: "Could not confirm sign-in. Try again.",
      invalid_password_reset: "The password reset link is invalid.",
      invalid_two_factor_code: "The authenticator code is invalid. Check it and try again.",
      active_check_exists: "Finish or cancel the active check first.",
      missing_project_profile: "Enter the project name and owner name.",
      moderator_mismatch: "This moderator does not belong to the current project.",
      moderator_not_found: "Moderator not found.",
      no_project: "Create a project first.",
      not_owner: "This action is available only to the project owner.",
      network_error: "Could not connect to the service. Check your connection and try again.",
      network_timeout: "The service did not respond in time. Check your connection and try again.",
      invalid_steam_callback: "Steam did not return the data needed for sign-in.",
      invalid_steam_return_to: "Steam returned to the wrong address. Try again.",
      invalid_steam_state: "The Steam sign-in session expired. Try again.",
      oauth_not_configured: "This sign-in method is not available yet.",
      oauth_profile_failed: "Could not get the profile. Try another sign-in method.",
      oauth_token_failed: "Provider sign-in was not completed.",
      payment_url_missing: "Could not open the payment page.",
      invalid_payment_plan: "This plan is no longer available. Choose a period again.",
      active_payment_exists: "There is an unfinished payment with different terms. Finish or cancel it before trying again.",
      project_access_inactive: "Project access is not active right now.",
      project_access_frozen: "The project is frozen. Renew the subscription to manage it again.",
      moderator_limit_reached: "The moderator limit for the current plan has been reached.",
      invalid_payment_purpose: "This payment flow is not available for the project right now.",
      reauthentication_required: "Sign in with your password again to complete this action.",
      payment_already_pending: "You already have an order waiting for confirmation.",
      payment_not_found: "The payment request no longer exists. Refresh the account and create a new one if needed.",
      payment_expired: "This payment request has expired. Create a new one.",
      payment_required: "Payment is not confirmed yet. Wait a moment, refresh the account and try again.",
      promo_code_exists: "This promo code already exists.",
      promo_invalid: "This promo code is unavailable, expired or has reached its limit.",
      promo_not_found: "Promo code not found.",
      payment_legal_not_configured: "Seller details and payment documents are not configured yet.",
      payments_not_configured: "Online payment is not configured yet.",
      password_too_long: "The password is too long.",
      smtp_not_configured: "Email sending is not configured yet.",
      smtp_send_failed: "Could not send email. Check the mailbox password.",
      steam_already_linked: "This Steam account is already linked to another account.",
      steam_openid_rejected: "Steam did not confirm sign-in. Try again.",
      two_factor_not_configured: "No two-factor setup was found. Start setup again.",
      unknown_operation: "This sign-in method is not available yet.",
      unsupported_payment_method: "The selected payment method is unavailable.",
      weak_password: "Password must be at least 8 characters.",
      fallback: "Could not complete sign-in. Try again.",
    },
    steamIdErrors: {
      length: "SteamID must contain 17 digits.",
      prefix: "SteamID must start with 7656.",
    },
  },
} as const;

type SiteCopy = (typeof siteCopy)[Lang];

function readStorageJson<T>(key: string): T | null {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) as T : null;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

function paymentConfigCacheKey(accountId: string) {
  return `${PAYMENT_CONFIG_CACHE_PREFIX}:${accountId}`;
}

function readCachedPaymentConfig() {
  const cachedUser = readStorageJson<SiteUser>(USER_CACHE_KEY) || readStorageJson<SiteDashboard>(DASHBOARD_CACHE_KEY)?.account;
  return cachedUser ? readStorageJson<SitePaymentConfig>(paymentConfigCacheKey(cachedUser.id)) : null;
}

function readSelectedPlanId(): SubscriptionPlanId {
  try {
    const savedPlanId = localStorage.getItem(SELECTED_PLAN_KEY);
    return savedPlanId === "quarterly" || savedPlanId === "annual" ? savedPlanId : "monthly";
  } catch {
    return "monthly";
  }
}

function withoutOwnerKey(dashboard: SiteDashboard): SiteDashboard {
  const { ownerKey: _ownerKey, ...safeDashboard } = dashboard;
  return safeDashboard as SiteDashboard;
}

function detectInitialLanguage(): Lang {
  const saved = localStorage.getItem(LANG_KEY);
  if (saved === "ru" || saved === "en") {
    return saved;
  }

  const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
  return languages.some((item) => item.toLowerCase().startsWith("ru")) ? "ru" : "en";
}

function oauthCallbackFromLocation(): OAuthCallback {
  const match = window.location.pathname.match(/^\/auth\/callback\/(google|yandex)$/);
  if (!match) {
    return null;
  }

  const provider = match[1] as AuthProvider;
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code") || "";
  const state = params.get("state") || "";
  const denied = params.get("error");

  if (denied) {
    return { ok: false, provider, reason: "cancelled" };
  }

  if (!code || !state) {
    return { ok: false, provider, reason: "missing_callback_data" };
  }

  return {
    ok: true,
    provider,
    code,
    state,
    redirectUri: `${window.location.origin}${window.location.pathname}`,
  };
}

function authErrorText(error: unknown, language: Lang = "ru") {
  const status = error instanceof Error ? error.message : String(error || "");
  const messages = siteCopy[language].authErrors;
  return messages[status as keyof typeof messages] || messages.fallback;
}

function paymentErrorText(error: unknown, language: Lang, action: "create" | "cancel" = "create") {
  const status = error instanceof Error ? error.message : String(error || "");
  const messages = siteCopy[language].authErrors;
  const knownMessage = messages[status as keyof typeof messages];
  if (knownMessage) return knownMessage;

  const create = action === "create";
  const ru: Record<string, string> = {
    http_400: "Не удалось создать заявку на оплату. Проверьте выбранный тариф и попробуйте ещё раз.",
    http_401: "Сессия больше не действует. Войдите в аккаунт заново.",
    http_403: "Сначала подтвердите почту, чтобы перейти к оплате.",
    http_409: "У вас уже может быть незавершённая оплата. Обновляем её статус — после этого можно будет отменить заявку.",
    http_429: "Слишком много попыток оплаты. Попробуйте чуть позже.",
    http_503: "Оплата временно недоступна. Попробуйте немного позже.",
    fallback: create ? "Не удалось создать заявку на оплату. Попробуйте ещё раз." : "Не удалось отменить заявку на оплату. Попробуйте ещё раз.",
  };
  const en: Record<string, string> = {
    http_400: "We could not create the payment request. Check the selected plan and try again.",
    http_401: "Your session is no longer active. Sign in again.",
    http_403: "Confirm your email before continuing to payment.",
    http_409: "An unfinished payment may already exist. Its status is being refreshed so you can cancel it.",
    http_429: "Too many payment attempts. Try again shortly.",
    http_503: "Payments are temporarily unavailable. Try again shortly.",
    fallback: create ? "We could not create the payment request. Try again." : "We could not cancel the payment request. Try again.",
  };
  return (language === "en" ? en : ru)[status] || (language === "en" ? en : ru).fallback;
}

function steamCallbackErrorText(error: unknown, language: Lang) {
  const status = error instanceof Error ? error.message : String(error || "");
  if (["http_400", "http_401", "invalid_steam_state", "invalid_steam_return_to", "invalid_steam_callback", "invalid_steam_claimed_id", "steam_openid_rejected"].includes(status)) {
    return language === "en" ? "The Steam sign-in session could not be confirmed. Start Steam sign-in again." : "Не удалось подтвердить вход через Steam. Запустите вход через Steam ещё раз.";
  }
  if (["http_503", "steam_openid_timeout", "backend_timeout"].includes(status)) {
    return language === "en" ? "Steam sign-in is temporarily unavailable. Try again in a moment." : "Вход через Steam временно недоступен. Повторите попытку чуть позже.";
  }
  if (["http_409", "moderator_ambiguous_access"].includes(status)) {
    return language === "en" ? "More than one moderator access was found. Contact project support." : "Найдено несколько доступов модератора. Обратитесь к владельцу проекта.";
  }
  return authErrorText(error, language);
}

function moderatorSessionUser(result: SiteModeratorDashboard): SiteUser {
  return {
    ...result.account,
    role: "moderator",
    projectId: result.project.id,
    steamId64: result.moderator.steamId64,
    developer: false,
  };
}

function twoFactorErrorText(error: unknown, language: Lang) {
  const status = error instanceof Error ? error.message : String(error || "");
  if (status === "unknown_operation") return language === "en" ? "Two-factor authentication is temporarily unavailable. Please try again shortly." : "Двухфакторная аутентификация временно недоступна. Повторите попытку чуть позже.";
  if (status === "reauthentication_required") return language === "en" ? "Sign in with your password again to change two-factor authentication." : "Войдите ещё раз по паролю, чтобы изменить двухфакторную аутентификацию.";
  return authErrorText(error, language);
}

function resetPasswordErrorText(error: unknown, language: Lang) {
  const status = error instanceof Error ? error.message : String(error || "");
  if (["expired_password_reset", "password_reset_already_used", "expired_password_reset_session", "password_reset_session_used"].includes(status)) return language === "en" ? "This reset link has expired. Request a new one." : "Срок действия ссылки истёк. Запросите новую.";
  if (["invalid_password_reset", "invalid_password_reset_session", "site_user_not_found"].includes(status)) return language === "en" ? "This reset link is invalid. Request a new one." : "Ссылка восстановления недействительна. Запросите новую.";
  if (["invalid_password", "weak_password"].includes(status)) return language === "en" ? "Use at least 8 characters for the new password." : "Новый пароль должен содержать не менее 8 символов.";
  if (status === "password_too_long") return language === "en" ? "The new password must be no longer than 128 characters." : "Новый пароль не должен быть длиннее 128 символов.";
  return language === "en" ? "The service is temporarily unavailable. Please try again shortly." : "Сервис временно недоступен. Повторите попытку чуть позже.";
}

function isTerminalPasswordResetError(error: unknown) {
  const status = error instanceof Error ? error.message : String(error || "");
  return ["invalid_password_reset", "expired_password_reset", "password_reset_already_used", "invalid_password_reset_session", "expired_password_reset_session", "password_reset_session_used", "site_user_not_found"].includes(status);
}

function projectDeletionErrorText(error: unknown, language: Lang) {
  const status = error instanceof Error ? error.message : String(error || "");
  const messages: Record<string, string> = language === "en" ? {
    unknown_operation: "Project deletion is temporarily unavailable. Please contact support.",
    oauth_not_configured: "Project deletion is temporarily unavailable. Please contact support.",
    no_project: "There is no project to delete.",
    mail_send_failed: "We could not send the confirmation email. Please try again later.",
  } : {
    unknown_operation: "Удаление проекта временно недоступно. Обратитесь в поддержку.",
    oauth_not_configured: "Удаление проекта временно недоступно. Обратитесь в поддержку.",
    no_project: "Проект для удаления не найден.",
    mail_send_failed: "Не удалось отправить письмо с подтверждением. Попробуйте позже.",
  };
  return messages[status] || authErrorText(error, language);
}

function projectKeyErrorText(error: unknown, language: Lang) {
  const status = error instanceof Error ? error.message : String(error || "");
  const ru: Record<string, string> = { key_unavailable: "Этот ключ нельзя восстановить. Создайте новый доступ при необходимости.", rate_limited: "Слишком много попыток. Попробуйте чуть позже.", not_owner: "Ключ доступен только владельцу проекта." };
  const en: Record<string, string> = { key_unavailable: "This key cannot be recovered.", rate_limited: "Too many attempts. Try again later.", not_owner: "Only the project owner can copy this key." };
  return (language === "en" ? en : ru)[status] || authErrorText(error, language);
}

function developerVerificationErrorText(error: unknown, language: Lang) {
  const status = error instanceof Error ? error.message : String(error || "");
  const messages = language === "ru" ? {
    developer_reauthentication_required: "Сессия устарела. Войдите в аккаунт заново.",
    invalid_site_session: "Сессия больше не действует. Войдите в аккаунт заново.",
    expired_site_session: "Сессия истекла. Войдите в аккаунт заново.",
    developer_verification_invalid: "Код не подходит. Проверьте его и попробуйте ещё раз.",
    developer_verification_expired: "Срок действия кода истёк. Отправьте новый.",
    developer_verification_already_used: "Этот код уже использован. Отправьте новый.",
    developer_verification_locked: "Слишком много попыток. Запросите новый код позже.",
    developer_verification_rate_limited: "Код уже недавно отправлялся. Попробуйте чуть позже.",
    rate_limited: "Слишком много запросов. Попробуйте чуть позже.",
    mail_send_failed: "Не удалось отправить письмо. Попробуйте ещё раз позже.",
    email_not_verified: "Сначала подтвердите адрес электронной почты.",
    developer_access_required: "Этот раздел доступен только владельцу разраб-кабинета.",
  } : {
    developer_reauthentication_required: "Your session has expired. Sign in again.",
    invalid_site_session: "Your session is no longer valid. Sign in again.",
    expired_site_session: "Your session has expired. Sign in again.",
    developer_verification_invalid: "The code is incorrect. Check it and try again.",
    developer_verification_expired: "The code has expired. Send a new one.",
    developer_verification_already_used: "This code has already been used. Send a new one.",
    developer_verification_locked: "Too many attempts. Request a new code later.",
    developer_verification_rate_limited: "A code was sent recently. Try again shortly.",
    rate_limited: "Too many requests. Try again shortly.",
    mail_send_failed: "Could not send the email. Try again later.",
    email_not_verified: "Confirm your email address first.",
    developer_access_required: "This area is only available to the developer account.",
  };
  return messages[status as keyof typeof messages] || authErrorText(error, language);
}

function isDeveloperAccount(account: SiteUser | null) {
  return account?.developer === true || account?.email.trim().toLowerCase() === "admin@cheatchecker.ru";
}

function submitRobokassaPayment(formData: NonNullable<SitePaymentOrder["paymentForm"]>) {
  const form = document.createElement("form");
  form.method = "post";
  form.action = formData.action;
  form.acceptCharset = "UTF-8";
  form.style.display = "none";

  for (const [name, value] of Object.entries(formData.fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
}

function pendingPaymentText(status: string, _invoiceId: string | undefined, language: Lang) {
  if (status === "pending") {
    return language === "en"
      ? "Payment is waiting for confirmation from Robokassa."
      : "Платёж ожидает подтверждения от Robokassa.";
  }
  return "";
}

function normalizeSteamId64(value: string) {
  return value.replace(/\D/g, "").slice(0, 17);
}

function steamId64Error(value: string, language: Lang = "ru") {
  const steamId = value.trim();
  if (!steamId) {
    return "";
  }
  if (steamId.length !== 17) {
    return siteCopy[language].steamIdErrors.length;
  }
  if (!steamId.startsWith("7656")) {
    return siteCopy[language].steamIdErrors.prefix;
  }
  return "";
}

export function App() {
  const [page, setPage] = useState<"home" | "account" | "developer">(() => window.location.pathname === "/developer" ? "developer" : window.location.pathname === "/account" ? "account" : "home");
  const [language, setLanguage] = useState<Lang>(detectInitialLanguage);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement | null>(null);
  const [release, setRelease] = useState<LoaderRelease>(() => readStorageJson<LoaderRelease>(RELEASE_CACHE_KEY) || fallbackRelease);
  const [releaseHydrating, setReleaseHydrating] = useState(true);
  const [authMode, setAuthMode] = useState<AuthMode>(() => new URLSearchParams(window.location.search).has("resetToken") || Boolean(sessionStorage.getItem(PASSWORD_RESET_SESSION_KEY)) ? "reset" : "login");
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState<AuthForm>({ email: "", password: "" });
  const [siteToken, setSiteToken] = useState(() => localStorage.getItem(SITE_TOKEN_KEY) || "");
  const [sessionHydrating, setSessionHydrating] = useState(() => Boolean(localStorage.getItem(SITE_TOKEN_KEY)));
  const [user, setUser] = useState<SiteUser | null>(() => {
    const token = localStorage.getItem(SITE_TOKEN_KEY);
    if (!token) return null;
    return readStorageJson<SiteUser>(USER_CACHE_KEY) || readStorageJson<SiteDashboard>(DASHBOARD_CACHE_KEY)?.account || null;
  });
  const [authBusy, setAuthBusy] = useState(false);
  const [authMessage, setAuthMessage] = useState("");
  const [authError, setAuthError] = useState("");
  const [steamCallbackError, setSteamCallbackError] = useState(() => sessionStorage.getItem(STEAM_CALLBACK_ERROR_KEY) || "");
  const steamCallbackHandled = useRef(false);
  const [contactsHighlighted, setContactsHighlighted] = useState(false);
  const [plansHighlighted, setPlansHighlighted] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [authOpen, setAuthOpen] = useState(() => new URLSearchParams(window.location.search).has("resetToken") || Boolean(sessionStorage.getItem(PASSWORD_RESET_SESSION_KEY)));
  const [passwordResetState, setPasswordResetState] = useState<"idle" | "exchanging" | "ready" | "retryable_error" | "terminal_error" | "completed">(() => {
    if (sessionStorage.getItem(PASSWORD_RESET_SESSION_KEY)) return "ready";
    return new URLSearchParams(window.location.search).has("resetToken") ? "exchanging" : "idle";
  });
  const passwordResetExchangeInFlight = useRef(false);
  const [developerReauthentication, setDeveloperReauthentication] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [checkoutEmailGateOpen, setCheckoutEmailGateOpen] = useState(false);
  const [checkoutEmail, setCheckoutEmail] = useState("");
  const [checkoutEmailBusy, setCheckoutEmailBusy] = useState(false);
  const [checkoutEmailSent, setCheckoutEmailSent] = useState(false);
  const [checkoutEmailError, setCheckoutEmailError] = useState("");
  const [resumeCheckoutAfterEmailVerification, setResumeCheckoutAfterEmailVerification] = useState(false);
  const [projectDeletionComplete, setProjectDeletionComplete] = useState(false);
  const [confirmation, setConfirmation] = useState<ConfirmationRequest | null>(null);
  const [renameRequest, setRenameRequest] = useState<RenameRequest | null>(null);
  const [twoFactorStepUp, setTwoFactorStepUp] = useState<TwoFactorStepUpRequest | null>(null);
  const [twoFactorLoginChallenge, setTwoFactorLoginChallenge] = useState<TwoFactorLoginChallenge | null>(null);
  const [passwordConfirmationOpen, setPasswordConfirmationOpen] = useState(false);
  const [dashboard, setDashboard] = useState<SiteDashboard | null>(() => {
    const token = localStorage.getItem(SITE_TOKEN_KEY);
    const cachedDashboard = token ? readStorageJson<SiteDashboard>(DASHBOARD_CACHE_KEY) : null;
    return cachedDashboard ? withoutOwnerKey(cachedDashboard) : null;
  });
  const [moderatorDashboard, setModeratorDashboard] = useState<SiteModeratorDashboard | null>(null);
  const [developerDashboard, setDeveloperDashboard] = useState<SiteDeveloperDashboard | null>(null);
  const [developerPromoCodes, setDeveloperPromoCodes] = useState<SiteDeveloperPromoCode[]>([]);
  const [developerBusy, setDeveloperBusy] = useState(false);
  const [developerError, setDeveloperError] = useState("");
  const [developerReauthenticationRequired, setDeveloperReauthenticationRequired] = useState(false);
  const [developerVerificationStage, setDeveloperVerificationStage] = useState<"request" | "code" | null>(null);
  const [developerVerificationBusy, setDeveloperVerificationBusy] = useState(false);
  const [developerVerificationMessage, setDeveloperVerificationMessage] = useState("");
  const [developerVerificationError, setDeveloperVerificationError] = useState("");
  const [dashboardBusy, setDashboardBusy] = useState(false);
  const [dashboardError, setDashboardError] = useState("");
  const [dashboardNotice, setDashboardNotice] = useState("");
  const [paymentConfig, setPaymentConfig] = useState<SitePaymentConfig | null>(readCachedPaymentConfig);
  const [selectedPlanId, setSelectedPlanId] = useState<SubscriptionPlanId>(readSelectedPlanId);
  const [paymentMessage, setPaymentMessage] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [pendingPayment, setPendingPayment] = useState<SitePaymentOrder | null>(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoPreview, setPromoPreview] = useState<SitePromoPreflight | null>(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [downloadCount, setDownloadCount] = useState<number | null>(() => {
    const cached = Number(localStorage.getItem(DOWNLOAD_COUNT_CACHE_KEY));
    return Number.isFinite(cached) && cached > 0 ? cached : null;
  });
  const [purchaseForm, setPurchaseForm] = useState<PurchaseForm>({
    displayName: "",
    game: "DayZ",
    ownerDisplayName: "",
  });
  const [projectForm, setProjectForm] = useState<PurchaseForm>({
    displayName: "",
    game: "DayZ",
    ownerDisplayName: "",
  });
  const [moderatorForm, setModeratorForm] = useState<ModeratorForm>({ name: "", steamId64: "" });
  const [steamEmail, setSteamEmail] = useState("");
  const [steamEmailSent, setSteamEmailSent] = useState(false);
  const copy = siteCopy[language];

  useEffect(() => {
    if (!dashboardNotice) return;
    const timer = window.setTimeout(() => setDashboardNotice(""), 4000);
    return () => window.clearTimeout(timer);
  }, [dashboardNotice]);

  const resetToken = new URLSearchParams(window.location.search).get("resetToken") || "";
  const resetSession = sessionStorage.getItem(PASSWORD_RESET_SESSION_KEY) || "";
  const recoveryMode = passwordResetState !== "idle" || Boolean(resetToken) || Boolean(resetSession);
  const deleteProjectToken = new URLSearchParams(window.location.search).get("deleteProjectToken") || "";

  function selectLanguage(nextLanguage: Lang) {
    if (nextLanguage === language) {
      setLanguageMenuOpen(false);
      return;
    }
    setLanguage(nextLanguage);
    setLanguageMenuOpen(false);
    if (!siteToken) {
      return;
    }
    void siteUpdateLanguage(siteToken, nextLanguage)
      .then((result) => setUser((current) => ({ ...(current || result.user), ...result.user })))
      .catch(() => undefined);
  }

  function navigate(path: string, replace = false) {
    if (replace) {
      window.history.replaceState({}, "", path);
    } else {
      window.history.pushState({}, "", path);
    }
    setPage(window.location.pathname === "/developer" ? "developer" : window.location.pathname === "/account" ? "account" : "home");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function clearPasswordResetSession() {
    sessionStorage.removeItem(PASSWORD_RESET_SESSION_KEY);
  }

  async function exchangePasswordReset(resetTokenToExchange: string) {
    if (!resetTokenToExchange || passwordResetExchangeInFlight.current) return;
    passwordResetExchangeInFlight.current = true;
    setPasswordResetState("exchanging");
    setAuthMode("reset");
    setAuthError("");
    setAuthMessage("");

    try {
      const result = await siteExchangePasswordReset(resetTokenToExchange);
      sessionStorage.setItem(PASSWORD_RESET_SESSION_KEY, result.resetSession);
      setPasswordResetState("ready");
      navigate("/account/password_reset", true);
    } catch (error) {
      setAuthError(resetPasswordErrorText(error, language));
      if (isTerminalPasswordResetError(error)) {
        clearPasswordResetSession();
        setPasswordResetState("terminal_error");
        navigate("/account/password_reset", true);
      } else {
        setPasswordResetState("retryable_error");
      }
    } finally {
      passwordResetExchangeInFlight.current = false;
    }
  }

  function requestNewPasswordResetLink() {
    clearPasswordResetSession();
    setPasswordResetState("idle");
    setAuthMode("forgot");
    setAuthError("");
    setAuthMessage("");
    setAuthOpen(true);
    navigate("/account", true);
  }

  function continueAfterPasswordReset() {
    setPasswordResetState("idle");
    setForm({ email: "", password: "" });
    setShowPassword(false);
    setAuthMode("login");
    setAuthError("");
    setAuthMessage("");
    setAuthOpen(true);
    navigate("/", true);
  }

  function openAuthDialog() {
    if (user || siteToken) {
      setAuthOpen(false);
      navigate(canOpenCabinet ? "/account" : "/", true);
      return;
    }
    setDeveloperReauthentication(false);
    setAuthMode("login");
    setAuthError("");
    setAuthMessage("");
    setAuthOpen(true);
  }

  function openDeveloperReauthentication() {
    setDeveloperReauthentication(true);
    setAuthMode("login");
    setAuthError("");
    setAuthMessage("");
    setAuthOpen(true);
  }

  function requestConfirmation(options: Omit<ConfirmationRequest, "resolve">) {
    return new Promise<boolean>((resolve) => setConfirmation({ ...options, resolve }));
  }

  function finishConfirmation(confirmed: boolean) {
    const activeConfirmation = confirmation;
    setConfirmation(null);
    activeConfirmation?.resolve(confirmed);
  }

  function requestModeratorName(options: Omit<RenameRequest, "resolve">) {
    return new Promise<string | null>((resolve) => setRenameRequest({ ...options, resolve }));
  }

  function finishRename(value: string | null) {
    const activeRequest = renameRequest;
    setRenameRequest(null);
    activeRequest?.resolve(value);
  }

  async function requestTwoFactorStepUp(): Promise<string | null> {
    if (!siteToken) return null;
    const security = await siteGetSecuritySettings(siteToken);
    if (!security.twoFactor.enabled) return "";
    return new Promise<string | null>((resolve) => setTwoFactorStepUp({ resolve }));
  }

  function finishTwoFactorStepUp(code: string | null) {
    const activeRequest = twoFactorStepUp;
    setTwoFactorStepUp(null);
    activeRequest?.resolve(code);
  }

  function openAccountOrAuth() {
    if (user || siteToken || sessionHydrating) {
      navigate("/account");
      return;
    }

    openAuthDialog();
  }

  const hasPaidAccess = Boolean(
    dashboard?.project
    || paymentConfig?.access?.status === "paid"
    || paymentConfig?.access?.status === "active",
  );

  function openPurchaseOrAuth() {
    if (!user || !siteToken) {
      openAuthDialog();
      return;
    }
    if (!paymentConfig?.emailVerified && !user.emailVerified) {
      setCheckoutEmail(user.email || steamEmail);
      setCheckoutEmailError("");
      setCheckoutEmailSent(false);
      setCheckoutEmailGateOpen(true);
      return;
    }
    setPaymentError("");
    setPurchaseOpen(true);
  }

  function scrollToPlans(highlightCheckout = false) {
    if (highlightCheckout) {
      setPlansHighlighted(true);
      window.setTimeout(() => setPlansHighlighted(false), 1800);
    }
    const planCheckout = document.getElementById("plans-checkout");
    (planCheckout || document.getElementById("plans"))?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  function openRenewal() {
    if (page !== "home") {
      navigate("/");
      window.setTimeout(() => scrollToPlans(true), 0);
      return;
    }
    scrollToPlans(true);
  }

  function scrollToContacts() {
    const contactBlock = document.getElementById("contacts");
    if (!contactBlock) {
      return;
    }
    setContactsHighlighted(true);
    window.setTimeout(() => setContactsHighlighted(false), 1800);
    contactBlock.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function openSupport() {
    setPurchaseOpen(false);
    window.setTimeout(scrollToContacts, 0);
  }

  async function refreshDeveloperDashboard() {
    if (!siteToken || !isDeveloperAccount(user)) {
      return;
    }
    setDeveloperBusy(true);
    setDeveloperError("");
    try {
      const [dashboardResult, promoResult] = await Promise.all([
        fetchSiteDeveloperDashboard(siteToken, 50),
        siteDeveloperListPromoCodes(siteToken),
      ]);
      setDeveloperDashboard(dashboardResult);
      setDeveloperPromoCodes(promoResult.promoCodes || []);
      setDeveloperVerificationStage(null);
      setDeveloperReauthenticationRequired(false);
    } catch (error) {
      const status = error instanceof Error ? error.message : String(error || "");
      if (["developer_reauthentication_required", "invalid_site_session", "expired_site_session"].includes(status)) {
        setDeveloperDashboard(null);
        setDeveloperPromoCodes([]);
        setDeveloperVerificationStage(null);
        setDeveloperReauthenticationRequired(true);
        setDeveloperError(developerVerificationErrorText(error, language));
        return;
      }
      if (status === "developer_verification_required") {
        setDeveloperDashboard(null);
        setDeveloperPromoCodes([]);
        setDeveloperVerificationStage("request");
        setDeveloperReauthenticationRequired(false);
        setDeveloperVerificationMessage("");
        setDeveloperVerificationError("");
        return;
      }
      if (status === "developer_access_required" || status === "email_not_verified") {
        setDeveloperDashboard(null);
        setDeveloperPromoCodes([]);
        setDeveloperError(developerVerificationErrorText(error, language));
        return;
      }
      setDeveloperError(language === "en" ? "Developer data is temporarily unavailable. Try again later." : "Данные разраб-кабинета временно недоступны. Попробуйте позже.");
    } finally {
      setDeveloperBusy(false);
    }
  }

  async function createDeveloperPromoCode(payload: Parameters<typeof siteDeveloperCreatePromoCode>[1]): Promise<boolean> {
    if (!siteToken || !isDeveloperAccount(user)) return false;
    setDeveloperBusy(true);
    setDeveloperError("");
    try {
      await siteDeveloperCreatePromoCode(siteToken, payload);
      await refreshDeveloperDashboard();
      return true;
    } catch (error) {
      if ((error instanceof Error ? error.message : String(error || "")) === "developer_verification_required") {
        setDeveloperDashboard(null);
        setDeveloperPromoCodes([]);
        setDeveloperVerificationStage("request");
        return false;
      }
      setDeveloperError(authErrorText(error, language));
      return false;
    } finally {
      setDeveloperBusy(false);
    }
  }

  async function updateDeveloperPromoCode(code: string, payload: Parameters<typeof siteDeveloperUpdatePromoCode>[2]): Promise<boolean> {
    if (!siteToken || !isDeveloperAccount(user)) return false;
    setDeveloperBusy(true);
    setDeveloperError("");
    try {
      await siteDeveloperUpdatePromoCode(siteToken, code, payload);
      await refreshDeveloperDashboard();
      return true;
    } catch (error) {
      if ((error instanceof Error ? error.message : String(error || "")) === "developer_verification_required") {
        setDeveloperDashboard(null);
        setDeveloperPromoCodes([]);
        setDeveloperVerificationStage("request");
        return false;
      }
      setDeveloperError(authErrorText(error, language));
      return false;
    } finally {
      setDeveloperBusy(false);
    }
  }

  async function requestDeveloperVerification() {
    if (!siteToken || !isDeveloperAccount(user)) return;
    setDeveloperVerificationBusy(true);
    setDeveloperVerificationError("");
    try {
      await siteDeveloperRequestVerification(siteToken, language);
      setDeveloperVerificationStage("code");
      setDeveloperVerificationMessage(language === "en" ? "The six-digit code was sent to your email." : "Шестизначный код отправлен на вашу почту.");
    } catch (error) {
      setDeveloperVerificationError(developerVerificationErrorText(error, language));
    } finally {
      setDeveloperVerificationBusy(false);
    }
  }

  async function verifyDeveloper(code: string) {
    if (!siteToken || !isDeveloperAccount(user) || code.length !== 6) return;
    setDeveloperVerificationBusy(true);
    setDeveloperVerificationError("");
    try {
      await siteDeveloperVerify(siteToken, code);
      setDeveloperVerificationStage(null);
      setDeveloperVerificationMessage("");
      await refreshDeveloperDashboard();
    } catch (error) {
      setDeveloperVerificationError(developerVerificationErrorText(error, language));
    } finally {
      setDeveloperVerificationBusy(false);
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function loadRelease() {
      try {
        const releaseResult = await getSiteDownloadRelease();
        if (!cancelled) {
          const nextRelease = { ...fallbackRelease, ...releaseResult };
          setRelease(nextRelease);
          localStorage.setItem(RELEASE_CACHE_KEY, JSON.stringify(nextRelease));
        }
      } catch {
        if (!cancelled && !release.downloadUrl) {
          setRelease(fallbackRelease);
        }
      } finally {
        if (!cancelled) {
          setReleaseHydrating(false);
        }
      }
    }

    loadRelease();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(LANG_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    const publicPaths = new Set(["/", "/terms", "/privacy"]);
    const path = window.location.pathname;
    const currentCanonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');

    if (!publicPaths.has(path)) {
      currentCanonical?.remove();
      return;
    }

    const canonical = currentCanonical || document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = `https://cheatchecker.ru${path}`;
    if (!currentCanonical) {
      document.head.appendChild(canonical);
    }
  }, [page]);

  useEffect(() => {
    if (!languageMenuOpen) {
      return;
    }

    function closeLanguageMenu(event: MouseEvent) {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setLanguageMenuOpen(false);
      }
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setLanguageMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", closeLanguageMenu);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeLanguageMenu);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [languageMenuOpen]);

  useEffect(() => {
    if (siteToken && user) {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
    }
  }, [siteToken, user]);

  useEffect(() => {
    if (siteToken && dashboard) {
      localStorage.setItem(DASHBOARD_CACHE_KEY, JSON.stringify(dashboard));
    }
  }, [siteToken, dashboard]);

  useEffect(() => {
    if (siteToken && user && paymentConfig) {
      localStorage.setItem(paymentConfigCacheKey(user.id), JSON.stringify(paymentConfig));
    }
  }, [siteToken, user?.id, paymentConfig]);

  useEffect(() => {
    function syncPage() {
      setPage(window.location.pathname === "/developer" ? "developer" : window.location.pathname === "/account" ? "account" : "home");
    }
    window.addEventListener("popstate", syncPage);
    return () => window.removeEventListener("popstate", syncPage);
  }, []);

  useEffect(() => {
    if (page === "developer" && isDeveloperAccount(user) && siteToken) {
      void refreshDeveloperDashboard();
    }
  }, [page, siteToken, user]);

  useEffect(() => {
    let cancelled = false;
    async function loadStats() {
      try {
        const result = await initialDownloadStatsRequest;
        if (!cancelled) {
          setDownloadCount(result.downloads);
          localStorage.setItem(DOWNLOAD_COUNT_CACHE_KEY, String(result.downloads));
        }
      } catch {
        if (!cancelled) {
          const cachedCount = Number(localStorage.getItem(DOWNLOAD_COUNT_CACHE_KEY));
          setDownloadCount(Number.isFinite(cachedCount) && cachedCount > 0 ? cachedCount : null);
        }
      }
    }
    loadStats();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      if (!siteToken) {
        if (!cancelled) {
          setSessionHydrating(false);
        }
        return;
      }

      setSessionHydrating(true);
      try {
        const result = await siteSession(siteToken, language);
        if (!cancelled) {
          setUser(result.user);
          localStorage.setItem(USER_CACHE_KEY, JSON.stringify(result.user));
        }
      } catch {
        if (!cancelled) {
          localStorage.removeItem(SITE_TOKEN_KEY);
          localStorage.removeItem(USER_CACHE_KEY);
          localStorage.removeItem(DASHBOARD_CACHE_KEY);
          if (user?.id) localStorage.removeItem(paymentConfigCacheKey(user.id));
          setSiteToken("");
          setUser(null);
          setDashboard(null);
        }
      } finally {
        if (!cancelled) {
          setSessionHydrating(false);
        }
      }
    }

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, [siteToken]);

  useEffect(() => {
    const callback = oauthCallbackFromLocation();
    if (!callback) {
      return;
    }

    if (!callback.ok) {
      setAuthOpen(true);
      setAuthError(language === "en"
        ? `${copy.providerNames[callback.provider]} sign-in was not completed.`
        : `Вход через ${copy.providerNames[callback.provider]} не завершён.`);
      window.history.replaceState({}, "", "/");
      return;
    }

    const oauthCallback = callback;
    let cancelled = false;

    async function completeOAuth() {
      setAuthBusy(true);
      setAuthError("");
      setAuthMessage(language === "en"
        ? `Completing ${copy.providerNames[oauthCallback.provider]} sign-in...`
        : `Завершаем вход через ${copy.providerNames[oauthCallback.provider]}...`);

      try {
        const result = await siteOAuthComplete({
          provider: oauthCallback.provider,
          code: oauthCallback.code,
          state: oauthCallback.state,
          redirectUri: oauthCallback.redirectUri,
        });

        if (cancelled) {
          return;
        }

        persistAuth(result.siteToken, result.user);
        setAuthOpen(false);
        setAuthMessage("");
        navigate("/", true);
      } catch (error) {
        if (!cancelled) {
          setAuthError(authErrorText(error, language));
          window.history.replaceState({}, "", "/");
        }
      } finally {
        if (!cancelled) {
          setAuthBusy(false);
        }
      }
    }

    completeOAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (window.location.pathname !== "/auth/callback/steam" || steamCallbackHandled.current) {
      return;
    }
    steamCallbackHandled.current = true;

    let cancelled = false;

    async function completeSteam() {
      setAuthBusy(true);
      setAuthError("");
      setAuthMessage(language === "en" ? "Completing Steam sign-in..." : "Завершаем вход через Steam...");

      try {
        const result = await siteSteamComplete(window.location.search);
        if (cancelled) {
          return;
        }

        if (!result.ok) {
          sessionStorage.removeItem(STEAM_CALLBACK_ERROR_KEY);
          setSteamCallbackError("");
          setAuthOpen(false);
          setTwoFactorLoginChallenge(result.status === "two_factor_setup_required"
            ? { kind: "steam_setup", twoFactorLoginSession: result.twoFactorLoginSession }
            : { kind: "steam_login", twoFactorLoginSession: result.twoFactorLoginSession });
          setAuthMessage("");
          navigate("/account", true);
          return;
        }

        persistSteamAuth(result.siteToken, result.user);
        setAuthOpen(false);
        setAuthMessage("");
        navigate("/", true);
      } catch (error) {
        if (!cancelled) {
          const status = error instanceof Error ? error.message : "invalid_steam_callback";
          sessionStorage.setItem(STEAM_CALLBACK_ERROR_KEY, status);
          setSteamCallbackError(status);
          setAuthOpen(true);
          setAuthError("");
          navigate("/", true);
        }
      } finally {
        if (!cancelled) {
          setAuthBusy(false);
        }
      }
    }

    completeSteam();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verified = params.get("emailVerified");
    const reset = params.get("resetToken");
    if (reset) {
      setAuthOpen(true);
      void exchangePasswordReset(reset);
      return;
    }
    if (!verified) {
      return;
    }
    const message = verified === "1"
      ? language === "en" ? "Email confirmed. You can sign in now." : "Почта подтверждена. Теперь можно войти."
      : language === "en" ? "Could not confirm email." : "Не удалось подтвердить почту.";
    if (verified === "1") {
      const checkoutRequestedAt = Number(localStorage.getItem(CHECKOUT_EMAIL_RESUME_KEY));
      if (Number.isFinite(checkoutRequestedAt) && Date.now() - checkoutRequestedAt < 30 * 60 * 1000) {
        setResumeCheckoutAfterEmailVerification(true);
      } else {
        localStorage.removeItem(CHECKOUT_EMAIL_RESUME_KEY);
      }
    }
    if (user || siteToken) {
      setAuthOpen(false);
      setDashboardNotice(message);
    } else {
      setAuthOpen(true);
      setAuthMessage(message);
    }
    navigate("/", true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadDashboard() {
      if (!siteToken || !user) {
        setDashboard(null);
        setModeratorDashboard(null);
        setPaymentConfig(null);
        return;
      }
      setDashboardBusy(true);
      setDashboardError("");
      try {
        if (user.role === "moderator") {
          const result = await fetchSiteModeratorDashboard(siteToken);
          if (!cancelled) {
            const moderatorUser = moderatorSessionUser(result);
            setModeratorDashboard(result);
            setDashboard(null);
            setPaymentConfig(null);
            setPendingPayment(null);
            setUser(moderatorUser);
            localStorage.setItem(USER_CACHE_KEY, JSON.stringify(moderatorUser));
          }
          return;
        }
        const [result, nextPaymentConfig, payment] = await Promise.all([
          fetchSiteDashboard(siteToken),
          fetchSitePaymentConfig(siteToken, language).catch(() => null),
          sitePaymentStatus(siteToken).catch(() => null),
        ]);
        if (!cancelled) {
          const safeDashboard = withoutOwnerKey(result);
          setDashboard(safeDashboard);
          setModeratorDashboard(null);
          setPaymentConfig(nextPaymentConfig);
          setPendingPayment(payment?.status === "pending" ? payment : null);
          const pendingText = payment ? pendingPaymentText(payment.status, payment.invoiceId, language) : "";
          if (pendingText) {
            setPaymentMessage(pendingText);
          }
          setUser(safeDashboard.account);
          localStorage.setItem(DASHBOARD_CACHE_KEY, JSON.stringify(safeDashboard));
          localStorage.setItem(USER_CACHE_KEY, JSON.stringify(safeDashboard.account));
        }
      } catch (error) {
        if (!cancelled) {
          setDashboardError(authErrorText(error, language));
        }
      } finally {
        if (!cancelled) {
          setDashboardBusy(false);
        }
      }
    }

    loadDashboard();
    return () => {
      cancelled = true;
    };
  }, [siteToken, user?.id, user?.projectId, user?.emailVerified, language]);

  useEffect(() => {
    if (!resumeCheckoutAfterEmailVerification || !paymentConfig?.emailVerified) {
      return;
    }
    localStorage.removeItem(CHECKOUT_EMAIL_RESUME_KEY);
    setResumeCheckoutAfterEmailVerification(false);
    setCheckoutEmailGateOpen(false);
    setDashboardNotice("");
    setPaymentError("");
    setPurchaseOpen(true);
  }, [resumeCheckoutAfterEmailVerification, paymentConfig?.emailVerified]);

  useEffect(() => {
    const paymentReturnInvoiceId = sessionStorage.getItem(PAYMENT_RETURN_KEY) || "";
    if (!siteToken || !paymentReturnInvoiceId) {
      return;
    }
    let cancelled = false;
    let timer = 0;

    async function checkPayment(attempt = 0) {
      try {
        const payment = await sitePaymentStatus(siteToken, paymentReturnInvoiceId);
        if (cancelled) {
          return;
        }
        if (payment.status === "paid") {
          setPaymentMessage(payment.purpose === "renewal"
            ? (language === "en"
              ? `Renewal confirmed${payment.licenseExpiresAt ? `. Access is active until ${formatSubscriptionDate(payment.licenseExpiresAt, language)}.` : "."}`
              : `Продление подтверждено${payment.licenseExpiresAt ? `. Доступ действует до ${formatSubscriptionDate(payment.licenseExpiresAt, language)}.` : "."}`)
            : (language === "en" ? "Payment received. Your key is ready." : "Оплата получена. Ключ готов."));
          sessionStorage.removeItem(PAYMENT_RETURN_KEY);
          await refreshDashboard();
          return;
        }
        if (payment.status === "paid_conflict") {
          setPaymentMessage(language === "en"
            ? "Payment was received, but the order needs a support review."
            : "Оплата получена, но заказ требует проверки поддержкой.");
          sessionStorage.removeItem(PAYMENT_RETURN_KEY);
          return;
        }
        if (payment.status !== "pending") {
          return;
        }
        if (attempt < 5) {
          timer = window.setTimeout(() => checkPayment(attempt + 1), 1500);
          return;
        }
        setPaymentMessage(language === "en"
          ? "Payment is still being confirmed. Refresh the account in a moment."
          : "Платёж ещё подтверждается. Обновите кабинет через несколько секунд.");
        sessionStorage.removeItem(PAYMENT_RETURN_KEY);
      } catch (error) {
        if (!cancelled) {
          setDashboardError(authErrorText(error, language));
        }
      }
    }

    checkPayment();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [siteToken]);

  useEffect(() => {
    if (!dashboard?.project) {
      return;
    }
    setProjectForm({
      displayName: dashboard.project.displayName || "",
      game: dashboard.project.game === "Rust" ? "Rust" : "DayZ",
      ownerDisplayName: dashboard.project.ownerDisplayName || "",
    });
  }, [dashboard?.project?.id, dashboard?.project?.displayName, dashboard?.project?.game, dashboard?.project?.ownerDisplayName]);

  useEffect(() => {
    if (!authOpen) {
      return;
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setAuthOpen(false);
      }
    }

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [authOpen]);

  useEffect(() => {
    if (page !== "account" || user || siteToken || sessionHydrating) {
      return;
    }

    openAuthDialog();
  }, [page, user, siteToken, sessionHydrating]);

  const downloadUnavailable = !release.downloadUrl;
  const downloadLabel = downloadBusy
    ? copy.download.preparing
    : downloadUnavailable && !releaseHydrating
      ? copy.download.unavailable
      : copy.download.ready;

  function updateField(field: keyof AuthForm, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function updatePurchaseField(field: keyof PurchaseForm, value: string) {
    setPurchaseForm((current) => ({ ...current, [field]: value }));
  }

  function updateProjectField(field: keyof PurchaseForm, value: string) {
    setProjectForm((current) => ({ ...current, [field]: value }));
  }

  function updateModeratorField(field: keyof ModeratorForm, value: string) {
    setModeratorForm((current) => ({
      ...current,
      [field]: field === "steamId64" ? normalizeSteamId64(value) : limitModeratorName(value),
    }));
  }

  function persistAuth(token: string, nextUser: SiteUser) {
    localStorage.setItem(SITE_TOKEN_KEY, token);
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(nextUser));
    setSiteToken(token);
    setUser(nextUser);
  }

  function persistSteamAuth(token: string, nextUser: SiteUser) {
    persistAuth(token, nextUser);
    sessionStorage.removeItem(STEAM_CALLBACK_ERROR_KEY);
    setSteamCallbackError("");
    setSteamEmail(nextUser.email || "");
    setSteamEmailSent(false);
  }

  async function submitAuth(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setAuthBusy(true);
    setAuthError("");
    setAuthMessage("");

    try {
      if (authMode === "register") {
        const result = await siteRegister({ email: form.email, password: form.password, language });
        setAuthMode("login");
        setAuthMessage(language === "en"
          ? `Confirmation email sent to ${result.email}. The account will appear after you open the link.`
          : `Письмо подтверждения отправлено на ${result.email}. Аккаунт появится после перехода по ссылке.`);
        return;
      }
      if (authMode === "forgot") {
        await siteForgotPassword(form.email, language);
        setAuthMode("login");
        setAuthMessage(language === "en"
          ? "If the account exists, we sent a password reset email."
          : "Если аккаунт существует, мы отправили письмо для восстановления пароля.");
        return;
      }
      if (recoveryMode) {
        if (!resetSession) {
          setAuthError(language === "en" ? "Open a new password reset link to continue." : "Откройте новую ссылку восстановления, чтобы продолжить.");
          return;
        }
        await siteResetPassword({ resetSession, password: form.password });
        clearPasswordResetSession();
        setForm({ email: "", password: "" });
        setShowPassword(false);
        setAuthOpen(false);
        setPasswordResetState("completed");
        setAuthMode("login");
        setAuthMessage("");
        return;
      }
      const result = await siteLogin({ email: form.email, password: form.password, language });
      if (!result.ok) {
        setAuthOpen(false);
        setTwoFactorLoginChallenge({ kind: "email" });
        return;
      }
      persistAuth(result.siteToken, result.user);
      setAuthOpen(false);
      setAuthMessage("");
      const nextPath = developerReauthentication ? "/developer" : "/";
      setDeveloperReauthentication(false);
      navigate(nextPath);
    } catch (error) {
      if (authMode === "reset") {
        if (isTerminalPasswordResetError(error)) {
          clearPasswordResetSession();
          setPasswordResetState("terminal_error");
        }
        setAuthError(resetPasswordErrorText(error, language));
      } else {
        setAuthError(authErrorText(error, language));
      }
    } finally {
      setAuthBusy(false);
    }
  }

  async function completeTwoFactorLogin(code: string) {
    if (!twoFactorLoginChallenge) return;
    setAuthBusy(true);
    setAuthError("");
    try {
      if (twoFactorLoginChallenge.kind === "steam_setup") {
        const result = await siteTwoFactorLoginSetupConfirm(twoFactorLoginChallenge.twoFactorLoginSession, code);
        persistSteamAuth(result.siteToken, result.user);
      } else if (twoFactorLoginChallenge.kind === "steam_login") {
        const result = await siteCompleteTwoFactorLogin(twoFactorLoginChallenge.twoFactorLoginSession, code);
        persistSteamAuth(result.siteToken, result.user);
      } else {
        const result = await siteLogin({ email: form.email, password: form.password, language, twoFactorCode: code });
        if (!result.ok) throw new Error(result.status);
        persistAuth(result.siteToken, result.user);
      }
      setTwoFactorLoginChallenge(null);
      setAuthOpen(false);
      setForm((current) => ({ ...current, password: "" }));
      const nextPath = developerReauthentication ? "/developer" : "/";
      setDeveloperReauthentication(false);
      navigate(nextPath, true);
    } finally {
      setAuthBusy(false);
    }
  }

  const startTwoFactorLoginSetup = useCallback(async (): Promise<SiteTwoFactorSetup> => {
    if (!twoFactorLoginChallenge || twoFactorLoginChallenge.kind !== "steam_setup") throw new Error("invalid_two_factor_login_session");
    return siteTwoFactorLoginSetupStart(twoFactorLoginChallenge.twoFactorLoginSession);
  }, [twoFactorLoginChallenge]);

  async function startProviderAuth(provider: AuthProvider) {
    setAuthBusy(true);
    setAuthError("");
    setAuthMessage("");

    try {
      const redirectUri = `${window.location.origin}/auth/callback/${provider}`;
      const result = await siteOAuthStart(provider, redirectUri, language);
      window.location.assign(result.authUrl);
    } catch (error) {
      setAuthError(authErrorText(error, language));
      setAuthBusy(false);
    }
  }

  async function startSteamAuth(mode: "login" | "link" = "login") {
    setAuthBusy(true);
    setAuthError("");
    sessionStorage.removeItem(STEAM_CALLBACK_ERROR_KEY);
    setSteamCallbackError("");
    setAuthMessage("");
    setDashboardError("");

    try {
      const redirectUri = steamCallbackUri();
      const result = await siteSteamStart({
        redirectUri,
        language,
        ...(mode === "link" && siteToken ? { mode: "link" as const, siteToken } : {}),
      });
      window.location.assign(result.authUrl);
    } catch (error) {
      console.warn("Steam auth start failed:", error instanceof Error ? error.message : error);
      const text = authErrorText(error, language);
      if (mode === "link") {
        setDashboardError(text);
      } else {
        setAuthError(text);
      }
      setAuthBusy(false);
    }
  }

  async function downloadApp() {
    if (downloadBusy) {
      return;
    }

    setDownloadBusy(true);
    setDownloadError("");

    try {
      if (!release.downloadUrl) {
        setDownloadError(copy.download.errorUnavailable);
        return;
      }

      window.location.assign(release.downloadUrl);
      setDownloadCount((count) => {
        if (count === null) return null;
        const nextCount = count + 1;
        localStorage.setItem(DOWNLOAD_COUNT_CACHE_KEY, String(nextCount));
        return nextCount;
      });
    } catch {
      setDownloadError(copy.download.errorFailed);
    } finally {
      setDownloadBusy(false);
    }
  }

  async function logout() {
    const confirmed = await requestConfirmation({
      title: language === "en" ? "Sign out?" : "Выйти из аккаунта?",
      message: language === "en"
        ? "Your session on this device will end."
        : "Текущая сессия на этом устройстве будет завершена.",
      confirmLabel: language === "en" ? "Sign out" : "Выйти",
      cancelLabel: language === "en" ? "Stay" : "Остаться",
      icon: "logout",
    });
    if (!confirmed) {
      return;
    }
    const token = siteToken;
    setAuthOpen(false);
    navigate("/", true);
    localStorage.removeItem(SITE_TOKEN_KEY);
    localStorage.removeItem(USER_CACHE_KEY);
    localStorage.removeItem(DASHBOARD_CACHE_KEY);
    if (user?.id) localStorage.removeItem(paymentConfigCacheKey(user.id));
    setSiteToken("");
    setUser(null);
    setDashboard(null);
    setAuthMessage("");
    setAuthError("");

    if (token) {
      try {
        await siteLogout(token);
      } catch {
        // Local logout should not be blocked by a transient network failure.
      }
    }
  }

  async function refreshDashboard() {
    if (!siteToken) {
      return;
    }
    setDashboardBusy(true);
    setDashboardError("");
    try {
      if (user?.role === "moderator") {
        const result = await fetchSiteModeratorDashboard(siteToken);
        const moderatorUser = moderatorSessionUser(result);
        setModeratorDashboard(result);
        setDashboard(null);
        setPaymentConfig(null);
        setPendingPayment(null);
        setUser(moderatorUser);
        localStorage.setItem(USER_CACHE_KEY, JSON.stringify(moderatorUser));
        return;
      }
      const [result, nextPaymentConfig, payment] = await Promise.all([
        fetchSiteDashboard(siteToken),
        fetchSitePaymentConfig(siteToken, language).catch(() => null),
        sitePaymentStatus(siteToken).catch(() => null),
      ]);
      const safeDashboard = withoutOwnerKey(result);
      setDashboard(safeDashboard);
      setPaymentConfig(nextPaymentConfig);
      setPendingPayment(payment?.status === "pending" ? payment : null);
      const pendingText = payment ? pendingPaymentText(payment.status, payment.invoiceId, language) : "";
      if (pendingText) {
        setPaymentMessage(pendingText);
      }
      setUser(safeDashboard.account);
    } catch (error) {
      setDashboardError(authErrorText(error, language));
    } finally {
      setDashboardBusy(false);
    }
  }

  async function resendVerification() {
    if (!siteToken) {
      return;
    }
    setDashboardBusy(true);
    setDashboardError("");
    try {
      const result = await siteResendVerification(siteToken, language);
      setUser(result.user);
      setAuthOpen(false);
      setDashboardNotice(result.status === "already_verified"
        ? language === "en" ? "Email is already confirmed." : "Почта уже подтверждена."
        : language === "en" ? "Confirmation email sent." : "Письмо подтверждения отправлено.");
    } catch (error) {
      setDashboardError(authErrorText(error, language));
    } finally {
      setDashboardBusy(false);
    }
  }

  async function attachSteamEmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const email = steamEmail.trim();
    if (!siteToken || !email) {
      return;
    }
    setDashboardBusy(true);
    setDashboardError("");
    try {
      const result = await siteAttachEmail(siteToken, email, language);
      setUser((current) => ({ ...(current || result.user), ...result.user }));
      setSteamEmail(result.user.email || email);
      setSteamEmailSent(true);
      setAuthOpen(false);
      setDashboardNotice(language === "en"
        ? "Email sent. Confirm it before buying a key."
        : "Письмо отправлено. Подтвердите email перед покупкой ключа.");
      await refreshDashboard();
    } catch (error) {
      setDashboardError(authErrorText(error, language));
    } finally {
      setDashboardBusy(false);
    }
  }

  async function submitCheckoutEmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const email = checkoutEmail.trim();
    if (!siteToken || !email) return;
    setCheckoutEmailBusy(true);
    setCheckoutEmailError("");
    try {
      const result = await siteAttachEmail(siteToken, email, language);
      setUser((current) => ({ ...(current || result.user), ...result.user }));
      setSteamEmail(result.user.email || email);
      if (result.user.emailVerified) {
        await refreshDashboard();
        setResumeCheckoutAfterEmailVerification(true);
        return;
      }
      localStorage.setItem(CHECKOUT_EMAIL_RESUME_KEY, String(Date.now()));
      setCheckoutEmailSent(true);
    } catch (error) {
      setCheckoutEmailError(authErrorText(error, language));
    } finally {
      setCheckoutEmailBusy(false);
    }
  }

  function updatePromoCode(value: string) {
    setPromoCode(value.toUpperCase().slice(0, 32));
    setPromoPreview(null);
    setPromoError("");
  }

  async function applyPromoCode(planId = selectedPlanId) {
    const code = promoCode.trim();
    if (!siteToken || !code) return;
    setPromoBusy(true);
    setPromoError("");
    setPromoPreview(null);
    try {
      const result = await sitePromoPreflight(siteToken, code, planId, language);
      setPromoCode(result.promo.code);
      setPromoPreview(result);
    } catch (error) {
      setPromoError(authErrorText(error, language));
    } finally {
      setPromoBusy(false);
    }
  }

  function clearPromoCode() {
    setPromoCode("");
    setPromoPreview(null);
    setPromoError("");
  }

  async function createPayment(method: "sbp" | "bank_card") {
    if (!siteToken) {
      return;
    }
    setDashboardBusy(true);
    setDashboardError("");
    setPaymentMessage("");
    setPaymentError("");
    try {
      const purpose = paymentConfig?.subscription?.mode;
      const result = await siteCreatePayment(siteToken, { planId: selectedPlanId, method, language, ...(purpose ? { purpose } : {}), ...(promoPreview ? { promoCode: promoPreview.promo.code } : {}) });
      if ((!result.paymentForm && !result.paymentUrl) || !result.invoiceId) {
        throw new Error("payment_url_missing");
      }
      sessionStorage.setItem(PAYMENT_RETURN_KEY, result.invoiceId);
      if (result.paymentForm) {
        submitRobokassaPayment(result.paymentForm);
        return;
      }
      window.location.assign(result.paymentUrl!);
    } catch (error) {
      const status = error instanceof Error ? error.message : String(error || "");
      setPaymentError(paymentErrorText(error, language));
      if (["active_payment_exists", "payment_already_pending", "http_409"].includes(status)) {
        await refreshDashboard();
      }
    } finally {
      setDashboardBusy(false);
    }
  }

  async function cancelPendingPayment() {
    if (!siteToken || pendingPayment?.status !== "pending") return;
    setDashboardBusy(true);
    setPaymentError("");
    try {
      const result = await siteCancelPayment(siteToken, pendingPayment.invoiceId);
      sessionStorage.removeItem(PAYMENT_RETURN_KEY);
      setPendingPayment(null);
      setPaymentMessage(result.status === "cancelled"
        ? (language === "en" ? "Payment request cancelled. You can choose different terms now." : "Заявка на оплату отменена. Теперь можно выбрать другие условия.")
        : (language === "en" ? "This payment is no longer pending. The current status has been refreshed." : "Эта заявка больше не ожидает оплаты. Текущий статус обновлён."));
      await refreshDashboard();
    } catch (error) {
      setPaymentError(paymentErrorText(error, language, "cancel"));
    } finally {
      setDashboardBusy(false);
    }
  }

  async function createModeratorFromDashboard(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!siteToken) {
      return;
    }
    const name = moderatorForm.name.trim();
    const nameError = moderatorNameError(name, language);
    if (nameError) {
      setDashboardError(nameError);
      return;
    }
    const steamId64 = normalizeSteamId64(moderatorForm.steamId64);
    const steamError = steamId64Error(steamId64, language);
    if (steamError) {
      setDashboardError(steamError);
      return;
    }
    setDashboardBusy(true);
    setDashboardError("");
    try {
      await siteCreateModerator(siteToken, { ...moderatorForm, name, steamId64 });
      setModeratorForm({ name: "", steamId64: "" });
      await refreshDashboard();
    } catch (error) {
      setDashboardError(authErrorText(error, language));
    } finally {
      setDashboardBusy(false);
    }
  }

  async function updateProjectFromDashboard(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!siteToken || !projectForm.displayName.trim()) {
      return;
    }
    setDashboardBusy(true);
    setDashboardError("");
    setDashboardNotice("");
    try {
      await siteUpdateProjectInfo(siteToken, projectForm);
      setDashboardNotice(language === "en" ? "Project updated." : "Проект обновлён.");
      await refreshDashboard();
    } catch (error) {
      setDashboardError(authErrorText(error, language));
    } finally {
      setDashboardBusy(false);
    }
  }

  async function renameModeratorFromDashboard(moderatorId: string, currentName: string) {
    if (!siteToken) {
      return;
    }
    const name = await requestModeratorName({
      title: language === "en" ? "Rename moderator" : "Переименовать модератора",
      message: language === "en" ? "Choose a name up to 20 characters." : "Укажите имя длиной до 20 символов.",
      inputLabel: language === "en" ? "Moderator name" : "Имя модератора",
      confirmLabel: language === "en" ? "Save" : "Сохранить",
      cancelLabel: language === "en" ? "Cancel" : "Отмена",
      initialValue: currentName,
    });
    if (name === null) {
      return;
    }
    const trimmedName = name.trim();
    const nameError = moderatorNameError(trimmedName, language);
    if (nameError) {
      setDashboardError(nameError);
      return;
    }
    setDashboardBusy(true);
    setDashboardError("");
    try {
      await siteRenameModerator(siteToken, { moderatorId, name: trimmedName });
      await refreshDashboard();
    } catch (error) {
      setDashboardError(authErrorText(error, language));
    } finally {
      setDashboardBusy(false);
    }
  }

  async function removeModeratorFromDashboard(moderatorId: string, currentName: string) {
    if (!siteToken) {
      return;
    }
    const displayName = currentName || moderatorId;
    const confirmed = await requestConfirmation({
      title: language === "en" ? "Remove moderator?" : "Удалить модератора?",
      message: language === "en"
        ? `“${displayName}” will lose access to the project. This action cannot be undone.`
        : `«${displayName}» потеряет доступ к проекту. Это действие нельзя отменить.`,
      confirmLabel: language === "en" ? "Remove" : "Удалить",
      cancelLabel: language === "en" ? "Cancel" : "Отмена",
      icon: "delete",
    });
    if (!confirmed) {
      return;
    }
    const twoFactorCode = await requestTwoFactorStepUp();
    if (twoFactorCode === null) return;
    setDashboardBusy(true);
    setDashboardError("");
    try {
      await siteRemoveModerator(siteToken, moderatorId, twoFactorCode || undefined);
      await refreshDashboard();
    } catch (error) {
      setDashboardError(authErrorText(error, language));
    } finally {
      setDashboardBusy(false);
    }
  }

  async function createModeratorCheck(payload: Parameters<typeof siteModeratorCreateCheck>[1]): Promise<SiteModeratorCheckResult | null> {
    if (!siteToken) {
      return null;
    }
    setDashboardBusy(true);
    setDashboardError("");
    try {
      const result = await siteModeratorCreateCheck(siteToken, payload);
      await refreshDashboard();
      return result;
    } catch (error) {
      setDashboardError(authErrorText(error, language));
    } finally {
      setDashboardBusy(false);
    }
    return null;
  }

  async function createProjectFromDashboard(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!siteToken || !purchaseForm.displayName.trim() || !purchaseForm.ownerDisplayName.trim()) return;
    setDashboardBusy(true);
    setDashboardError("");
    try {
      const result = await siteCreateProject(siteToken, purchaseForm);
      await refreshDashboard();
    } catch (error) {
      setDashboardError(authErrorText(error, language));
    } finally {
      setDashboardBusy(false);
    }
  }

  async function requestProjectDeletion() {
    if (!siteToken) return;
    setAuthOpen(false);
    const confirmed = await requestConfirmation({
      title: language === "en" ? "Delete project?" : "Удалить проект?",
      message: language === "en" ? "We will email a confirmation link. Nothing is deleted until you confirm it there." : "Мы отправим ссылку для подтверждения на почту. До подтверждения проект не удаляется.",
      confirmLabel: language === "en" ? "Send email" : "Отправить письмо",
      cancelLabel: language === "en" ? "Cancel" : "Отмена",
      icon: "delete",
    });
    if (!confirmed) return;
    const twoFactorCode = await requestTwoFactorStepUp();
    if (twoFactorCode === null) return;
    setDashboardBusy(true);
    setDashboardError("");
    try {
      await siteRequestProjectDeletion(siteToken, language, twoFactorCode || undefined);
      setPaymentMessage(language === "en" ? "Confirmation email sent. The link is valid for 15 minutes." : "Письмо с подтверждением отправлено. Ссылка действует 15 минут.");
    } catch (error) {
      setDashboardError(projectDeletionErrorText(error, language));
    } finally {
      setDashboardBusy(false);
    }
  }

  async function confirmProjectDeletion() {
    if (!deleteProjectToken) return;
    setAuthOpen(false);
    const confirmed = await requestConfirmation({
      title: language === "en" ? "Permanently delete project?" : "Удалить проект безвозвратно?",
      message: language === "en" ? "The project, keys, moderators and check history will be deleted permanently." : "Проект, ключи, модераторы и история проверок будут удалены без возможности восстановления.",
      confirmLabel: language === "en" ? "Delete permanently" : "Удалить безвозвратно",
      cancelLabel: language === "en" ? "Cancel" : "Отмена",
      icon: "delete",
    });
    if (!confirmed) return;
    const twoFactorCode = await requestTwoFactorStepUp();
    if (twoFactorCode === null) return;
    setDashboardBusy(true);
    setDashboardError("");
    try {
      const result = await siteConfirmProjectDeletion(deleteProjectToken, twoFactorCode || undefined);
      if (result.status !== "deleted") {
        throw new Error("confirmation_invalid");
      }
      localStorage.removeItem(SITE_TOKEN_KEY);
      localStorage.removeItem(USER_CACHE_KEY);
      localStorage.removeItem(DASHBOARD_CACHE_KEY);
      setSiteToken("");
      setUser(null);
      setDashboard(null);
      setModeratorDashboard(null);
      setDeveloperDashboard(null);
      setPaymentConfig(null);
      setPaymentMessage("");
      setSessionHydrating(false);
      setProjectDeletionComplete(true);
      navigate("/account", true);
    } catch (error) {
      setDashboardError(projectDeletionErrorText(error, language));
    } finally {
      setDashboardBusy(false);
    }
  }

  async function finishModeratorCheck(payload: Parameters<typeof siteModeratorFinishCheck>[1]) {
    if (!siteToken) {
      return;
    }
    setDashboardBusy(true);
    setDashboardError("");
    try {
      await siteModeratorFinishCheck(siteToken, payload);
      await refreshDashboard();
    } catch (error) {
      setDashboardError(authErrorText(error, language));
    } finally {
      setDashboardBusy(false);
    }
  }

  async function cancelModeratorCheck(sessionId: string) {
    if (!siteToken) {
      return;
    }
    setDashboardBusy(true);
    setDashboardError("");
    try {
      await siteModeratorCancelCheck(siteToken, sessionId);
      await refreshDashboard();
    } catch (error) {
      setDashboardError(authErrorText(error, language));
    } finally {
      setDashboardBusy(false);
    }
  }

  async function createOwnerCheck(payload: Parameters<typeof siteOwnerCreateCheck>[1]): Promise<SiteModeratorCheckResult | null> {
    if (!siteToken) return null;
    setDashboardBusy(true);
    setDashboardError("");
    try {
      const result = await siteOwnerCreateCheck(siteToken, payload);
      await refreshDashboard();
      return result;
    } catch (error) {
      setDashboardError(authErrorText(error, language));
      return null;
    } finally {
      setDashboardBusy(false);
    }
  }

  async function finishOwnerCheck(payload: Parameters<typeof siteOwnerFinishCheck>[1]) {
    if (!siteToken) return;
    setDashboardBusy(true);
    setDashboardError("");
    try {
      await siteOwnerFinishCheck(siteToken, payload);
      await refreshDashboard();
    } catch (error) {
      setDashboardError(authErrorText(error, language));
    } finally {
      setDashboardBusy(false);
    }
  }

  async function cancelOwnerCheck(sessionId: string) {
    if (!siteToken) return;
    setDashboardBusy(true);
    setDashboardError("");
    try {
      await siteOwnerCancelCheck(siteToken, sessionId);
      await refreshDashboard();
    } catch (error) {
      setDashboardError(authErrorText(error, language));
    } finally {
      setDashboardBusy(false);
    }
  }

  async function getSecuritySettings(): Promise<SiteSecuritySettings> {
    if (!siteToken) throw new Error("invalid_site_session");
    return siteGetSecuritySettings(siteToken);
  }

  async function getOwnerModeratorStats(moderatorId: string): Promise<SiteOwnerModeratorStats> {
    if (!siteToken) throw new Error("invalid_site_session");
    return siteOwnerModeratorStats(siteToken, moderatorId);
  }

  async function startTwoFactorSetup(): Promise<SiteTwoFactorSetup> {
    if (!siteToken) throw new Error("invalid_site_session");
    setDashboardBusy(true);
    try {
      return await siteTwoFactorSetupStart(siteToken);
    } finally {
      setDashboardBusy(false);
    }
  }

  async function confirmTwoFactorSetup(code: string): Promise<boolean> {
    if (!siteToken) throw new Error("invalid_site_session");
    setDashboardBusy(true);
    try {
      await siteTwoFactorSetupConfirm(siteToken, code);
      return true;
    } finally {
      setDashboardBusy(false);
    }
  }

  async function disableTwoFactor(code: string): Promise<boolean> {
    if (!siteToken) throw new Error("invalid_site_session");
    setDashboardBusy(true);
    try {
      await siteTwoFactorDisable(siteToken, code);
      return true;
    } finally {
      setDashboardBusy(false);
    }
  }

  function requestRecentPasswordLogin() {
    setPasswordConfirmationOpen(true);
  }

  async function confirmRecentPasswordLogin(password: string, twoFactorCode: string) {
    if (!user?.email) throw new Error("email_required");
    const result = await siteLogin({
      email: user.email,
      password,
      language,
      ...(twoFactorCode ? { twoFactorCode } : {}),
    });
    if (!result.ok) throw new Error(result.status);
    persistAuth(result.siteToken, result.user);
    setPasswordConfirmationOpen(false);
    setDashboardNotice(language === "en" ? "Password confirmed. Continue with the security action." : "Пароль подтверждён. Продолжите действие в настройках.");
  }

  const legalDocument = copy.legalDocuments[window.location.pathname as keyof typeof copy.legalDocuments];
  const canOpenCabinet = hasPaidAccess || isDeveloperAccount(user) || user?.role === "moderator";
  const cabinetSessionPending = Boolean(siteToken && !user && sessionHydrating);
  const showCabinetEntry = canOpenCabinet || cabinetSessionPending;
  const subscriptionPlans = paymentConfig?.plans?.filter((plan) => plan.active) || (language === "en" ? FALLBACK_SUBSCRIPTION_PLANS_EN : FALLBACK_SUBSCRIPTION_PLANS);
  const selectedPlan = subscriptionPlans.find((plan) => plan.id === selectedPlanId) || subscriptionPlans[0];

  useEffect(() => {
    if (page !== "account" || sessionHydrating || user?.role === "moderator" || isDeveloperAccount(user)) {
      return;
    }
    if (!siteToken || !user || (paymentConfig && !hasPaidAccess)) {
      navigate("/", true);
    }
  }, [page, sessionHydrating, siteToken, user?.id, user?.role, paymentConfig?.access?.status, dashboard?.project?.id]);

  function selectSubscriptionPlan(planId: SubscriptionPlanId) {
    if (promoBusy || planId === selectedPlanId) return;
    localStorage.setItem(SELECTED_PLAN_KEY, planId);
    setSelectedPlanId(planId);
    if (promoPreview && promoCode.trim()) void applyPromoCode(planId);
  }

  if (recoveryMode) {
    return <PasswordRecoveryPage language={language} form={form} updateField={updateField} showPassword={showPassword} setShowPassword={setShowPassword} submit={submitAuth} busy={authBusy} error={authError} message={authMessage} state={passwordResetState} retryExchange={() => void exchangePasswordReset(resetToken)} requestNewLink={requestNewPasswordResetLink} continueToLogin={continueAfterPasswordReset} />;
  }

  return (
    <div className="site-shell">
      <div className="site-background" aria-hidden="true">
        <div className="grid-plane" />
        <div className="blue-horizon" />
      </div>

      <header className="topbar">
        <a className="brand" href="/" aria-label="CheatChecker">
          <img src="/cc_logo.png" alt="" className="brand-mark" />
          <span>CheatChecker</span>
        </a>
        <nav className="nav-links" aria-label={copy.navLabel}>
          <button className="nav-contact" type="button" onClick={scrollToContacts}>
            {copy.contacts}
          </button>
          {isDeveloperAccount(user) ? (
            <button className="nav-contact" type="button" onClick={() => navigate("/developer")}>
              {language === "en" ? "Developer" : "Разработка"}
            </button>
          ) : null}
          <button
            className="nav-login"
            type="button"
            onClick={() => {
              if (!user && !siteToken) openAuthDialog();
              else if (showCabinetEntry) navigate("/account");
              else void logout();
            }}
          >
            {!user && !siteToken ? copy.login : showCabinetEntry ? copy.account : language === "en" ? "Sign out" : "Выйти"}
          </button>
          <div className="language-menu" ref={languageMenuRef}>
            <button
              className="language-trigger"
              type="button"
              aria-haspopup="listbox"
              aria-expanded={languageMenuOpen}
              onClick={() => setLanguageMenuOpen((open) => !open)}
            >
              <span className={`language-flag flag-${language}`} aria-hidden="true" />
              <span className="language-code">{copy.langName}</span>
              <span className="language-caret" aria-hidden="true" />
            </button>
            {languageMenuOpen ? (
              <div className="language-options" role="listbox" aria-label={language === "ru" ? "Выбор языка" : "Language selection"}>
                <button
                  className={language === "ru" ? "is-selected" : ""}
                  type="button"
                  role="option"
                  aria-selected={language === "ru"}
                  onClick={() => selectLanguage("ru")}
                >
                  <span className="language-flag flag-ru" aria-hidden="true" />
                  <span className="language-option-code">RU</span>
                </button>
                <button
                  className={language === "en" ? "is-selected" : ""}
                  type="button"
                  role="option"
                  aria-selected={language === "en"}
                  onClick={() => selectLanguage("en")}
                >
                  <span className="language-flag flag-en" aria-hidden="true" />
                  <span className="language-option-code">EN</span>
                </button>
              </div>
            ) : null}
          </div>
        </nav>
      </header>

      <main id="top">
        {legalDocument ? (
          <LegalPage document={legalDocument} copy={copy} />
        ) : page === "developer" ? (
          <section className="section account-section">
            {isDeveloperAccount(user) ? (
              developerReauthenticationRequired ? <DeveloperReauthenticationGate
                language={language}
                message={developerError}
                onLogin={openDeveloperReauthentication}
              /> : developerVerificationStage ? <DeveloperVerificationGate
                stage={developerVerificationStage}
                busy={developerVerificationBusy}
                error={developerVerificationError}
                message={developerVerificationMessage}
                language={language}
                onRequest={() => void requestDeveloperVerification()}
                onVerify={(code) => void verifyDeveloper(code)}
              /> : <DeveloperDashboardPanel
                data={developerDashboard}
                busy={developerBusy}
                error={developerError}
                language={language}
                promoCodes={developerPromoCodes}
                onRefresh={refreshDeveloperDashboard}
                onCreatePromo={createDeveloperPromoCode}
                onUpdatePromo={updateDeveloperPromoCode}
                onLogout={logout}
              />
            ) : <AccountQuietShell />}
          </section>
        ) : page === "account" ? (
          <section className="section account-section">
            {projectDeletionComplete ? <ProjectDeletedShell language={language} onHome={() => navigate("/", true)} /> : user || dashboard || siteToken || sessionHydrating ? (
              user?.role === "moderator" ? (
                <ModeratorCommandCenter
                  currentUser={user}
                  dashboard={moderatorDashboard}
                  busy={dashboardBusy || sessionHydrating}
                  error={dashboardError}
                  createCheck={createModeratorCheck}
                  finishCheck={finishModeratorCheck}
                  cancelCheck={cancelModeratorCheck}
                  logout={logout}
                  language={language}
                />
              ) : <AccountCommandCenter
                currentUser={user}
                dashboard={dashboard}
                busy={dashboardBusy || sessionHydrating}
                error={dashboardError}
                notice={dashboardNotice}
                paymentConfig={paymentConfig}
                selectedPlan={selectedPlan}
                paymentMessage={paymentMessage}
                openRenewal={openRenewal}
                purchaseForm={purchaseForm}
                projectForm={projectForm}
                moderatorForm={moderatorForm}
                steamEmail={steamEmail}
                steamEmailSent={steamEmailSent}
                setSteamEmail={setSteamEmail}
                updatePurchaseField={updatePurchaseField}
                updateProjectField={updateProjectField}
                updateModeratorField={updateModeratorField}
                attachSteamEmail={attachSteamEmail}
                resendVerification={resendVerification}
                startSteamLink={() => startSteamAuth("link")}
                createPayment={createPayment}
                promoCode={promoCode}
                promoPreview={promoPreview}
                promoBusy={promoBusy}
                promoError={promoError}
                updatePromoCode={updatePromoCode}
                applyPromoCode={applyPromoCode}
                clearPromoCode={clearPromoCode}
                createProject={createProjectFromDashboard}
                requestProjectDeletion={requestProjectDeletion}
                confirmProjectDeletion={deleteProjectToken ? confirmProjectDeletion : undefined}
                updateProject={updateProjectFromDashboard}
                createModerator={createModeratorFromDashboard}
                renameModerator={renameModeratorFromDashboard}
                removeModerator={removeModeratorFromDashboard}
                refreshDashboard={refreshDashboard}
                createOwnerCheck={createOwnerCheck}
                finishOwnerCheck={finishOwnerCheck}
                cancelOwnerCheck={cancelOwnerCheck}
                getOwnerModeratorStats={getOwnerModeratorStats}
                getSecuritySettings={getSecuritySettings}
                startTwoFactorSetup={startTwoFactorSetup}
                confirmTwoFactorSetup={confirmTwoFactorSetup}
                disableTwoFactor={disableTwoFactor}
                requestRecentPasswordLogin={requestRecentPasswordLogin}
                logout={logout}
                language={language}
              />
            ) : (
              <AccountQuietShell />
            )}
          </section>
        ) : (
          <>
            <section className="hero-section">
              <div className="hero-copy">
                <h1>{copy.hero.title}</h1>
                <ul className="hero-points" aria-label={copy.hero.aria}>
                  {copy.hero.points.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <button className="hero-download" type="button" disabled={downloadBusy || downloadUnavailable} onClick={downloadApp}>
                  <Download size={21} />
                  {downloadLabel}
                </button>
                <button className="hero-buy" type="button" onClick={() => scrollToPlans()}>
                  {copy.hero.buy}
                </button>
                <p className="download-stat">{copy.download.count}: <strong>{downloadCount === null ? "—" : downloadCount.toLocaleString(language === "en" ? "en-US" : "ru-RU")}</strong></p>
                {downloadError ? <p className="download-error">{downloadError}</p> : null}
              </div>
            </section>

            <section className="section purchase-section" id="plans" aria-label={language === "en" ? "License purchase" : "Покупка лицензии"}>
              <div className="section-heading purchase-heading"><h2>{language === "en" ? "Plans" : "Тарифы"}</h2></div>
              <SubscriptionPlanCard language={language} plans={subscriptionPlans} selectedPlan={selectedPlan} busy={promoBusy} renewal={paymentConfig?.subscription?.mode === "renewal" || Boolean(dashboard?.project)} highlightCheckout={plansHighlighted} onSelect={selectSubscriptionPlan} onCheckout={openPurchaseOrAuth} />
            </section>

          </>
        )}
      </main>

      <footer className="footer" id="contacts">
        <div className="footer-brand">
          <span>CheatChecker</span>
          <p>{copy.footer.text}</p>
        </div>
        <div className={contactsHighlighted ? "footer-contacts is-highlighted" : "footer-contacts"} aria-label={copy.footer.contactsLabel}>
          <h2>{copy.footer.contactsLabel}</h2>
          <a href="mailto:support@cheatchecker.ru">
            <Mail size={18} />
            <span>{copy.footer.support}</span>
          </a>
          <a href="tel:+79671200076">
            <Phone size={18} />
            <span>{copy.footer.phone}</span>
          </a>
        </div>
        <nav className="footer-links" aria-label={copy.footer.docsLabel}>
          <h2>{copy.footer.docsLabel}</h2>
          <a href="/terms">{copy.footer.terms}</a>
          <a href="/privacy">{copy.footer.privacy}</a>
        </nav>
        <div className="footer-legal-line">{copy.footer.legal}</div>
        <div className="footer-rights">© 2026 CheatChecker. {language === "en" ? "All rights reserved." : "Все права защищены."}</div>
      </footer>

      {confirmation ? (
        <ConfirmationDialog request={confirmation} onClose={finishConfirmation} language={language} />
      ) : null}

      {renameRequest ? (
        <RenameModeratorDialog request={renameRequest} onClose={finishRename} />
      ) : null}

      {twoFactorStepUp ? <TwoFactorStepUpDialog language={language} onClose={finishTwoFactorStepUp} /> : null}

      {twoFactorLoginChallenge ? <TwoFactorLoginDialog
        challenge={twoFactorLoginChallenge}
        language={language}
        busy={authBusy}
        onClose={() => { if (!authBusy) { setTwoFactorLoginChallenge(null); setForm((current) => ({ ...current, password: "" })); setAuthOpen(true); } }}
        onConfirm={completeTwoFactorLogin}
        onStartSetup={startTwoFactorLoginSetup}
      /> : null}

      {passwordConfirmationOpen ? <PasswordConfirmationDialog language={language} onClose={() => setPasswordConfirmationOpen(false)} onConfirm={confirmRecentPasswordLogin} /> : null}

      {checkoutEmailGateOpen ? <CheckoutEmailDialog
        language={language}
        email={checkoutEmail}
        busy={checkoutEmailBusy}
        sent={checkoutEmailSent}
        error={checkoutEmailError}
        onEmailChange={(value) => { setCheckoutEmail(value); setCheckoutEmailError(""); }}
        onClose={() => { if (!checkoutEmailBusy) setCheckoutEmailGateOpen(false); }}
        onSubmit={submitCheckoutEmail}
      /> : null}

      {purchaseOpen ? (
        <PaymentDialog
          language={language}
          config={paymentConfig}
          plan={selectedPlan}
          busy={dashboardBusy}
          onClose={() => { setPurchaseOpen(false); setPaymentError(""); }}
          onPay={createPayment}
          promoCode={promoCode}
          promoPreview={promoPreview}
          promoBusy={promoBusy}
          promoError={promoError}
          paymentError={paymentError}
          pendingPayment={pendingPayment}
          onCancelPending={cancelPendingPayment}
          onPromoCodeChange={updatePromoCode}
          onApplyPromo={applyPromoCode}
          onClearPromo={clearPromoCode}
          onContactSupport={openSupport}
        />
      ) : null}

      {authOpen && (!user || developerReauthentication) ? (
        <div className="auth-modal" role="presentation" onMouseDown={() => { setAuthOpen(false); setDeveloperReauthentication(false); }}>
          <div
            className="auth-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={copy.login}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button className="modal-close" type="button" aria-label={copy.close} onClick={() => { setAuthOpen(false); setDeveloperReauthentication(false); }}>
              <X size={20} />
            </button>
            <AuthPanel
              authMode={recoveryMode ? "reset" : authMode}
              setAuthMode={(mode) => { if (!recoveryMode) setAuthMode(mode); }}
              form={form}
              updateField={updateField}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              submitAuth={submitAuth}
              startProviderAuth={startProviderAuth}
              startSteamAuth={() => startSteamAuth("login")}
              user={user}
              authBusy={authBusy}
              authMessage={authMessage}
              authError={steamCallbackError ? steamCallbackErrorText(new Error(steamCallbackError), language) : authError}
              logout={logout}
              language={language}
              developerReauthentication={developerReauthentication}
              recoveryMode={recoveryMode}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ConfirmationDialog({
  request,
  onClose,
  language,
}: {
  request: ConfirmationRequest;
  onClose: (confirmed: boolean) => void;
  language: Lang;
}) {
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
  const Icon = request.icon === "logout" ? LogOut : Trash2;

  useEffect(() => {
    cancelButtonRef.current?.focus();
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") onClose(false);
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <div className="action-modal" role="presentation" onMouseDown={() => onClose(false)}>
      <div className="action-dialog is-danger" role="alertdialog" aria-modal="true" aria-labelledby="action-dialog-title" aria-describedby="action-dialog-message" onMouseDown={(event) => event.stopPropagation()}>
        <div className="action-dialog-heading">
          <span className="action-dialog-icon is-danger" aria-hidden="true"><Icon size={22} /></span>
          <div>
            <span className="action-dialog-kicker">{language === "en" ? "Confirmation" : "Подтверждение"}</span>
            <h2 id="action-dialog-title">{request.title}</h2>
          </div>
        </div>
        <p id="action-dialog-message">{request.message}</p>
        <div className="action-dialog-actions">
          <button ref={cancelButtonRef} className="action-dialog-button" type="button" onClick={() => onClose(false)}>{request.cancelLabel}</button>
          <button className="action-dialog-button is-danger" type="button" onClick={() => onClose(true)}>{request.confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function TwoFactorStepUpDialog({ language, onClose }: { language: Lang; onClose: (code: string | null) => void }) {
  const [code, setCode] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(null); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);
  return <div className="action-modal" role="presentation" onMouseDown={() => onClose(null)}><section className="moderator-ban-dialog two-factor-stepup-dialog" role="dialog" aria-modal="true" aria-labelledby="two-factor-stepup-title" onMouseDown={(event) => event.stopPropagation()}><h2 id="two-factor-stepup-title">{language === "en" ? "Confirm with 2FA" : "Подтвердите через 2FA"}</h2><p>{language === "en" ? "Enter the six-digit code from your authenticator app." : "Введите шестизначный код из приложения-аутентификатора."}</p><label className="moderator-custom-field"><span>{language === "en" ? "Authenticator code" : "Код из приложения"}</span><input ref={inputRef} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" /></label><div className="action-dialog-actions"><button className="action-dialog-button" type="button" onClick={() => onClose(null)}>{language === "en" ? "Cancel" : "Отмена"}</button><button className="action-dialog-button is-primary" type="button" disabled={code.length !== 6} onClick={() => onClose(code)}>{language === "en" ? "Confirm" : "Подтвердить"}</button></div></section></div>;
}

function TwoFactorLoginDialog({
  challenge,
  language,
  busy,
  onClose,
  onConfirm,
  onStartSetup,
}: {
  challenge: TwoFactorLoginChallenge;
  language: Lang;
  busy: boolean;
  onClose: () => void;
  onConfirm: (code: string) => Promise<void>;
  onStartSetup: () => Promise<SiteTwoFactorSetup>;
}) {
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState<SiteTwoFactorSetup | null>(null);
  const [setupLoading, setSetupLoading] = useState(challenge.kind === "steam_setup");
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const isSetup = challenge.kind === "steam_setup";

  useEffect(() => {
    inputRef.current?.focus();
  }, [setupLoading]);

  useEffect(() => {
    if (!isSetup) return;
    let cancelled = false;
    void onStartSetup()
      .then((result) => { if (!cancelled) setSetup(result); })
      .catch((reason) => { if (!cancelled) setError(authErrorText(reason, language)); })
      .finally(() => { if (!cancelled) setSetupLoading(false); });
    return () => { cancelled = true; };
  }, [isSetup, language, onStartSetup]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [busy, onClose]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || setupLoading || code.length !== 6) return;
    setError("");
    try {
      await onConfirm(code);
    } catch (reason) {
      setError(authErrorText(reason, language));
    }
  }

  const title = isSetup
    ? language === "en" ? "Set up two-factor authentication" : "Подключите двухфакторную аутентификацию"
    : language === "en" ? "Confirm sign-in" : "Подтвердите вход";

  return <div className="action-modal" role="presentation" onMouseDown={() => !busy && onClose()}>
    <form className="action-dialog two-factor-login-dialog" role="dialog" aria-modal="true" aria-labelledby="two-factor-login-title" onSubmit={submit} onMouseDown={(event) => event.stopPropagation()}>
      <div className="action-dialog-heading"><span className="action-dialog-icon" aria-hidden="true">{isSetup ? <QrCode size={22} /> : <ShieldCheck size={22} />}</span><div><span className="action-dialog-kicker">{isSetup ? "Steam" : language === "en" ? "Two-factor authentication" : "Двухфакторная аутентификация"}</span><h2 id="two-factor-login-title">{title}</h2></div></div>
      {isSetup ? <p>{language === "en" ? "Scan the QR code with your authenticator app, then enter the six-digit code." : "Отсканируйте QR-код в приложении-аутентификаторе и введите шестизначный код."}</p> : <p>{language === "en" ? "Enter the six-digit code from your authenticator app to continue." : "Введите шестизначный код из приложения-аутентификатора, чтобы продолжить."}</p>}
      {isSetup && setupLoading ? <p className="two-factor-setup-loading" role="status">{language === "en" ? "Preparing QR code…" : "Подготавливаем QR-код…"}</p> : null}
      {isSetup && setup ? <div className="two-factor-qr"><img src={setup.qrCodeDataUrl} alt={language === "en" ? "Authenticator setup QR code" : "QR-код для подключения приложения-аутентификатора"} /></div> : null}
      <label className="action-dialog-field"><span>{language === "en" ? "Authenticator code" : "Код из приложения"}</span><input ref={inputRef} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" disabled={busy || setupLoading || (isSetup && !setup)} /></label>
      {error ? <p className="field-error" role="alert">{error}</p> : null}
      <div className="action-dialog-actions"><button className="action-dialog-button" type="button" disabled={busy} onClick={onClose}>{language === "en" ? "Cancel" : "Отмена"}</button><button className="action-dialog-button is-primary" type="submit" disabled={busy || setupLoading || (isSetup && !setup) || code.length !== 6}>{busy ? (language === "en" ? "Checking…" : "Проверяем…") : (language === "en" ? "Continue" : "Продолжить")}</button></div>
    </form>
  </div>;
}

function TwoFactorSetupDialog({ setup, language, busy, onClose, onConfirm }: {
  setup: SiteTwoFactorSetup;
  language: Lang;
  busy: boolean;
  onClose: () => void;
  onConfirm: (code: string) => Promise<void>;
}) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [busy, onClose]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || code.length !== 6) return;
    setError("");
    try {
      await onConfirm(code);
    } catch (reason) {
      setError(twoFactorErrorText(reason, language));
    }
  }

  return <div className="action-modal" role="presentation" onMouseDown={() => !busy && onClose()}>
    <form className="action-dialog two-factor-login-dialog" role="dialog" aria-modal="true" aria-labelledby="two-factor-setup-title" onSubmit={submit} onMouseDown={(event) => event.stopPropagation()}>
      <div className="action-dialog-heading"><span className="action-dialog-icon" aria-hidden="true"><QrCode size={22} /></span><div><span className="action-dialog-kicker">{language === "en" ? "Account security" : "Защита аккаунта"}</span><h2 id="two-factor-setup-title">{language === "en" ? "Set up two-factor authentication" : "Подключите двухфакторную аутентификацию"}</h2></div></div>
      <p>{language === "en" ? "Scan the QR code with your authenticator app, then enter the six-digit code." : "Отсканируйте QR-код в приложении-аутентификаторе и введите шестизначный код."}</p>
      <div className="two-factor-qr"><img src={setup.qrCodeDataUrl} alt={language === "en" ? "Authenticator setup QR code" : "QR-код для подключения приложения-аутентификатора"} /></div>
      <label className="action-dialog-field"><span>{language === "en" ? "Authenticator code" : "Код из приложения"}</span><input ref={inputRef} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" disabled={busy} /></label>
      {error ? <p className="field-error" role="alert">{error}</p> : null}
      <div className="action-dialog-actions"><button className="action-dialog-button" type="button" disabled={busy} onClick={onClose}>{language === "en" ? "Cancel" : "Отмена"}</button><button className="action-dialog-button is-primary" type="submit" disabled={busy || code.length !== 6}>{busy ? (language === "en" ? "Enabling…" : "Подключаем…") : (language === "en" ? "Enable 2FA" : "Подключить 2FA")}</button></div>
    </form>
  </div>;
}

function PasswordConfirmationDialog({ language, onClose, onConfirm }: { language: Lang; onClose: () => void; onConfirm: (password: string, twoFactorCode: string) => Promise<void> }) {
  const [password, setPassword] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    passwordRef.current?.focus();
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && !busy) onClose();
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [busy, onClose]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError("");
    try {
      await onConfirm(password, twoFactorCode);
    } catch (submitError) {
      setError(authErrorText(submitError, language));
    } finally {
      setBusy(false);
    }
  }

  return <div className="action-modal" role="presentation" onMouseDown={() => !busy && onClose()}>
    <form className="action-dialog password-confirmation-dialog" role="dialog" aria-modal="true" aria-labelledby="password-confirmation-title" onSubmit={submit} onMouseDown={(event) => event.stopPropagation()}>
      <div className="action-dialog-heading"><span className="action-dialog-icon" aria-hidden="true"><LockKeyhole size={22} /></span><div><span className="action-dialog-kicker">{language === "en" ? "Security confirmation" : "Подтверждение безопасности"}</span><h2 id="password-confirmation-title">{language === "en" ? "Confirm your password" : "Подтвердите пароль"}</h2></div></div>
      <p>{language === "en" ? "Enter your current password to continue. If 2FA is enabled, add its code too." : "Введите текущий пароль, чтобы продолжить. Если включена 2FA, укажите и код из приложения."}</p>
      <label className="action-dialog-field"><span>{language === "en" ? "Current password" : "Текущий пароль"}</span><input ref={passwordRef} value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="current-password" disabled={busy} required /></label>
      <label className="action-dialog-field"><span>{language === "en" ? "2FA code (if enabled)" : "Код 2FA (если включена)"}</span><input value={twoFactorCode} onChange={(event) => setTwoFactorCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" disabled={busy} /></label>
      {error ? <p className="field-error" role="alert">{error}</p> : null}
      <div className="action-dialog-actions"><button className="action-dialog-button" type="button" disabled={busy} onClick={onClose}>{language === "en" ? "Cancel" : "Отмена"}</button><button className="action-dialog-button is-primary" type="submit" disabled={busy || !password}>{busy ? (language === "en" ? "Confirming…" : "Подтверждаем…") : (language === "en" ? "Continue" : "Продолжить")}</button></div>
    </form>
  </div>;
}

function SubscriptionPlanCard({ language, plans, selectedPlan, busy, renewal, highlightCheckout, onSelect, onCheckout }: {
  language: Lang;
  plans: SiteSubscriptionPlan[];
  selectedPlan: SiteSubscriptionPlan;
  busy: boolean;
  renewal: boolean;
  highlightCheckout: boolean;
  onSelect: (planId: SubscriptionPlanId) => void;
  onCheckout: () => void;
}) {
  const ru = language === "ru";
  const price = (amount: number, currency = selectedPlan.currency || "RUB") => currency === "USD"
    ? `$${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(amount)}`
    : `${new Intl.NumberFormat(ru ? "ru-RU" : "en-US", { maximumFractionDigits: 2 }).format(amount)} ₽`;
  const monthly = `≈ ${price(selectedPlan.monthlyEquivalent)}`;
  const showPlanBreakdown = selectedPlan.id !== "monthly";
  const checkoutLabel = ru
    ? `${renewal ? "Перейти к продлению" : "Перейти к оплате"} подписки на ${selectedPlan.labels.ru} за ${price(selectedPlan.amount)}`
    : `${renewal ? "Proceed to renewal" : "Proceed to payment"} for ${selectedPlan.labels.en} at ${price(selectedPlan.amount)}`;
  return <article className="purchase-preview subscription-card">
    <div className="purchase-preview-head"><h2>{ru ? (renewal ? "Продление CheatChecker" : "Лицензия CheatChecker") : (renewal ? "Renew CheatChecker" : "CheatChecker license")}</h2></div>
    <div className="subscription-periods" role="radiogroup" aria-label={ru ? "Срок подписки" : "Subscription period"}>
      {plans.map((plan) => <button key={plan.id} type="button" role="radio" aria-checked={selectedPlan.id === plan.id} className={selectedPlan.id === plan.id ? "subscription-period is-active" : "subscription-period"} disabled={busy} onClick={() => onSelect(plan.id)}>
        {plan.recommended ? <span className="subscription-period-badge"><span className="subscription-period-badge-full">{ru ? "Выгоднее всего" : "Best value"}</span><span className="subscription-period-badge-short">{ru ? "Выгодно" : "Best"}</span></span> : null}
        <span className="subscription-period-label"><span className="subscription-period-label-full">{plan.labels[language]}</span><span className="subscription-period-label-short">{plan.months} {ru ? "мес." : "mo."}</span></span>
        {plan.id !== "monthly" ? <span className="subscription-period-discount">−{Math.round(plan.discountPercent)}%</span> : null}
      </button>)}
    </div>
    <div className="subscription-divider" />
    <div className="purchase-price subscription-price" aria-live="polite">
      {selectedPlan.listAmount && selectedPlan.listAmount > selectedPlan.amount ? <s>{price(selectedPlan.listAmount)}</s> : null}
      <strong>{price(selectedPlan.amount)}</strong>
      <div className="subscription-price-details" aria-hidden={!showPlanBreakdown}>
        {showPlanBreakdown ? <span>{monthly} {ru ? "в месяц" : "per month"}</span> : null}
        {showPlanBreakdown ? <small>{ru ? `Экономия ${price(selectedPlan.savings)}` : `Save ${price(selectedPlan.savings)}`}</small> : null}
      </div>
    </div>
    <div className="subscription-divider" />
    <ul className="purchase-benefits">
      <li><CheckCircle2 size={24} />{ru ? "Доступ ко всем функциям" : "Access to every feature"}</li>
      <li><RefreshCw size={24} />{ru ? "Постоянные обновления и исправления" : "Continuous updates and fixes"}</li>
      <li><UsersRound size={24} />{ru ? `Добавление до ${selectedPlan.maxModerators} модераторов проекта` : `Add up to ${selectedPlan.maxModerators} project moderators`}</li>
      <li><Infinity size={27} />{ru ? "Неограниченные проверки" : "Unlimited checks"}</li>
      <li><Headphones size={25} />{ru ? "Приоритетная и быстрая поддержка" : "Priority, fast support"}</li>
    </ul>
    <div className="purchase-preview-bottom"><button id="plans-checkout" className={`hero-buy purchase-preview-action${highlightCheckout ? " is-highlighted" : ""}`} type="button" aria-label={checkoutLabel} disabled={busy} onClick={onCheckout}>{busy ? (ru ? "Переходим к оплате…" : "Opening payment…") : (ru ? (renewal ? "Перейти к продлению" : "Перейти к оплате") : (renewal ? "Proceed to renewal" : "Proceed to payment"))}<ArrowRight size={23} /></button></div>
  </article>;
}

function PromoCodeField({ language, code, preview, busy, error, baseAmount, currency = "RUB", onChange, onApply, onClear }: {
  language: Lang;
  code: string;
  preview: SitePromoPreflight | null;
  busy: boolean;
  error: string;
  baseAmount: number;
  currency?: "RUB" | "USD";
  onChange: (value: string) => void;
  onApply: () => void;
  onClear: () => void;
}) {
  const amount = (value: number, valueCurrency = currency) => valueCurrency === "USD"
    ? `$${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value)}`
    : `${new Intl.NumberFormat(language === "en" ? "en-US" : "ru-RU", { maximumFractionDigits: 2 }).format(value)} ₽`;
  return <section className="promo-code-field" aria-label={language === "en" ? "Promo code" : "Промокод"}>
    <div className="promo-code-entry">
      <label><span>{language === "en" ? "Promo code" : "Промокод"}</span><input value={code} onChange={(event) => onChange(event.target.value)} disabled={busy} maxLength={32} autoCapitalize="characters" autoComplete="off" placeholder={language === "en" ? "Enter code" : "Введите код"} /></label>
      <button className="mini-action" type="button" disabled={busy || !code.trim()} onClick={onApply}>{busy ? (language === "en" ? "Checking…" : "Проверяем…") : (language === "en" ? "Apply" : "Применить")}</button>
    </div>
    {preview ? <div className="promo-code-result is-applied" role="status"><CheckCircle2 size={17} /><span>{language === "en" ? `Code ${preview.promo.code} applied` : `Промокод ${preview.promo.code} применён`}. {language === "en" ? "Total" : "К оплате"}: <strong>{amount(preview.amount, preview.currency)}</strong>{baseAmount > preview.amount ? <small>{language === "en" ? ` instead of ${amount(baseAmount)}` : ` вместо ${amount(baseAmount)}`}</small> : null}</span><button type="button" onClick={onClear}>{language === "en" ? "Remove" : "Убрать"}</button></div> : null}
    {error ? <p className="field-error" role="alert">{error}</p> : null}
  </section>;
}

function CheckoutEmailDialog({
  language,
  email,
  busy,
  sent,
  error,
  onEmailChange,
  onClose,
  onSubmit,
}: {
  language: Lang;
  email: string;
  busy: boolean;
  sent: boolean;
  error: string;
  onEmailChange: (value: string) => void;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    inputRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [busy, onClose]);

  return <div className="action-modal" role="presentation" onMouseDown={() => !busy && onClose()}>
    <form className="action-dialog checkout-email-dialog" role="dialog" aria-modal="true" aria-labelledby="checkout-email-title" onSubmit={onSubmit} onMouseDown={(event) => event.stopPropagation()}>
      <div className="action-dialog-heading"><span className="action-dialog-icon" aria-hidden="true"><Mail size={22} /></span><div><span className="action-dialog-kicker">CheatChecker</span><h2 id="checkout-email-title">{language === "en" ? "Confirm email before payment" : "Подтвердите почту перед оплатой"}</h2></div></div>
      <p>{language === "en" ? "Add the email for payment receipts and confirm it. After you open the link from the email, the payment window will open automatically." : "Укажите почту для чека и подтвердите её. После перехода по ссылке из письма окно оплаты откроется автоматически."}</p>
      <label className="action-dialog-field"><span>Email</span><input ref={inputRef} value={email} onChange={(event) => onEmailChange(event.target.value)} type="email" autoComplete="email" placeholder="you@example.com" disabled={busy} required /></label>
      {sent ? <p className="checkout-email-sent" role="status">{language === "en" ? "Confirmation email sent. You can return to the payment here after opening the link." : "Письмо с подтверждением отправлено. После перехода по ссылке вы вернётесь к оплате здесь."}</p> : null}
      {error ? <p className="field-error" role="alert">{error}</p> : null}
      <div className="action-dialog-actions"><button className="action-dialog-button" type="button" disabled={busy} onClick={onClose}>{language === "en" ? "Cancel" : "Отмена"}</button><button className="action-dialog-button is-primary" type="submit" disabled={busy || !email.trim()}>{busy ? (language === "en" ? "Sending…" : "Отправляем…") : sent ? (language === "en" ? "Send again" : "Отправить ещё раз") : (language === "en" ? "Send confirmation" : "Отправить подтверждение")}</button></div>
    </form>
  </div>;
}

function PaymentDialog({
  language,
  config,
  plan,
  busy,
  onClose,
  onPay,
  promoCode,
  promoPreview,
  promoBusy,
  promoError,
  paymentError,
  pendingPayment,
  onCancelPending,
  onPromoCodeChange,
  onApplyPromo,
  onClearPromo,
  onContactSupport,
}: {
  language: Lang;
  config: SitePaymentConfig | null;
  plan: SiteSubscriptionPlan;
  busy: boolean;
  onClose: () => void;
  onPay: (method: "sbp" | "bank_card") => void;
  promoCode: string;
  promoPreview: SitePromoPreflight | null;
  promoBusy: boolean;
  promoError: string;
  paymentError: string;
  pendingPayment: SitePaymentOrder | null;
  onCancelPending: () => void;
  onPromoCodeChange: (value: string) => void;
  onApplyPromo: () => Promise<void>;
  onClearPromo: () => void;
  onContactSupport: () => void;
}) {
  const enabled = Boolean(config?.enabled);
  const renewal = config?.subscription?.mode === "renewal";
  const hasPendingPayment = pendingPayment?.status === "pending";
  const title = language === "en" ? (renewal ? "Renew subscription" : "Payment") : (renewal ? "Продлить подписку" : "Оплата");
  const settlementAmount = promoPreview?.settlementAmount ?? plan.settlementAmount;
  const settlementNotice = language === "en" && config?.requiresRubleSettlement && settlementAmount != null
    ? `Robokassa will charge ${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(settlementAmount)} ₽.`
    : null;
  return <div className="purchase-modal" role="presentation" onMouseDown={onClose}>
    <section className="purchase-dialog" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(event) => event.stopPropagation()}>
      <div className="purchase-dialog-heading">
        <span><ShieldCheck size={28} /></span>
        <div><h2>{title}</h2><p>{language === "en" ? (renewal ? "Choose a method to add the selected term to your subscription" : "Choose a convenient payment method") : (renewal ? "Выберите способ оплаты — выбранный срок добавится к текущей подписке" : "Выберите удобный способ оплаты")}</p></div>
        <button type="button" className="purchase-dialog-close" aria-label={language === "en" ? "Close" : "Закрыть"} onClick={onClose}><X size={25} /></button>
      </div>
      <div className="purchase-methods">
        {hasPendingPayment ? <div className="pending-payment-action" role="status"><div><strong>{language === "en" ? "An unfinished payment is already open" : "У вас уже есть незавершённая оплата"}</strong><p>{language === "en" ? "Cancel it to choose a different plan, promo code or payment method." : "Отмените её, чтобы выбрать другой тариф, промокод или способ оплаты."}</p></div><button className="command-button is-muted" type="button" disabled={busy} onClick={onCancelPending}>{busy ? (language === "en" ? "Cancelling…" : "Отменяем…") : (language === "en" ? "Cancel payment" : "Отменить оплату")}</button></div> : null}
        {settlementNotice ? <p className="payment-settlement-notice" role="status">{settlementNotice} <span>Your USD price is a fixed display price.</span></p> : null}
        <button className="purchase-method is-sbp" type="button" disabled={busy || !enabled || hasPendingPayment} onClick={() => onPay("sbp")}><span className="purchase-method-icon" aria-hidden="true"><img src="/payment-methods/sbp.png" alt="" /></span><span><strong>{language === "en" ? "SBP" : "СБП"}</strong><small>{language === "en" ? "Pay through your bank's mobile app" : "Оплата через мобильное приложение вашего банка"}</small><em><Zap size={16} />{language === "en" ? "Instant" : "Мгновенно"}</em></span><ChevronRight size={29} /></button>
        <button className="purchase-method" type="button" disabled={busy || !enabled || hasPendingPayment} onClick={() => onPay("bank_card")}><span className="purchase-method-icon" aria-hidden="true"><img src="/payment-methods/bank-card.png" alt="" /></span><span><strong>{language === "en" ? "Bank cards" : "Банковские карты"}</strong><small>{language === "en" ? "Visa, MasterCard, Mir and other Russian cards" : "Visa, MasterCard, Мир и другие карты РФ"}</small></span><ChevronRight size={29} /></button>
        <PromoCodeField
          language={language}
          code={promoCode}
          preview={promoPreview}
          busy={busy || promoBusy || hasPendingPayment}
          error={promoError}
          baseAmount={plan.amount}
          currency={plan.currency || config?.currency || "RUB"}
          onChange={onPromoCodeChange}
          onApply={() => void onApplyPromo()}
          onClear={onClearPromo}
        />
        {paymentError ? <p className="field-error payment-dialog-error" role="alert">{paymentError}</p> : null}
      </div>
      <div className="purchase-dialog-footer"><p><LockKeyhole size={18} />{language === "en" ? "Your payment data is protected and never reaches us." : "Ваши платёжные данные защищены и не передаются нам."}</p><p>{language === "en" ? "Need help?" : "Нужна помощь?"} <a href="#contacts" onClick={(event) => { event.preventDefault(); onContactSupport(); }}>{language === "en" ? "Contact support" : "Связаться с поддержкой"}</a></p></div>
    </section>
  </div>;
}

function RenameModeratorDialog({ request, onClose }: { request: RenameRequest; onClose: (value: string | null) => void }) {
  const [value, setValue] = useState(request.initialValue);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const trimmedValue = value.trim();

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") onClose(null);
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  function submitRename(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (trimmedValue) onClose(trimmedValue);
  }

  return (
    <div className="action-modal" role="presentation" onMouseDown={() => onClose(null)}>
      <form className="action-dialog" role="dialog" aria-modal="true" aria-labelledby="rename-dialog-title" aria-describedby="rename-dialog-message" onSubmit={submitRename} onMouseDown={(event) => event.stopPropagation()}>
        <div className="action-dialog-heading">
          <span className="action-dialog-icon" aria-hidden="true"><Pencil size={21} /></span>
          <div>
            <span className="action-dialog-kicker">CheatChecker</span>
            <h2 id="rename-dialog-title">{request.title}</h2>
          </div>
        </div>
        <p id="rename-dialog-message">{request.message}</p>
        <label className="action-dialog-field">
          <span>{request.inputLabel}</span>
          <input ref={inputRef} value={value} maxLength={MAX_MODERATOR_NAME_LENGTH} onChange={(event) => setValue(limitModeratorName(event.target.value))} />
        </label>
        <div className="action-dialog-actions">
          <button className="action-dialog-button" type="button" onClick={() => onClose(null)}>{request.cancelLabel}</button>
          <button className="action-dialog-button is-primary" type="submit" disabled={!trimmedValue}>{request.confirmLabel}</button>
        </div>
      </form>
    </div>
  );
}

function AccountQuietShell() {
  return (
    <div className="account-workspace account-quiet-shell" id="account" aria-busy="true">
      <div className="quiet-line is-wide" />
      <div className="quiet-line" />
      <div className="dashboard-skeleton-grid" aria-hidden="true">
        <div />
        <div />
        <div />
      </div>
    </div>
  );
}

function ProjectDeletedShell({ language, onHome }: { language: Lang; onHome: () => void }) {
  const ru = language === "ru";
  return (
    <div className="project-deleted-shell" role="status">
      <div className="project-deleted-icon" aria-hidden="true"><CheckCircle2 size={30} /></div>
      <p>CheatChecker</p>
      <h1>{ru ? "Проект удалён" : "Project deleted"}</h1>
      <span>{ru ? "Мы завершили сеанс на этом устройстве. Создать новый проект можно после следующей покупки." : "Your session on this device has ended. You can create a new project after your next purchase."}</span>
      <button className="command-button" type="button" onClick={onHome}>{ru ? "На главную" : "Back to home"}<ArrowRight size={17} /></button>
    </div>
  );
}

function DeveloperReauthenticationGate({ language, message, onLogin }: { language: Lang; message: string; onLogin: () => void }) {
  const ru = language === "ru";
  return (
    <section className="developer-verification-gate" aria-labelledby="developer-reauthentication-title">
      <div className="developer-verification-card">
        <div className="developer-verification-icon" aria-hidden="true"><KeyRound size={30} /></div>
        <p>CheatChecker</p>
        <h1 id="developer-reauthentication-title">{ru ? "Войдите заново" : "Sign in again"}</h1>
        <span>{message || (ru ? "Сессия устарела. Войдите в аккаунт заново." : "Your session has expired. Sign in again.")}</span>
        <div className="developer-verification-actions"><button className="command-button" type="button" onClick={onLogin}>{ru ? "Войти" : "Sign in"}</button></div>
      </div>
    </section>
  );
}

function DeveloperVerificationGate({
  stage,
  busy,
  error,
  message,
  language,
  onRequest,
  onVerify,
}: {
  stage: "request" | "code";
  busy: boolean;
  error: string;
  message: string;
  language: Lang;
  onRequest: () => void;
  onVerify: (code: string) => void;
}) {
  const [code, setCode] = useState("");
  const ru = language === "ru";
  const awaitingCode = stage === "code";

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (awaitingCode) {
      onVerify(code);
      return;
    }
    setCode("");
    onRequest();
  }

  function requestAnotherCode() {
    setCode("");
    onRequest();
  }

  return (
    <section className="developer-verification-gate" aria-labelledby="developer-verification-title">
      <form className="developer-verification-card" onSubmit={submit}>
        <div className="developer-verification-icon" aria-hidden="true"><ShieldCheck size={30} /></div>
        <p>CheatChecker</p>
        <h1 id="developer-verification-title">{ru ? "Подтвердите вход" : "Confirm your sign-in"}</h1>
        <span>{awaitingCode
          ? (ru ? "Введите код из письма, чтобы открыть разраб-кабинет." : "Enter the code from the email to open the developer dashboard.")
          : (ru ? "Для доступа к разраб-кабинету подтвердите вход кодом из письма." : "Confirm your sign-in with an email code to access the developer dashboard.")}</span>

        {awaitingCode ? <label className="developer-verification-code"><span>{ru ? "Код из письма" : "Email code"}</span><input value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" disabled={busy} /></label> : null}
        {message ? <p className="auth-note is-success">{message}</p> : null}
        {error ? <p className="auth-note is-error">{error}</p> : null}

        <div className="developer-verification-actions">
          <button className="command-button" type="submit" disabled={busy || (awaitingCode && code.length !== 6)}>{awaitingCode ? (ru ? "Подтвердить" : "Confirm") : (ru ? "Отправить код" : "Send code")}</button>
          {awaitingCode ? <button className="command-button is-muted" type="button" disabled={busy} onClick={requestAnotherCode}>{ru ? "Отправить ещё раз" : "Send again"}</button> : null}
        </div>
      </form>
    </section>
  );
}

function DeveloperDashboardPanel({
  data,
  busy,
  error,
  language,
  promoCodes,
  onRefresh,
  onCreatePromo,
  onUpdatePromo,
  onLogout,
}: {
  data: SiteDeveloperDashboard | null;
  busy: boolean;
  error: string;
  language: Lang;
  promoCodes: SiteDeveloperPromoCode[];
  onRefresh: () => Promise<void>;
  onCreatePromo: (payload: Parameters<typeof siteDeveloperCreatePromoCode>[1]) => Promise<boolean>;
  onUpdatePromo: (code: string, payload: Parameters<typeof siteDeveloperUpdatePromoCode>[2]) => Promise<boolean>;
  onLogout: () => Promise<void>;
}) {
  const ru = language === "ru";
  const dashboard = data?.data;
  const projects = dashboard?.projects || [];
  const value = (text: string) => text || "—";
  const unknownAmount = ru ? "Неизвестно" : "Unknown";
  const price = (amount: number | null | undefined, currency?: string | null) => amount == null
    ? unknownAmount
    : new Intl.NumberFormat(ru ? "ru-RU" : "en-US").format(amount) + (currency ? ` ${currency}` : "");
  const date = (text?: string | null) => {
    if (!text) return "—";
    const msk = /^(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{2}):(\d{2})(?::\d{2})?)?$/.exec(text);
    if (msk) return `${msk[1]}.${msk[2]}.${msk[3]}${msk[4] ? ` · ${msk[4]}:${msk[5]}` : ""}`;
    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) return text;
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Moscow", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(parsed).reduce<Record<string, string>>((result, part) => ({ ...result, [part.type]: part.value }), {});
    return `${parts.day}.${parts.month}.${parts.year} · ${parts.hour}:${parts.minute}`;
  };
  const status = (text: string) => {
    const labels: Record<string, [string, string]> = {
      active: ["Активен", "Active"],
      expired: ["Истёк", "Expired"],
      none: ["Нет лицензии", "No license"],
      paid: ["Оплачено", "Paid"],
      pending: ["Ожидает оплаты", "Pending"],
      failed: ["Не оплачено", "Failed"],
    };
    return labels[text]?.[ru ? 0 : 1] || value(text);
  };
  const provider = (name: string) => ({
    email: ru ? "Email и пароль" : "Email and password",
    google: "Google",
    yandex: ru ? "Яндекс" : "Yandex",
    steam: "Steam",
  }[name] || value(name));

  return (
    <section className="developer-dashboard" aria-label={ru ? "Разраб-кабинет" : "Developer dashboard"}>
      <header className="developer-dashboard-header">
        <div>
          <p>CheatChecker</p>
          <h1>{ru ? "Разраб-кабинет" : "Developer dashboard"}</h1>
          <span>{ru ? "Закрытый обзор работы сервиса" : "Private service overview"}</span>
        </div>
        <div className="command-actions">
          <button className="command-button is-muted" type="button" disabled={busy} onClick={() => void onRefresh()}><RefreshCw size={17} />{ru ? "Обновить" : "Refresh"}</button>
          <button className="command-button is-danger" type="button" onClick={() => void onLogout()}><LogOut size={17} />{ru ? "Выйти" : "Sign out"}</button>
        </div>
      </header>

      {error ? <p className="auth-note is-error">{error}</p> : null}
      {!dashboard && busy ? <div className="dashboard-skeleton-grid" aria-hidden="true"><div /><div /><div /></div> : null}

      {dashboard ? <>
        <div className="developer-summary">
          <span><CreditCard size={18} /><strong>{dashboard.purchases.length}</strong>{ru ? "покупок" : "purchases"}</span>
          <span><UsersRound size={18} /><strong>{dashboard.users.length}</strong>{ru ? "пользователей" : "users"}</span>
          <span><LogOut size={18} /><strong>{dashboard.logins.length}</strong>{ru ? "входов" : "sign-ins"}</span>
          <span><LayoutDashboard size={18} /><strong>{projects.length}</strong>{ru ? "проектов" : "projects"}</span>
        </div>

        <div className="developer-sections">
          <DeveloperPromoCodesSection language={language} promoCodes={promoCodes} busy={busy} onCreate={onCreatePromo} onUpdate={onUpdatePromo} />
          <DeveloperDataSection title={ru ? "Покупки" : "Purchases"}>
            <table><thead><tr><th>{ru ? "Пользователь" : "User"}</th><th>{ru ? "Сумма" : "Amount"}</th><th>{ru ? "Статус" : "Status"}</th><th>{ru ? "Дата" : "Created"}</th></tr></thead><tbody>{dashboard.purchases.length ? dashboard.purchases.map((row) => <tr key={row.id}><td>{value(row.ownerEmail)}</td><td>{price(row.amount, row.currency)}</td><td><span className="developer-status">{status(row.status)}</span></td><td>{date(row.paidAt || row.createdAt)}</td></tr>) : <DeveloperEmptyRow columns={4} />}</tbody></table>
          </DeveloperDataSection>

          <DeveloperDataSection title={ru ? "Пользователи" : "Users"}>
            <table><thead><tr><th>Email</th><th>{ru ? "Доступ" : "Access"}</th><th>{ru ? "Подтверждение" : "Verified"}</th><th>{ru ? "Последний вход" : "Last sign-in"}</th></tr></thead><tbody>{dashboard.users.length ? dashboard.users.map((row) => <tr key={row.id}><td>{value(row.email)}</td><td><span className="developer-status">{status(row.accessStatus)}</span></td><td>{row.emailVerified ? (ru ? "Подтверждён" : "Verified") : (ru ? "Не подтверждён" : "Not verified")}</td><td>{date(row.lastLoginAt)}</td></tr>) : <DeveloperEmptyRow columns={4} />}</tbody></table>
          </DeveloperDataSection>

          <DeveloperDataSection title={ru ? "Входы" : "Sign-ins"}>
            <table><thead><tr><th>Email</th><th>{ru ? "Способ входа" : "Provider"}</th><th>{ru ? "Дата" : "Date"}</th></tr></thead><tbody>{dashboard.logins.length ? dashboard.logins.map((row) => <tr key={row.id}><td>{value(row.email)}</td><td>{provider(row.provider)}</td><td>{date(row.createdAt)}</td></tr>) : <DeveloperEmptyRow columns={3} />}</tbody></table>
          </DeveloperDataSection>

          <DeveloperDataSection title={ru ? "Проекты" : "Projects"}>
            <table><thead><tr><th>{ru ? "Название" : "Name"}</th><th>{ru ? "Срок действия" : "Valid until"}</th><th>{ru ? "Покупка" : "Purchase"}</th></tr></thead><tbody>{projects.length ? projects.map((row) => <tr key={`${row.name}-${row.accessExpiresAt}-${row.purchasedAt}`}><td>{value(row.name)}</td><td><span className="developer-status">{status(row.accessStatus)}</span><small className="developer-date">{date(row.accessExpiresAt)}</small></td><td>{price(row.purchaseAmount, row.purchaseCurrency || "")}<small className="developer-date">{[value(row.purchaseProvider || ""), date(row.purchasedAt)].filter((item) => item !== "—").join(" · ") || "—"}</small></td></tr>) : <DeveloperEmptyRow columns={3} />}</tbody></table>
          </DeveloperDataSection>
        </div>
      </> : null}
    </section>
  );
}

function DeveloperDataSection({ title, children }: { title: string; children: ReactNode }) {
  return <article className="developer-data-section"><h2>{title}</h2><div className="developer-table-scroll">{children}</div></article>;
}

function DeveloperPromoCodesSection({ language, promoCodes, busy, onCreate, onUpdate }: {
  language: Lang;
  promoCodes: SiteDeveloperPromoCode[];
  busy: boolean;
  onCreate: (payload: Parameters<typeof siteDeveloperCreatePromoCode>[1]) => Promise<boolean>;
  onUpdate: (code: string, payload: Parameters<typeof siteDeveloperUpdatePromoCode>[2]) => Promise<boolean>;
}) {
  const ru = language === "ru";
  const [code, setCode] = useState("");
  const [discountType, setDiscountType] = useState<"percent" | "fixed">("percent");
  const [discountValue, setDiscountValue] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [redemptionLimit, setRedemptionLimit] = useState("");
  const [notes, setNotes] = useState("");
  const [active, setActive] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const title = ru ? "Промокоды" : "Promo codes";
  const formatDate = (value?: string | null) => value ? new Intl.DateTimeFormat(ru ? "ru-RU" : "en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Moscow" }).format(new Date(value)) : (ru ? "Без срока" : "No expiry");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const value = Number(discountValue);
    const limit = redemptionLimit ? Number.parseInt(redemptionLimit, 10) : undefined;
    if (!code.trim() || !Number.isFinite(value) || value <= 0 || (redemptionLimit && (!Number.isInteger(limit) || limit! < 1))) {
      setError(ru ? "Проверьте код, размер скидки и лимит активаций." : "Check the code, discount value and redemption limit.");
      return;
    }
    setError("");
    setMessage("");
    const ok = await onCreate({
      code: code.trim().toUpperCase(),
      discountType,
      discountValue: value,
      ...(validFrom ? { validFrom: new Date(validFrom).toISOString() } : {}),
      ...(validTo ? { validTo: new Date(validTo).toISOString() } : {}),
      ...(limit ? { redemptionLimit: limit } : {}),
      active,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    });
    if (!ok) return;
    setCode("");
    setDiscountValue("");
    setValidFrom("");
    setValidTo("");
    setRedemptionLimit("");
    setNotes("");
    setActive(true);
    setMessage(ru ? "Промокод создан." : "Promo code created.");
  }

  return <article className="developer-promo-section">
    <header><div><h2>{title}</h2><p>{ru ? "Скидка и лимит применяются сервером при создании заказа." : "The server applies the discount and limit when it creates the order."}</p></div></header>
    <form className="developer-promo-form" onSubmit={submit}>
      <label className="developer-promo-code"><span>{ru ? "Код" : "Code"}</span><input value={code} onChange={(event) => { setCode(event.target.value.toUpperCase().slice(0, 32)); setError(""); }} disabled={busy} maxLength={32} placeholder="WELCOME10" autoCapitalize="characters" /></label>
      <label className="developer-promo-type"><span>{ru ? "Тип" : "Type"}</span><select value={discountType} disabled={busy} onChange={(event) => setDiscountType(event.target.value as "percent" | "fixed")}><option value="percent">{ru ? "Проценты" : "Percent"}</option><option value="fixed">{ru ? "Рубли" : "RUB"}</option></select></label>
      <label className="developer-promo-value"><span>{ru ? "Скидка" : "Discount"}</span><input value={discountValue} onChange={(event) => setDiscountValue(event.target.value)} disabled={busy} type="number" inputMode="decimal" min="0.01" step="0.01" placeholder={discountType === "percent" ? "10" : "500"} /></label>
      <label className="developer-promo-limit"><span>{ru ? "Лимит" : "Limit"}</span><input value={redemptionLimit} onChange={(event) => setRedemptionLimit(event.target.value.replace(/\D/g, "").slice(0, 8))} disabled={busy} inputMode="numeric" placeholder={ru ? "Без лимита" : "Unlimited"} /></label>
      <label className="developer-promo-from"><span>{ru ? "С" : "From"}</span><input value={validFrom} onChange={(event) => setValidFrom(event.target.value)} disabled={busy} type="datetime-local" /></label>
      <label className="developer-promo-until"><span>{ru ? "До" : "Until"}</span><input value={validTo} onChange={(event) => setValidTo(event.target.value)} disabled={busy} type="datetime-local" /></label>
      <label className="developer-promo-notes"><span>{ru ? "Заметка" : "Note"}</span><input value={notes} onChange={(event) => setNotes(event.target.value.slice(0, 160))} disabled={busy} maxLength={160} placeholder={ru ? "Внутренняя заметка" : "Internal note"} /></label>
      <label className="developer-promo-active"><input checked={active} onChange={(event) => setActive(event.target.checked)} disabled={busy} type="checkbox" /><span>{ru ? "Активен" : "Active"}</span></label>
      <button className="command-button" type="submit" disabled={busy}>{ru ? "Добавить промокод" : "Add promo code"}</button>
    </form>
    {error ? <p className="auth-note is-error" role="alert">{error}</p> : null}
    {message ? <p className="auth-note is-success" role="status">{message}</p> : null}
    <div className="developer-table-scroll"><table className="developer-promo-table"><thead><tr><th>{ru ? "Код" : "Code"}</th><th>{ru ? "Скидка" : "Discount"}</th><th>{ru ? "Период" : "Period"}</th><th>{ru ? "Использования" : "Uses"}</th><th>{ru ? "Статус" : "Status"}</th><th>{ru ? "Заметка" : "Note"}</th></tr></thead><tbody>{promoCodes.length ? promoCodes.map((promo) => { const note = noteDrafts[promo.code] ?? promo.notes ?? ""; return <tr key={promo.code}><td><strong>{promo.code}</strong></td><td>{promo.discountType === "percent" ? `${promo.discountValue}%` : `${promo.discountValue} ₽`}</td><td><small>{formatDate(promo.validFrom)}<br />{formatDate(promo.validTo)}</small></td><td>{promo.redemptions}{promo.redemptionLimit ? ` / ${promo.redemptionLimit}` : ""}</td><td><label className="developer-promo-toggle"><input checked={promo.active} disabled={busy} onChange={(event) => void onUpdate(promo.code, { active: event.target.checked })} type="checkbox" /><span>{promo.active ? (ru ? "Активен" : "Active") : (ru ? "Выключен" : "Disabled")}</span></label></td><td><div className="developer-promo-note"><input value={note} disabled={busy} onChange={(event) => setNoteDrafts((current) => ({ ...current, [promo.code]: event.target.value.slice(0, 160) }))} maxLength={160} /><button className="mini-action" type="button" disabled={busy || note === (promo.notes || "")} onClick={() => void onUpdate(promo.code, { notes: note })}>{ru ? "Сохранить" : "Save"}</button></div></td></tr>; }) : <DeveloperEmptyRow columns={6} />}</tbody></table></div>
  </article>;
}

function DeveloperEmptyRow({ columns }: { columns: number }) {
  return <tr><td colSpan={columns} className="developer-empty">—</td></tr>;
}

type ModeratorTab = "checks" | "stats" | "history";
type ModeratorBanDuration = "two_weeks" | "one_month" | "three_months" | "six_months" | "one_year" | "forever" | "custom";

function LegacyModeratorCommandCenter({
  currentUser,
  dashboard,
  busy,
  error,
  createCheck,
  finishCheck,
  cancelCheck,
  refreshDashboard,
  logout,
  language,
}: {
  currentUser: SiteUser | null;
  dashboard: SiteModeratorDashboard | null;
  busy: boolean;
  error: string;
  createCheck: (payload: { suspectSteamId64: string; suspectName: string }) => Promise<void>;
  finishCheck: (payload: Parameters<typeof siteModeratorFinishCheck>[1]) => Promise<void>;
  cancelCheck: (sessionId: string) => Promise<void>;
  refreshDashboard: () => Promise<void>;
  logout: () => Promise<void>;
  language: Lang;
}) {
  const [activeTab, setActiveTab] = useState<ModeratorTab>("checks");
  const [suspectSteamId64, setSuspectSteamId64] = useState("");
  const [suspectName, setSuspectName] = useState("");
  const [banReasons, setBanReasons] = useState<Record<string, string>>({});
  const [banDurations, setBanDurations] = useState<Record<string, "two_weeks" | "one_month" | "three_months" | "six_months" | "one_year" | "forever">>({});
  const steamError = steamId64Error(suspectSteamId64, language);
  const rows = dashboard ? getCheckRows(dashboard, language) : [];
  const activeRows = rows.filter((row) => row.status !== "completed" && row.status !== "cancelled");
  const completedRows = rows.filter((row) => row.status === "completed");
  const navItems: Array<{ id: ModeratorTab; label: string; icon: typeof ShieldCheck }> = [
    { id: "checks", label: language === "en" ? "Checks" : "Проверки", icon: ShieldCheck },
    { id: "stats", label: language === "en" ? "My stats" : "Моя статистика", icon: BarChart3 },
    { id: "history", label: language === "en" ? "History" : "История", icon: ClipboardList },
  ];

  async function submitCheck(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (steamError || !suspectSteamId64) {
      return;
    }
    await createCheck({ suspectSteamId64, suspectName: suspectName.trim() });
    setSuspectSteamId64("");
    setSuspectName("");
  }

  return (
    <section className="command-center moderator-center" id="account">
      <aside className="command-sidebar" aria-label={language === "en" ? "Moderator sections" : "Разделы модератора"}>
        <a className="command-brand" href="/">
          <img src="/cc_logo.png" alt="" />
          <span><strong>CheatChecker</strong></span>
        </a>
        <nav className="command-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            return <button key={item.id} className={activeTab === item.id ? "is-active" : ""} type="button" onClick={() => setActiveTab(item.id)}><Icon size={18} /><span>{item.label}</span></button>;
          })}
        </nav>
      </aside>

      <div className="command-main">
        <header className="command-header">
          <div>
            <p>{currentUser?.name || dashboard?.moderator.displayName || currentUser?.email || "CheatChecker"}</p>
            <h1>{language === "en" ? "Moderator panel" : "Панель модератора"}</h1>
          </div>
          <div className="command-actions">
            <button className="command-button is-muted" type="button" disabled={busy} onClick={refreshDashboard}>{language === "en" ? "Refresh" : "Обновить"}</button>
            <button className="command-button is-danger" type="button" onClick={logout}>{language === "en" ? "Sign out" : "Выйти"}</button>
          </div>
        </header>

        {error ? <p className="auth-note is-error">{error}</p> : null}
        {!dashboard ? <div className="dashboard-skeleton-grid" aria-hidden="true"><div /><div /><div /></div> : null}

        {dashboard && activeTab === "checks" ? (
          <div className="command-section">
            <div className="command-section-title"><div><h2>{language === "en" ? "Start a check" : "Вызвать на проверку"}</h2></div></div>
            <div className="command-grid is-main moderator-checks-grid">
              <article className="command-card">
                <form className="command-form" onSubmit={submitCheck}>
                  <label><span>SteamID64</span><input value={suspectSteamId64} onChange={(event) => setSuspectSteamId64(normalizeSteamId64(event.target.value))} placeholder="7656..." inputMode="numeric" maxLength={17} disabled={busy} aria-invalid={Boolean(steamError)} /></label>
                  {steamError ? <p className="field-error">{steamError}</p> : null}
                  <label><span>{language === "en" ? "Player name" : "Имя игрока"}</span><input value={suspectName} onChange={(event) => setSuspectName(event.target.value)} maxLength={64} disabled={busy} /></label>
                  <button className="command-button" type="submit" disabled={busy || !suspectSteamId64 || Boolean(steamError)}><ShieldCheck size={18} />{language === "en" ? "Send check request" : "Отправить вызов"}</button>
                </form>
              </article>
              <article className="command-card moderator-active-card">
                <div className="command-card-head"><div><h3>{language === "en" ? "Active checks" : "Активные проверки"}</h3></div><span className="command-pill">{activeRows.length}</span></div>
                {activeRows.length ? <div className="moderator-session-list">{activeRows.map((row) => {
                  const reason = banReasons[row.id] || "";
                  const duration = banDurations[row.id] || "forever";
                  return <article className="moderator-session" key={row.id}>
                    <div><strong>{row.target}</strong><span>{row.steamId}</span></div>
                    <div className="moderator-session-actions">
                      <button className="mini-action" type="button" disabled={busy} onClick={() => finishCheck({ sessionId: row.id, verdict: "clean" })}>{language === "en" ? "Clean" : "Чист"}</button>
                      <select value={duration} disabled={busy} onChange={(event) => setBanDurations((state) => ({ ...state, [row.id]: event.target.value as typeof duration }))}><option value="forever">{language === "en" ? "Forever" : "Навсегда"}</option><option value="one_month">{language === "en" ? "1 month" : "1 месяц"}</option><option value="three_months">{language === "en" ? "3 months" : "3 месяца"}</option></select>
                      <input value={reason} disabled={busy} onChange={(event) => setBanReasons((state) => ({ ...state, [row.id]: event.target.value }))} placeholder={language === "en" ? "Ban reason" : "Причина бана"} />
                      <button className="mini-action is-danger" type="button" disabled={busy || !reason.trim()} onClick={() => finishCheck({ sessionId: row.id, verdict: "ban", reason: reason.trim(), banDuration: duration })}>{language === "en" ? "Ban" : "Бан"}</button>
                      <button className="mini-action" type="button" disabled={busy} onClick={() => cancelCheck(row.id)}>{language === "en" ? "Cancel" : "Отменить"}</button>
                    </div>
                  </article>;
                })}</div> : <p className="muted-line">{language === "en" ? "No active checks." : "Нет активных проверок."}</p>}
              </article>
            </div>
          </div>
        ) : null}

        {dashboard && activeTab === "stats" ? <div className="command-section"><div className="command-section-title"><div><h2>{language === "en" ? "My stats" : "Моя статистика"}</h2></div></div><div className="command-metrics"><Metric label={language === "en" ? "Checks" : "Проверки"} value={dashboard.stats.totalChecks} /><Metric label={language === "en" ? "Bans" : "Баны"} value={dashboard.stats.totalBans} /><Metric label={language === "en" ? "Today" : "Сегодня"} value={dashboard.stats.todayChecks} /><Metric label={language === "en" ? "Average" : "Среднее время"} value={dashboard.stats.averageDuration} /></div><article className="command-card"><div className="command-card-head"><div><h3>{language === "en" ? "Recent checks" : "Последние проверки"}</h3></div></div><CheckTable rows={completedRows.slice(0, 8)} language={language} compact /></article></div> : null}
        {dashboard && activeTab === "history" ? <div className="command-section"><div className="command-section-title"><div><h2>{language === "en" ? "History" : "История проверок"}</h2></div></div><article className="command-card"><CheckTable rows={completedRows} language={language} /></article></div> : null}
      </div>
    </section>
  );
}

function ModeratorCommandCenter({
  currentUser,
  dashboard,
  busy,
  error,
  createCheck,
  finishCheck,
  cancelCheck,
  logout,
  language,
}: {
  currentUser: SiteUser | null;
  dashboard: SiteModeratorDashboard | null;
  busy: boolean;
  error: string;
  createCheck: (payload: Parameters<typeof siteModeratorCreateCheck>[1]) => Promise<SiteModeratorCheckResult | null>;
  finishCheck: (payload: Parameters<typeof siteModeratorFinishCheck>[1]) => Promise<void>;
  cancelCheck: (sessionId: string) => Promise<void>;
  logout: () => Promise<void>;
  language: Lang;
}) {
  const [activeTab, setActiveTab] = useState<ModeratorTab>("checks");
  const [suspectSteamId64, setSuspectSteamId64] = useState("");
  const [oneTimeKey, setOneTimeKey] = useState("");
  const [oneTimeKeySessionId, setOneTimeKeySessionId] = useState("");
  const [keyCopied, setKeyCopied] = useState(false);
  const [activeNow, setActiveNow] = useState(() => Date.now());
  const [banSession, setBanSession] = useState<CheckRow | null>(null);
  const [banReason, setBanReason] = useState("review_result");
  const [banCustomReason, setBanCustomReason] = useState("");
  const [banDuration, setBanDuration] = useState<ModeratorBanDuration>("forever");
  const [banCustomDays, setBanCustomDays] = useState("");
  const steamError = steamId64Error(suspectSteamId64, language);
  const rows = dashboard ? getCheckRows(dashboard, language) : [];
  const activeRows = rows.filter((row) => row.status !== "completed" && row.status !== "cancelled");
  const completedRows = rows.filter((row) => row.status === "completed");
  const subscription = dashboard?.subscription || dashboard?.project.subscription;
  const projectActionsLocked = Boolean(subscription && subscription.actionsAllowed === false);
  const copy = language === "en" ? {
    checks: "Checks", stats: "My statistics", history: "Check history", sections: "Moderator sections",
    panel: "Moderator panel", logout: "Sign out", start: "Start a check",
    steam: "SteamID64", steamPlaceholder: "Enter Steam ID", begin: "Start check",
    active: "Active checks", player: "Player", started: "Started", duration: "Duration", actions: "Actions",
    clean: "Clean", ban: "Ban", cancel: "Cancel", empty: "No active checks.",
    statsTitle: "My statistics", allChecks: "All checks", bans: "Bans", today: "Today", average: "Average time",
    recent: "Recent checks", historyTitle: "Check history", banTitle: "Finish check: ban", reason: "Reason", term: "Duration", finish: "Finish",
  } : {
    checks: "\u041f\u0440\u043e\u0432\u0435\u0440\u043a\u0438", stats: "\u041c\u043e\u044f \u0441\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043a\u0430", history: "\u0418\u0441\u0442\u043e\u0440\u0438\u044f \u043f\u0440\u043e\u0432\u0435\u0440\u043e\u043a", sections: "\u0420\u0430\u0437\u0434\u0435\u043b\u044b \u043c\u043e\u0434\u0435\u0440\u0430\u0442\u043e\u0440\u0430",
    panel: "\u041f\u0430\u043d\u0435\u043b\u044c \u043c\u043e\u0434\u0435\u0440\u0430\u0442\u043e\u0440\u0430", logout: "\u0412\u044b\u0439\u0442\u0438", start: "\u041d\u0430\u0447\u0430\u0442\u044c \u043f\u0440\u043e\u0432\u0435\u0440\u043a\u0443",
    steam: "SteamID64", steamPlaceholder: "\u0412\u0432\u0435\u0434\u0438\u0442\u0435 Steam ID", begin: "\u041d\u0430\u0447\u0430\u0442\u044c \u043f\u0440\u043e\u0432\u0435\u0440\u043a\u0443",
    active: "\u0410\u043a\u0442\u0438\u0432\u043d\u044b\u0435 \u043f\u0440\u043e\u0432\u0435\u0440\u043a\u0438", player: "\u0418\u0433\u0440\u043e\u043a", started: "\u041d\u0430\u0447\u0430\u043b\u043e (\u041c\u0421\u041a)", duration: "\u0414\u043b\u0438\u0442\u0435\u043b\u044c\u043d\u043e\u0441\u0442\u044c", actions: "\u0414\u0435\u0439\u0441\u0442\u0432\u0438\u044f",
    clean: "\u0427\u0438\u0441\u0442", ban: "\u0411\u0430\u043d", cancel: "\u041e\u0442\u043c\u0435\u043d\u0430", empty: "\u041d\u0435\u0442 \u0430\u043a\u0442\u0438\u0432\u043d\u044b\u0445 \u043f\u0440\u043e\u0432\u0435\u0440\u043e\u043a.",
    statsTitle: "\u041c\u043e\u044f \u0441\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043a\u0430", allChecks: "\u0412\u0441\u0435\u0433\u043e \u043f\u0440\u043e\u0432\u0435\u0440\u043e\u043a", bans: "\u0411\u0430\u043d\u043e\u0432", today: "\u0421\u0435\u0433\u043e\u0434\u043d\u044f", average: "\u0421\u0440\u0435\u0434\u043d\u0435\u0435 \u0432\u0440\u0435\u043c\u044f",
    recent: "\u041f\u043e\u0441\u043b\u0435\u0434\u043d\u0438\u0435 \u043f\u0440\u043e\u0432\u0435\u0440\u043a\u0438", historyTitle: "\u0418\u0441\u0442\u043e\u0440\u0438\u044f \u043f\u0440\u043e\u0432\u0435\u0440\u043e\u043a", banTitle: "\u0417\u0430\u0432\u0435\u0440\u0448\u0438\u0442\u044c \u043f\u0440\u043e\u0432\u0435\u0440\u043a\u0443: \u0431\u0430\u043d", reason: "\u041f\u0440\u0438\u0447\u0438\u043d\u0430", term: "\u0421\u0440\u043e\u043a", finish: "\u0417\u0430\u0432\u0435\u0440\u0448\u0438\u0442\u044c",
  };
  const navItems: Array<{ id: ModeratorTab; label: string; icon: typeof ShieldCheck }> = [
    { id: "checks", label: copy.checks, icon: ShieldCheck },
    { id: "stats", label: copy.stats, icon: BarChart3 },
    { id: "history", label: copy.history, icon: ClipboardList },
  ];

  useEffect(() => {
    const timer = window.setInterval(() => setActiveNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  async function submitCheck(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (projectActionsLocked || steamError || !suspectSteamId64) return;
    const result = await createCheck({ mode: "steam", suspectSteamId64, suspectName: "" });
    if (result?.mode === "steam") setSuspectSteamId64("");
  }

  async function generateOneTimeKey() {
    if (projectActionsLocked) return;
    const result = await createCheck({ mode: "key" });
    if (result?.oneTimeKey) {
      setOneTimeKey(result.oneTimeKey);
      setOneTimeKeySessionId(result.sessionId);
      setKeyCopied(false);
    }
  }

  async function copyOneTimeKey() {
    if (!oneTimeKey || !navigator.clipboard) return;
    await navigator.clipboard.writeText(oneTimeKey);
    setKeyCopied(true);
    window.setTimeout(() => setKeyCopied(false), 1800);
  }

  function clearOneTimeKey(sessionId?: string) {
    if (!sessionId || oneTimeKeySessionId === sessionId) {
      setOneTimeKey("");
      setOneTimeKeySessionId("");
      setKeyCopied(false);
    }
  }

  async function finishClean(sessionId: string) {
    if (projectActionsLocked) return;
    await finishCheck({ sessionId, verdict: "clean" });
    clearOneTimeKey(sessionId);
  }

  async function cancelActive(sessionId: string) {
    if (projectActionsLocked) return;
    await cancelCheck(sessionId);
    clearOneTimeKey(sessionId);
  }

  async function confirmBan() {
    const reason = moderatorBanReason(banReason, banCustomReason, language);
    const banDays = Number.parseInt(banCustomDays, 10);
    if (projectActionsLocked || !banSession || !reason || (banDuration === "custom" && (!Number.isFinite(banDays) || banDays < 1))) return;
    await finishCheck({ sessionId: banSession.id, verdict: "ban", reason, banDuration, ...(banDuration === "custom" ? { banDays } : {}) });
    clearOneTimeKey(banSession.id);
    setBanSession(null);
  }

  return (
    <section className="command-center moderator-center" id="account">
      <aside className="command-sidebar" aria-label={copy.sections}>
        <a className="command-brand" href="/"><img src="/cc_logo.png" alt="" /><span><strong>{dashboard?.project.displayName || "CheatChecker"}</strong></span></a>
        <nav className="command-nav">{navItems.map((item) => { const Icon = item.icon; return <button key={item.id} className={activeTab === item.id ? "is-active" : ""} type="button" onClick={() => setActiveTab(item.id)}><Icon size={18} /><span>{item.label}</span></button>; })}</nav>
      </aside>
      <div className="command-main">
        <header className="command-header"><div><p>{currentUser?.name || dashboard?.moderator.displayName || currentUser?.email || "CheatChecker"}</p><h1>{copy.panel}</h1></div><div className="command-actions"><button className="command-button is-danger" type="button" onClick={logout}>{copy.logout}</button></div></header>
        {error ? <p className="auth-note is-error">{error}</p> : null}
        <SubscriptionLifecycleNotice subscription={subscription} language={language} />
        {!dashboard ? <div className="dashboard-skeleton-grid" aria-hidden="true"><div /><div /><div /></div> : null}

        {dashboard && activeTab === "checks" ? <div className="command-section moderator-checks-section">
          <article className="command-card moderator-start-card"><h2>{copy.start}</h2><form className="moderator-start-form" onSubmit={submitCheck}><label><span>{copy.steam}</span><input value={suspectSteamId64} onChange={(event) => setSuspectSteamId64(normalizeSteamId64(event.target.value))} placeholder={copy.steamPlaceholder} inputMode="numeric" maxLength={17} required disabled={projectActionsLocked} aria-invalid={Boolean(steamError)} /></label><button className="command-button" type="submit" disabled={busy || projectActionsLocked || !suspectSteamId64 || Boolean(steamError)}><ShieldCheck size={18} />{copy.begin}</button><div className="moderator-key-option"><span>{language === "en" ? "or" : "или"}</span><button className="moderator-key-link" type="button" disabled={busy || projectActionsLocked} onClick={() => void generateOneTimeKey()}><KeyRound size={16} />{language === "en" ? "Generate one-time key" : "Сгенерировать случайный ключ"}</button></div></form>{steamError ? <p className="field-error">{steamError}</p> : null}{oneTimeKey ? <div className="command-key-box owner-check-key"><p>{language === "en" ? "Give this key to the player. It is shown only for this active check." : "Передайте ключ игроку. Он показан только для этой активной проверки."}</p><code>{oneTimeKey}</code><button className="mini-action" type="button" onClick={() => void copyOneTimeKey()}>{keyCopied ? <CheckCircle2 size={15} /> : <Copy size={15} />}{keyCopied ? (language === "en" ? "Copied" : "Скопировано") : (language === "en" ? "Copy key" : "Скопировать ключ")}</button></div> : null}</article>
          <article className="command-card moderator-active-card"><div className="command-card-head"><h3>{copy.active}</h3></div>{activeRows.length ? <div className="moderator-active-table"><div className="moderator-active-head"><span>{copy.player}</span><span>Steam ID</span><span>{copy.started}</span><span>{copy.duration}</span><span>{copy.actions}</span></div>{activeRows.map((row) => <div className="moderator-active-row" key={row.id}><span className="moderator-player"><strong>{row.target}</strong></span><span>{row.steamId}</span><span>{row.startedAt}</span><span>{formatActiveDuration(row.startedAt, activeNow, row.duration)}</span><span className="moderator-row-actions"><button className="mini-action is-clean" type="button" disabled={busy || projectActionsLocked} onClick={() => void finishClean(row.id)}>{copy.clean}</button><button className="mini-action is-danger" type="button" disabled={busy || projectActionsLocked} onClick={() => { setBanSession(row); setBanReason("review_result"); setBanCustomReason(""); setBanDuration("forever"); setBanCustomDays(""); }}>{copy.ban}</button><button className="mini-action" type="button" disabled={busy || projectActionsLocked} onClick={() => void cancelActive(row.id)}>{copy.cancel}</button></span></div>)}</div> : <p className="muted-line">{copy.empty}</p>}</article>
        </div> : null}

        {dashboard && activeTab === "stats" ? <ModeratorStats dashboard={dashboard} rows={completedRows} language={language} copy={copy} /> : null}
        {dashboard && activeTab === "history" ? <div className="command-section"><div className="command-section-title"><div><h2>{copy.historyTitle}</h2></div></div><article className="command-card"><CheckTable rows={completedRows} language={language} hideModerator /></article></div> : null}
      </div>
      {banSession ? <ModeratorBanDialog copy={copy} busy={busy || projectActionsLocked} reason={banReason} setReason={setBanReason} customReason={banCustomReason} setCustomReason={setBanCustomReason} duration={banDuration} setDuration={setBanDuration} customDays={banCustomDays} setCustomDays={setBanCustomDays} onCancel={() => setBanSession(null)} onConfirm={confirmBan} /> : null}
    </section>
  );
}

function ModeratorBanDialog({ copy, busy, reason, setReason, customReason, setCustomReason, duration, setDuration, customDays, setCustomDays, onCancel, onConfirm }: { copy: Record<string, string>; busy: boolean; reason: string; setReason: (value: string) => void; customReason: string; setCustomReason: (value: string) => void; duration: ModeratorBanDuration; setDuration: (value: ModeratorBanDuration) => void; customDays: string; setCustomDays: (value: string) => void; onCancel: () => void; onConfirm: () => void }) {
  const isEnglish = copy.checks === "Checks";
  const reasons = [
    ["review_result", isEnglish ? "Check results" : "\u041f\u043e \u0440\u0435\u0437\u0443\u043b\u044c\u0442\u0430\u0442\u0430\u043c \u043f\u0440\u043e\u0432\u0435\u0440\u043a\u0438"],
    ["cheat", isEnglish ? "Cheat" : "\u0427\u0438\u0442"],
    ["macro", isEnglish ? "Macro" : "\u041c\u0430\u043a\u0440\u043e\u0441"],
    ["rules", isEnglish ? "Project rules" : "\u041d\u0430\u0440\u0443\u0448\u0435\u043d\u0438\u0435 \u043f\u0440\u0430\u0432\u0438\u043b \u043f\u0440\u043e\u0435\u043a\u0442\u0430"],
    ["other_bans", isEnglish ? "Bans on other projects" : "\u0411\u0430\u043d\u044b \u043d\u0430 \u0434\u0440\u0443\u0433\u0438\u0445 \u043f\u0440\u043e\u0435\u043a\u0442\u0430\u0445"],
    ["ignore", isEnglish ? "Ignoring the check" : "\u0418\u0433\u043d\u043e\u0440\u0438\u0440\u043e\u0432\u0430\u043d\u0438\u0435 \u043f\u0440\u043e\u0432\u0435\u0440\u043a\u0438"],
    ["custom", isEnglish ? "Custom" : "\u041a\u0430\u0441\u0442\u043e\u043c"],
  ] as const;
  const durations: Array<[ModeratorBanDuration, string]> = [
    ["forever", isEnglish ? "Forever" : "\u041d\u0430\u0432\u0441\u0435\u0433\u0434\u0430"], ["two_weeks", isEnglish ? "2 weeks" : "2 \u043d\u0435\u0434\u0435\u043b\u0438"], ["one_month", isEnglish ? "1 month" : "\u041c\u0435\u0441\u044f\u0446"], ["three_months", isEnglish ? "3 months" : "3 \u043c\u0435\u0441\u044f\u0446\u0430"], ["six_months", isEnglish ? "6 months" : "6 \u043c\u0435\u0441\u044f\u0446\u0435\u0432"], ["one_year", isEnglish ? "1 year" : "1 \u0433\u043e\u0434"], ["custom", isEnglish ? "Custom" : "\u041a\u0430\u0441\u0442\u043e\u043c"],
  ];
  const customReasonLabel = isEnglish ? "Enter reason" : "\u0423\u043a\u0430\u0436\u0438\u0442\u0435 \u043f\u0440\u0438\u0447\u0438\u043d\u0443";
  const customDaysLabel = isEnglish ? "Number of days" : "\u041a\u043e\u043b\u0438\u0447\u0435\u0441\u0442\u0432\u043e \u0434\u043d\u0435\u0439";
  const invalidCustom = (reason === "custom" && !customReason.trim()) || (duration === "custom" && (!/^\d+$/.test(customDays) || Number(customDays) < 1));
  return <div className="action-modal moderator-ban-modal" role="presentation" onMouseDown={onCancel}><section className="moderator-ban-dialog" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}><h2>{copy.banTitle}</h2><p>{copy.reason}</p><div className="moderator-choice-grid">{reasons.map(([value, label]) => <button className={reason === value ? "is-active" : ""} type="button" key={value} onClick={() => setReason(value)}>{label}</button>)}</div>{reason === "custom" ? <label className="moderator-custom-field"><span>{customReasonLabel}</span><input value={customReason} onChange={(event) => setCustomReason(event.target.value)} maxLength={160} autoFocus /></label> : null}<p>{copy.term}</p><div className="moderator-choice-grid is-duration">{durations.map(([value, label]) => <button className={duration === value ? "is-active" : ""} type="button" key={value} onClick={() => setDuration(value)}>{label}</button>)}</div>{duration === "custom" ? <label className="moderator-custom-field"><span>{customDaysLabel}</span><input value={customDays} onChange={(event) => setCustomDays(event.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" placeholder="30" /></label> : null}<div className="action-dialog-actions"><button className="action-dialog-button" type="button" disabled={busy} onClick={onCancel}>{copy.cancel}</button><button className="action-dialog-button is-primary" type="button" disabled={busy || invalidCustom} onClick={onConfirm}>{copy.finish}</button></div></section></div>;
}

function LegacyModeratorBanDialog({ copy, busy, reason, setReason, duration, setDuration, onCancel, onConfirm }: { copy: Record<string, string>; busy: boolean; reason: string; setReason: (value: string) => void; duration: "two_weeks" | "one_month" | "three_months" | "six_months" | "one_year" | "forever"; setDuration: (value: "two_weeks" | "one_month" | "three_months" | "six_months" | "one_year" | "forever") => void; onCancel: () => void; onConfirm: () => void }) {
  const reasons = [["review_result", "\u041f\u043e \u0440\u0435\u0437\u0443\u043b\u044c\u0442\u0430\u0442\u0430\u043c \u043f\u0440\u043e\u0432\u0435\u0440\u043a\u0438"], ["cheat", "\u0427\u0438\u0442"], ["macro", "\u041c\u0430\u043a\u0440\u043e\u0441"], ["rules", "\u041d\u0430\u0440\u0443\u0448\u0435\u043d\u0438\u0435 \u043f\u0440\u0430\u0432\u0438\u043b"]] as const;
  const durations = [["forever", languageLabel(copy, "Forever", "\u041d\u0430\u0432\u0441\u0435\u0433\u0434\u0430")], ["two_weeks", languageLabel(copy, "2 weeks", "2 \u043d\u0435\u0434\u0435\u043b\u0438")], ["one_month", languageLabel(copy, "1 month", "\u041c\u0435\u0441\u044f\u0446")], ["three_months", languageLabel(copy, "3 months", "3 \u043c\u0435\u0441\u044f\u0446\u0430")], ["six_months", languageLabel(copy, "6 months", "6 \u043c\u0435\u0441\u044f\u0446\u0435\u0432")], ["one_year", languageLabel(copy, "1 year", "1 \u0433\u043e\u0434")]] as const;
  return <div className="action-modal moderator-ban-modal" role="presentation" onMouseDown={onCancel}><section className="moderator-ban-dialog" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}><h2>{copy.banTitle}</h2><p>{copy.reason}</p><div className="moderator-choice-grid">{reasons.map(([value, label]) => <button className={reason === value ? "is-active" : ""} type="button" key={value} onClick={() => setReason(value)}>{label}</button>)}</div><p>{copy.term}</p><div className="moderator-choice-grid is-duration">{durations.map(([value, label]) => <button className={duration === value ? "is-active" : ""} type="button" key={value} onClick={() => setDuration(value)}>{label}</button>)}</div><div className="action-dialog-actions"><button className="action-dialog-button" type="button" disabled={busy} onClick={onCancel}>{copy.cancel}</button><button className="action-dialog-button is-primary" type="button" disabled={busy || !reason.trim()} onClick={onConfirm}>{copy.finish}</button></div></section></div>;
}

function moderatorBanReason(reason: string, customReason: string, language: Lang) {
  if (reason === "custom") return customReason.trim();
  const labels = language === "en" ? {
    review_result: "Check results", cheat: "Cheat", macro: "Macro", rules: "Project rules", other_bans: "Bans on other projects", ignore: "Ignoring the check",
  } : {
    review_result: "\u041f\u043e \u0440\u0435\u0437\u0443\u043b\u044c\u0442\u0430\u0442\u0430\u043c \u043f\u0440\u043e\u0432\u0435\u0440\u043a\u0438", cheat: "\u0427\u0438\u0442", macro: "\u041c\u0430\u043a\u0440\u043e\u0441", rules: "\u041d\u0430\u0440\u0443\u0448\u0435\u043d\u0438\u0435 \u043f\u0440\u0430\u0432\u0438\u043b \u043f\u0440\u043e\u0435\u043a\u0442\u0430", other_bans: "\u0411\u0430\u043d\u044b \u043d\u0430 \u0434\u0440\u0443\u0433\u0438\u0445 \u043f\u0440\u043e\u0435\u043a\u0442\u0430\u0445", ignore: "\u0418\u0433\u043d\u043e\u0440\u0438\u0440\u043e\u0432\u0430\u043d\u0438\u0435 \u043f\u0440\u043e\u0432\u0435\u0440\u043a\u0438",
  };
  return labels[reason as keyof typeof labels] || reason;
}

function languageLabel(copy: Record<string, string>, english: string, russian: string) {
  return copy.checks === "Checks" ? english : russian;
}

function ModeratorStats({ dashboard, rows, language, copy }: { dashboard: SiteModeratorDashboard; rows: CheckRow[]; language: Lang; copy: Record<string, string> }) {
  const [dailyMode, setDailyMode] = useState<"7" | "30">("7");
  const stats = getModeratorStats(dashboard, rows);
  const total = Math.max(1, stats.total);
  const banPercent = Math.round((stats.bans / total) * 100);
  const dailyBars = getDailyBars(rows, dailyMode === "7" ? 7 : 30);
  return <div className="command-section"><div className="command-section-title"><div><h2>{copy.statsTitle}</h2></div></div><div className="command-metrics"><StatTile icon={<ClipboardList size={18} />} label={copy.allChecks} value={stats.total} /><StatTile icon={<ShieldCheck size={18} />} label={copy.bans} value={stats.bans} tone="danger" /><StatTile icon={<CalendarDays size={18} />} label={copy.today} value={dashboard.stats.todayChecks} /><StatTile icon={<FileClock size={18} />} label={copy.average} value={dashboard.stats.averageDuration || "-"} /></div><article className="command-card chart-card daily-chart-card"><div className="chart-title-row"><h3>{language === "en" ? "Checks by day" : "\u041f\u0440\u043e\u0432\u0435\u0440\u043a\u0438 \u043f\u043e \u0434\u043d\u044f\u043c"}</h3><div className="chart-switch"><button className={dailyMode === "7" ? "is-active" : ""} type="button" onClick={() => setDailyMode("7")}>7</button><button className={dailyMode === "30" ? "is-active" : ""} type="button" onClick={() => setDailyMode("30")}>30</button></div></div><DailyChecksChart bars={dailyBars} language={language} /></article><div className="command-grid is-stats"><article className="command-card chart-card"><h3>{language === "en" ? "Verdicts" : "\u0412\u0435\u0440\u0434\u0438\u043a\u0442\u044b"}</h3><div className="verdict-donut" style={{ background: `radial-gradient(circle at center, rgba(12, 20, 34, 1) 0 56%, transparent 57%), conic-gradient(#ff6d88 0 ${banPercent}%, #62b1ff ${banPercent}% 100%)` }}><strong>{stats.total}</strong><span>{language === "en" ? "checks" : "\u043f\u0440\u043e\u0432\u0435\u0440\u043e\u043a"}</span></div><div className="chart-legend"><span><i className="is-ban" />{copy.bans} {banPercent}%</span><span><i />{language === "en" ? "Clean" : "\u0427\u0438\u0441\u0442"} {100 - banPercent}%</span></div></article><article className="command-card chart-card"><h3>{language === "en" ? "Top reasons" : "\u0422\u043e\u043f-\u043f\u0440\u0438\u0447\u0438\u043d\u044b"}</h3><div className="reason-list">{stats.reasonBars.length ? stats.reasonBars.map((item) => <div key={item.label}><span>{item.label}</span><strong>{item.value}</strong></div>) : <p className="muted-line">{language === "en" ? "No bans with a reason yet." : "\u041f\u043e\u043a\u0430 \u043d\u0435\u0442 \u0431\u0430\u043d\u043e\u0432 \u0441 \u043f\u0440\u0438\u0447\u0438\u043d\u043e\u0439."}</p>}</div></article></div></div>;
}

function getModeratorStats(dashboard: SiteModeratorDashboard, rows: CheckRow[]) {
  const total = dashboard.stats.totalChecks || rows.length;
  const bans = dashboard.stats.totalBans || rows.filter((row) => row.verdict === "ban").length;
  const reasons = new Map<string, number>();
  rows.filter((row) => row.verdict === "ban" && row.reason && row.reason !== "-").forEach((row) => reasons.set(row.reason, (reasons.get(row.reason) || 0) + 1));
  return { total, bans, reasonBars: Array.from(reasons.entries()).sort(([, left], [, right]) => right - left).slice(0, 5).map(([label, value]) => ({ label, value })) };
}

type AccountDashboardProps = {
  currentUser: SiteUser | null;
  dashboard: SiteDashboard | null;
  busy: boolean;
  error: string;
  notice: string;
  paymentConfig: SitePaymentConfig | null;
  selectedPlan: SiteSubscriptionPlan;
  paymentMessage: string;
  openRenewal: () => void;
  purchaseForm: PurchaseForm;
  projectForm: PurchaseForm;
  moderatorForm: ModeratorForm;
  steamEmail: string;
  steamEmailSent: boolean;
  setSteamEmail: (value: string) => void;
  updatePurchaseField: (field: keyof PurchaseForm, value: string) => void;
  updateProjectField: (field: keyof PurchaseForm, value: string) => void;
  updateModeratorField: (field: keyof ModeratorForm, value: string) => void;
  attachSteamEmail: (event: FormEvent<HTMLFormElement>) => void;
  resendVerification: () => void;
  startSteamLink: () => void;
  createPayment: (method: "sbp" | "bank_card") => void;
  promoCode: string;
  promoPreview: SitePromoPreflight | null;
  promoBusy: boolean;
  promoError: string;
  updatePromoCode: (value: string) => void;
  applyPromoCode: () => Promise<void>;
  clearPromoCode: () => void;
  createProject: (event: FormEvent<HTMLFormElement>) => void;
  requestProjectDeletion: () => void;
  confirmProjectDeletion?: () => void;
  updateProject: (event: FormEvent<HTMLFormElement>) => void;
  createModerator: (event: FormEvent<HTMLFormElement>) => void;
  renameModerator: (moderatorId: string, currentName: string) => void;
  removeModerator: (moderatorId: string, currentName: string) => void;
  refreshDashboard: () => void;
  createOwnerCheck: (payload: Parameters<typeof siteOwnerCreateCheck>[1]) => Promise<SiteModeratorCheckResult | null>;
  finishOwnerCheck: (payload: Parameters<typeof siteOwnerFinishCheck>[1]) => Promise<void>;
  cancelOwnerCheck: (sessionId: string) => Promise<void>;
  getOwnerModeratorStats: (moderatorId: string) => Promise<SiteOwnerModeratorStats>;
  getSecuritySettings: () => Promise<SiteSecuritySettings>;
  startTwoFactorSetup: () => Promise<SiteTwoFactorSetup>;
  confirmTwoFactorSetup: (code: string) => Promise<boolean>;
  disableTwoFactor: (code: string) => Promise<boolean>;
  requestRecentPasswordLogin: () => void;
  logout: () => void;
  language: Lang;
};

type AccountTab = "overview" | "checks" | "moderators" | "stats" | "history" | "settings";
type VerdictFilter = "all" | "clean" | "ban";
type VerdictKind = "clean" | "ban" | "unknown";

function isAccountTab(value: string | null): value is AccountTab {
  return value === "overview" || value === "checks" || value === "moderators" || value === "stats" || value === "history" || value === "settings";
}

function detectInitialAccountTab(): AccountTab {
  const saved = localStorage.getItem(ACCOUNT_TAB_KEY);
  return isAccountTab(saved) ? saved : "overview";
}

function formatSubscriptionDate(value: string | null, language: Lang) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(language === "en" ? "en-GB" : "ru-RU", { day: "2-digit", month: "long", year: "numeric" }).format(date);
}

function SubscriptionLifecycleNotice({ subscription, language, onRenew }: { subscription: SiteSubscriptionLifecycle | undefined; language: Lang; onRenew?: () => void }) {
  if (!subscription || subscription.status === "active" || subscription.status === "none") return null;
  const ru = language === "ru";
  const copy = subscription.status === "expiring_soon"
    ? {
      title: ru ? "Подписка скоро закончится" : "Subscription ends soon",
      text: ru ? `Доступ действует до ${formatSubscriptionDate(subscription.licenseExpiresAt, language)} Продлите подписку заранее, чтобы проект работал без перерыва.` : `Access is active until ${formatSubscriptionDate(subscription.licenseExpiresAt, language)}. Renew now to avoid an interruption.`,
    }
    : subscription.status === "grace"
      ? {
        title: ru ? "Подписка закончилась, проект ещё работает" : "Subscription ended, project is still working",
        text: ru ? `Льготный период закончится ${formatSubscriptionDate(subscription.graceExpiresAt, language)}. После этого проект будет заморожен до продления.` : `The grace period ends ${formatSubscriptionDate(subscription.graceExpiresAt, language)}. After that, the project will freeze until renewal.`,
      }
      : {
        title: ru ? "Проект заморожен" : "Project is frozen",
        text: ru ? "Срок подписки и льготный период закончились. Управление проектом будет доступно сразу после продления." : "The subscription and grace period have ended. Project management returns immediately after renewal.",
      };
  return <article className={`subscription-lifecycle-notice is-${subscription.status}`} role={subscription.status === "frozen" ? "alert" : "status"}>
    <div><h2>{copy.title}</h2><p>{copy.text}</p></div>
    {subscription.canRenew && onRenew ? <button className="command-button" type="button" onClick={onRenew}>{ru ? "Продлить" : "Renew"}<ArrowRight size={17} /></button> : null}
  </article>;
}

type CheckRow = {
  id: string;
  status: string;
  target: string;
  steamId: string;
  moderator: string;
  startedAt: string;
  endedAt: string;
  duration: string;
  verdict: VerdictKind;
  verdictText: string;
  reason: string;
  dateInput: string;
};

function AccountCommandCenter({
  currentUser,
  dashboard,
  busy,
  error,
  notice,
  paymentConfig,
  selectedPlan,
  paymentMessage,
  openRenewal,
  purchaseForm,
  projectForm,
  moderatorForm,
  steamEmail,
  steamEmailSent,
  setSteamEmail,
  updatePurchaseField,
  updateProjectField,
  updateModeratorField,
  attachSteamEmail,
  resendVerification,
  startSteamLink,
  createPayment,
  promoCode,
  promoPreview,
  promoBusy,
  promoError,
  updatePromoCode,
  applyPromoCode,
  clearPromoCode,
  createProject,
  requestProjectDeletion,
  confirmProjectDeletion,
  updateProject,
  createModerator,
  renameModerator,
  removeModerator,
  refreshDashboard,
  createOwnerCheck,
  finishOwnerCheck,
  cancelOwnerCheck,
  getOwnerModeratorStats,
  getSecuritySettings,
  startTwoFactorSetup,
  confirmTwoFactorSetup,
  disableTwoFactor,
  requestRecentPasswordLogin,
  logout,
  language,
}: AccountDashboardProps) {
  const [activeTab, setActiveTab] = useState<AccountTab>(detectInitialAccountTab);
  const [historySearch, setHistorySearch] = useState("");
  const [historyVerdict, setHistoryVerdict] = useState<VerdictFilter>("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const account = dashboard?.account || currentUser;
  const verified = account?.emailVerified;
  const project = dashboard?.project;
  const projectCreated = Boolean(project);
  const projectActionsLocked = Boolean(project && project.subscription?.actionsAllowed === false);
  const rows = dashboard ? getCheckRows(dashboard, language) : [];
  const filteredRows = rows.filter((row) => filterCheckRow(row, historySearch, historyVerdict, dateFrom, dateTo));
  const stats = dashboard ? getDashboardStats(dashboard, rows) : null;
  const projectName = project?.displayName || purchaseForm.displayName || (language === "en" ? "New project" : "Новый проект");
  const steamLinked = Boolean(account?.steamId64);
  const needsSteamEmail = account?.authProvider === "steam" && !account?.email;
  const moderatorSteamError = steamId64Error(moderatorForm.steamId64, language);

  useEffect(() => {
    localStorage.setItem(ACCOUNT_TAB_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (!projectCreated && activeTab !== "overview") {
      setActiveTab("overview");
    }
  }, [activeTab, projectCreated]);

  const navItems: Array<{ id: AccountTab; label: string; icon: typeof LayoutDashboard }> = [
    { id: "overview", label: language === "en" ? "Overview" : "Общее", icon: LayoutDashboard },
    { id: "moderators", label: language === "en" ? "Moderators" : "Модераторы", icon: UsersRound },
    { id: "stats", label: language === "en" ? "Check stats" : "Статистика проверок", icon: BarChart3 },
    { id: "history", label: language === "en" ? "Check history" : "История проверок", icon: ClipboardList },
    { id: "checks", label: language === "en" ? "Start a check" : "Начать проверку", icon: ShieldCheck },
    { id: "settings", label: language === "en" ? "Settings" : "Настройки", icon: Settings },
  ];

  return (
    <section className="command-center" id="account">
      <aside className="command-sidebar" aria-label={language === "en" ? "Account sections" : "Разделы кабинета"}>
        <a className="command-brand" href="/">
          <img src="/cc_logo.png" alt="" />
          <span>
            <strong>{projectName}</strong>
          </span>
        </a>

        <nav className="command-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const unavailableUntilProject = !projectCreated && item.id !== "overview";
            return (
              <button
                key={item.id}
                className={`${activeTab === item.id ? "is-active" : ""}${unavailableUntilProject ? " is-unavailable" : ""}`}
                type="button"
                disabled={unavailableUntilProject}
                aria-disabled={unavailableUntilProject}
                title={unavailableUntilProject ? (language === "en" ? "Create a project to unlock this section" : "Создайте проект, чтобы открыть этот раздел") : undefined}
                onClick={() => setActiveTab(item.id)}
              >
                <Icon size={18} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <a className="command-contact" href="#contacts">
          <MessageCircle size={17} />
          {language === "en" ? "Support and contacts" : "Поддержка и контакты"}
        </a>
      </aside>

      <div className="command-main">
        <header className="command-header">
          <div>
            <h1>{language === "en" ? "Account" : "Личный кабинет"}</h1>
          </div>
          <div className="command-actions">
            <button className="command-button is-danger" type="button" onClick={logout}>
              {language === "en" ? "Sign out" : "Выйти"}
            </button>
          </div>
        </header>

        {error ? <p className="auth-note is-error">{error}</p> : null}
        {notice ? <p className="command-feedback dashboard-toast" role="status">{notice}</p> : null}
        <SubscriptionLifecycleNotice subscription={project?.subscription} language={language} onRenew={openRenewal} />

        {!dashboard ? (
          <div className="dashboard-skeleton-grid" aria-hidden="true">
            <div />
            <div />
            <div />
          </div>
        ) : null}

        {dashboard && needsSteamEmail ? (
          <article className="command-notice">
            <div>
              <h3>{language === "en" ? "Add email" : "Добавьте email"}</h3>
              <p>{language === "en" ? "Steam does not share email. Add one and confirm it to create a project." : "Steam не передаёт почту. Укажите email и подтвердите письмо, чтобы создать проект."}</p>
            </div>
            <form className="command-inline-form" onSubmit={attachSteamEmail}>
              <input
                value={steamEmail}
                onChange={(event) => setSteamEmail(event.target.value)}
                disabled={busy}
                placeholder="you@example.com"
                type="email"
                autoComplete="email"
              />
              <button className="command-button" type="submit" disabled={busy || !steamEmail.trim()}>
                {language === "en" ? "Send" : "Отправить"}
              </button>
            </form>
            {steamEmailSent ? <p className="field-hint">{language === "en" ? "Email sent. After confirmation, refresh the account." : "Письмо отправлено. После подтверждения обновите кабинет."}</p> : null}
          </article>
        ) : dashboard && !verified ? (
          <article className="command-notice">
            <div>
              <h3>{language === "en" ? "Confirm email" : "Подтвердите почту"}</h3>
              <p>{language === "en" ? "After confirmation, you can create a project and issue access." : "После подтверждения можно создать проект и выдать доступы."}</p>
            </div>
            <button className="command-button is-muted" type="button" disabled={busy} onClick={resendVerification}>
              {language === "en" ? "Send email" : "Отправить письмо"}
            </button>
          </article>
        ) : null}

        {dashboard ? (
          <>
            {activeTab === "overview" ? (
              <CommandOverview
                busy={busy || projectActionsLocked}
                paymentConfig={paymentConfig}
                selectedPlan={selectedPlan}
                paymentMessage={paymentMessage}
                dashboard={dashboard}
                language={language}
                purchaseForm={purchaseForm}
                projectForm={projectForm}
                updatePurchaseField={updatePurchaseField}
                updateProjectField={updateProjectField}
                createPayment={createPayment}
                promoCode={promoCode}
                promoPreview={promoPreview}
                promoBusy={promoBusy}
                promoError={promoError}
                updatePromoCode={updatePromoCode}
                applyPromoCode={applyPromoCode}
                clearPromoCode={clearPromoCode}
                createProject={createProject}
                requestProjectDeletion={requestProjectDeletion}
                confirmProjectDeletion={confirmProjectDeletion}
                updateProject={updateProject}
                verified={Boolean(verified)}
                rows={rows}
              />
            ) : null}

            {activeTab === "checks" ? (
              <OwnerChecks
                busy={busy || projectActionsLocked}
                dashboard={dashboard}
                language={language}
                createCheck={createOwnerCheck}
                finishCheck={finishOwnerCheck}
                cancelCheck={cancelOwnerCheck}
              />
            ) : null}

            {activeTab === "moderators" ? (
              <CommandModerators
                busy={busy || projectActionsLocked}
                dashboard={dashboard}
                language={language}
                moderatorForm={moderatorForm}
                moderatorSteamError={moderatorSteamError}
                updateModeratorField={updateModeratorField}
                createModerator={createModerator}
                renameModerator={renameModerator}
                removeModerator={removeModerator}
                getModeratorStats={getOwnerModeratorStats}
              />
            ) : null}

            {activeTab === "stats" && stats ? (
              <CommandStats dashboard={dashboard} rows={rows} stats={stats} language={language} />
            ) : null}

            {activeTab === "history" ? (
              <CommandHistory
                rows={filteredRows}
                language={language}
                search={historySearch}
                setSearch={setHistorySearch}
                verdict={historyVerdict}
                setVerdict={setHistoryVerdict}
                dateFrom={dateFrom}
                setDateFrom={setDateFrom}
                dateTo={dateTo}
                setDateTo={setDateTo}
              />
            ) : null}

            {activeTab === "settings" ? (
              <CommandSettings
                busy={busy || projectActionsLocked}
                dashboard={dashboard}
                language={language}
                projectForm={projectForm}
                updateProjectField={updateProjectField}
                updateProject={updateProject}
                steamLinked={steamLinked}
                startSteamLink={startSteamLink}
                requestProjectDeletion={requestProjectDeletion}
                confirmProjectDeletion={confirmProjectDeletion}
                getSecuritySettings={getSecuritySettings}
                startTwoFactorSetup={startTwoFactorSetup}
                confirmTwoFactorSetup={confirmTwoFactorSetup}
                disableTwoFactor={disableTwoFactor}
                requestRecentPasswordLogin={requestRecentPasswordLogin}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}

function CommandOverview({
  busy,
  paymentConfig,
  selectedPlan,
  paymentMessage,
  dashboard,
  language,
  purchaseForm,
  projectForm,
  updatePurchaseField,
  updateProjectField,
  createPayment,
  promoCode,
  promoPreview,
  promoBusy,
  promoError,
  updatePromoCode,
  applyPromoCode,
  clearPromoCode,
  createProject,
  requestProjectDeletion,
  confirmProjectDeletion,
  updateProject,
  verified,
  rows,
}: {
  busy: boolean;
  paymentConfig: SitePaymentConfig | null;
  selectedPlan: SiteSubscriptionPlan;
  paymentMessage: string;
  dashboard: SiteDashboard;
  language: Lang;
  purchaseForm: PurchaseForm;
  projectForm: PurchaseForm;
  updatePurchaseField: (field: keyof PurchaseForm, value: string) => void;
  updateProjectField: (field: keyof PurchaseForm, value: string) => void;
  createPayment: (method: "sbp" | "bank_card") => void;
  promoCode: string;
  promoPreview: SitePromoPreflight | null;
  promoBusy: boolean;
  promoError: string;
  updatePromoCode: (value: string) => void;
  applyPromoCode: () => Promise<void>;
  clearPromoCode: () => void;
  createProject: (event: FormEvent<HTMLFormElement>) => void;
  requestProjectDeletion: () => void;
  confirmProjectDeletion?: () => void;
  updateProject: (event: FormEvent<HTMLFormElement>) => void;
  verified: boolean;
  rows: CheckRow[];
}) {
  const project = dashboard.project;
  const purchaseProfileReady = Boolean(verified && purchaseForm.displayName.trim() && purchaseForm.ownerDisplayName.trim());
  const paymentReady = Boolean(paymentConfig?.enabled && verified);
  const canCreateProject = Boolean(paymentConfig?.access?.canCreateProject);
  const priceLabel = `${new Intl.NumberFormat(language === "en" ? "en-US" : "ru-RU").format(selectedPlan.amount)} ₽`;
  const promoPriceLabel = promoPreview
    ? `${new Intl.NumberFormat(language === "en" ? "en-US" : "ru-RU", { maximumFractionDigits: 2 }).format(promoPreview.amount)} ₽`
    : priceLabel;

  if (!project && !canCreateProject) {
    return <div className="command-section account-access-pending"><div className="command-section-title"><span className="overview-title-icon" aria-hidden="true"><LockKeyhole size={18} /></span><div><h2>{language === "en" ? "Access pending" : "Доступ ожидает оплаты"}</h2><p>{language === "en" ? "Purchase a license from the homepage to open the cabinet." : "Купите лицензию на главной странице, чтобы открыть кабинет."}</p></div></div></div>;
  }

  return (
    <div className="command-section">
      <div className="command-section-title">
        <div>
          <h2>{language === "en" ? "Overview" : "Общее"}</h2>
        </div>
      </div>

      <div className="command-grid is-main is-single">
        <article className="command-card project-command-card">
          <div className="command-card-head">
            <div>
              <h3>{language === "en" ? "Project" : "Проект"}</h3>
            </div>
          </div>

          {project ? (<>
            <form className="command-form" onSubmit={updateProject}>
              <div className="command-form-grid">
                <label>
                  <span>{language === "en" ? "Name" : "Название"}</span>
                  <input value={projectForm.displayName} onChange={(event) => updateProjectField("displayName", event.target.value)} disabled={busy} />
                </label>
                <label>
                  <span>{language === "en" ? "Game" : "Игра"}</span>
                  <GamePicker value={projectForm.game} onChange={(game) => updateProjectField("game", game)} disabled={busy} />
                </label>
              </div>
              <button className="command-button" type="submit" disabled={busy || !projectForm.displayName.trim() || !projectForm.ownerDisplayName.trim()}>
                {language === "en" ? "Save" : "Сохранить"}
              </button>
            </form>
            <p className="project-subscription-until">{language === "en" ? "Subscription active until" : "Подписка активна до"}: {formatSubscriptionDate(project.subscription?.licenseExpiresAt || project.licenseExpiresAt, language)}</p>
          </>) : canCreateProject ? (
            <form className="command-form" onSubmit={createProject}>
              <div className="project-unlocked-note"><CheckCircle2 size={19} /><div><strong>{language === "en" ? "Payment confirmed" : "Оплата подтверждена"}</strong><span>{language === "en" ? "Create your project to activate the cabinet." : "Создайте проект, чтобы активировать кабинет."}</span></div></div>
              <div className="command-form-grid">
                <label><span>{language === "en" ? "Name" : "Название"}</span><input value={purchaseForm.displayName} onChange={(event) => updatePurchaseField("displayName", event.target.value)} disabled={busy} placeholder="Project" /></label>
                <label><span>{language === "en" ? "Game" : "Игра"}</span><GamePicker value={purchaseForm.game} onChange={(game) => updatePurchaseField("game", game)} disabled={busy} /></label>
                <label><span>{language === "en" ? "Owner" : "Владелец"}</span><input value={purchaseForm.ownerDisplayName} onChange={(event) => updatePurchaseField("ownerDisplayName", event.target.value)} disabled={busy} placeholder="Owner" /></label>
              </div>
              <button className="command-button" type="submit" disabled={busy || !purchaseProfileReady}><KeyRound size={18} />{language === "en" ? "Create project" : "Создать проект"}</button>
            </form>
          ) : (
            <form className="command-form checkout-form" onSubmit={(event) => { event.preventDefault(); createPayment("sbp"); }}>
              <div className="command-form-grid">
                <label>
                  <span>{language === "en" ? "Name" : "Название"}</span>
                  <input value={purchaseForm.displayName} onChange={(event) => updatePurchaseField("displayName", event.target.value)} disabled={busy || !verified} placeholder="Project" />
                </label>
                <label>
                  <span>{language === "en" ? "Game" : "Игра"}</span>
                  <GamePicker value={purchaseForm.game} onChange={(game) => updatePurchaseField("game", game)} disabled={busy || !verified} />
                </label>
                <label>
                  <span>{language === "en" ? "Owner" : "Владелец"}</span>
                  <input value={purchaseForm.ownerDisplayName} onChange={(event) => updatePurchaseField("ownerDisplayName", event.target.value)} disabled={busy || !verified} placeholder="Owner" />
                </label>
              </div>
              <div className="checkout-intro">
                <span className="checkout-intro-icon"><CreditCard size={21} /></span>
                <div>
                  <h4>{language === "en" ? "Choose a payment method" : "Выберите способ оплаты"}</h4>
                  <p>{language === "en" ? "Access is activated after payment confirmation." : "Доступ будет активирован после подтверждения оплаты."}</p>
                </div>
              </div>
              <div className="payment-actions">
                <button className="command-button payment-button" type="submit" disabled={busy || !paymentReady}>
                  <QrCode size={18} />
                  {language === "en" ? "Pay with SBP" : "Оплатить по СБП"}
                </button>
                <button className="command-button is-muted payment-button" type="button" disabled={busy || !paymentReady} onClick={() => createPayment("bank_card")}>
                  <CreditCard size={18} />
                  {language === "en" ? "Pay by card" : "Оплатить картой"}
                </button>
              </div>
              <PromoCodeField
                language={language}
                code={promoCode}
                preview={promoPreview}
                busy={busy || promoBusy}
                error={promoError}
                baseAmount={selectedPlan.amount}
                currency={selectedPlan.currency || paymentConfig?.currency || "RUB"}
                onChange={updatePromoCode}
                onApply={() => void applyPromoCode()}
                onClear={clearPromoCode}
              />
              <div className="payment-summary">
                <div>
                  <span>{language === "en" ? "License" : "Лицензия"}</span>
                  <strong>{language === "en" ? `${selectedPlan.months} ${selectedPlan.months === 1 ? "month" : "months"}` : selectedPlan.labels.ru}</strong>
                </div>
                <div>
                  <span>{language === "en" ? "Total" : "К оплате"}</span>
                  <strong>{promoPriceLabel}</strong>
                </div>
              </div>
              {paymentMessage ? <p className="payment-message" role="status">{paymentMessage}</p> : null}
              {paymentConfig?.legalReady ? (
                <p className="payment-legal">
                  {language === "en" ? "Seller:" : "Продавец:"} {paymentConfig.sellerName}. {" "}
                  <a href={paymentConfig.offerUrl} target={paymentConfig.offerUrl.startsWith("http") ? "_blank" : undefined} rel={paymentConfig.offerUrl.startsWith("http") ? "noreferrer" : undefined}>
                    {language === "en" ? "Payment terms" : "Условия оплаты"}
                  </a>
                  {" · "}
                  <a href={paymentConfig.privacyUrl} target={paymentConfig.privacyUrl.startsWith("http") ? "_blank" : undefined} rel={paymentConfig.privacyUrl.startsWith("http") ? "noreferrer" : undefined}>
                    {language === "en" ? "Privacy policy" : "Политика конфиденциальности"}
                  </a>
                </p>
              ) : null}
              {!paymentConfig?.enabled ? (
                <p className="payment-note">{language === "en" ? "Online payment is being configured." : "Онлайн-оплата пока настраивается."}</p>
              ) : paymentConfig.testMode ? (
                <p className="payment-note is-test">{language === "en" ? "Robokassa test mode is enabled." : "Включён тестовый режим Robokassa."}</p>
              ) : (
                <p className="payment-note">{language === "en" ? "Access activates after confirmed payment. A receipt is sent to your email." : "Доступ активируется после подтверждения оплаты. Чек придёт на вашу почту."}</p>
              )}
            </form>
          )}
        </article>

      </div>

      <div className="command-metrics">
        <Metric label={language === "en" ? "Checks" : "Проверок"} value={dashboard.stats.totalChecks} />
        <Metric label={language === "en" ? "Today" : "Сегодня"} value={dashboard.stats.todayChecks} />
        <Metric label={language === "en" ? "Active" : "Активные"} value={dashboard.stats.activeSessions} />
        <Metric label={language === "en" ? "Moderators" : "Модераторы"} value={dashboard.stats.moderators} />
      </div>

      <article className="command-card owner-recent-checks-card">
        <div className="command-card-head">
          <div>
            <h3>{language === "en" ? "Latest checks" : "Последние проверки"}</h3>
          </div>
        </div>
        <CheckTable rows={rows.slice(0, 5)} language={language} compact />
      </article>
    </div>
  );
}

function GamePicker({ value, onChange, disabled }: { value: PurchaseForm["game"]; onChange: (game: PurchaseForm["game"]) => void; disabled: boolean }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function closeOnOutside(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, []);

  return <div className="game-picker" ref={rootRef}>
    <button className="game-picker-trigger" type="button" disabled={disabled} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
      <span>{value}</span><ChevronDown size={17} aria-hidden="true" />
    </button>
    {open ? <div className="game-picker-menu" role="listbox" aria-label="Игра">
      {GAME_OPTIONS.map((game) => <button key={game} type="button" role="option" aria-selected={value === game} className={value === game ? "is-selected" : ""} onClick={() => { onChange(game); setOpen(false); }}>{game}</button>)}
    </div> : null}
  </div>;
}

function OwnerChecks({ busy, dashboard, language, createCheck, finishCheck, cancelCheck }: {
  busy: boolean;
  dashboard: SiteDashboard;
  language: Lang;
  createCheck: (payload: Parameters<typeof siteOwnerCreateCheck>[1]) => Promise<SiteModeratorCheckResult | null>;
  finishCheck: (payload: Parameters<typeof siteOwnerFinishCheck>[1]) => Promise<void>;
  cancelCheck: (sessionId: string) => Promise<void>;
}) {
  const [suspectSteamId64, setSuspectSteamId64] = useState("");
  const [oneTimeKey, setOneTimeKey] = useState("");
  const [keyCopied, setKeyCopied] = useState(false);
  const [activeNow, setActiveNow] = useState(() => Date.now());
  const [banSession, setBanSession] = useState<CheckRow | null>(null);
  const [banReason, setBanReason] = useState("review_result");
  const [banCustomReason, setBanCustomReason] = useState("");
  const [banDuration, setBanDuration] = useState<ModeratorBanDuration>("forever");
  const [banCustomDays, setBanCustomDays] = useState("");
  const steamError = steamId64Error(suspectSteamId64, language);
  const rows = getCheckRows(dashboard, language);
  const activeRows = rows.filter((row) => row.status !== "completed" && row.status !== "cancelled");
  const copy = language === "en" ? {
    checks: "Checks", banTitle: "Finish check: ban", reason: "Reason", term: "Duration", cancel: "Cancel", finish: "Finish",
  } : {
    checks: "Проверки", banTitle: "Завершить проверку: бан", reason: "Причина", term: "Срок", cancel: "Отмена", finish: "Завершить",
  };

  useEffect(() => {
    const timer = window.setInterval(() => setActiveNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  async function submitSteamCheck(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (steamError || !suspectSteamId64) return;
    const result = await createCheck({ mode: "steam", suspectSteamId64 });
    if (result?.mode === "steam") setSuspectSteamId64("");
  }

  async function generateOneTimeKey() {
    const result = await createCheck({ mode: "key" });
    if (result?.oneTimeKey) {
      setOneTimeKey(result.oneTimeKey);
      setKeyCopied(false);
    }
  }

  async function copyOneTimeKey() {
    if (!oneTimeKey || !navigator.clipboard) return;
    await navigator.clipboard.writeText(oneTimeKey);
    setKeyCopied(true);
    window.setTimeout(() => setKeyCopied(false), 1800);
  }

  async function finishClean(sessionId: string) {
    await finishCheck({ sessionId, verdict: "clean" });
    setOneTimeKey("");
  }

  async function cancelActive(sessionId: string) {
    await cancelCheck(sessionId);
    setOneTimeKey("");
  }

  async function confirmBan() {
    const reason = moderatorBanReason(banReason, banCustomReason, language);
    const banDays = Number.parseInt(banCustomDays, 10);
    if (!banSession || !reason || (banDuration === "custom" && (!Number.isFinite(banDays) || banDays < 1))) return;
    await finishCheck({ sessionId: banSession.id, verdict: "ban", reason, banDuration, ...(banDuration === "custom" ? { banDays } : {}) });
    setOneTimeKey("");
    setBanSession(null);
  }

  return <div className="command-section moderator-checks-section owner-checks-section">
    <div className="command-section-title"><div><h2>{language === "en" ? "Start a check" : "Начать проверку"}</h2></div></div>
    <div className="moderator-checks-grid">
      <article className="command-card moderator-start-card">
        <h3>{language === "en" ? "Start a player check" : "Начать проверку игрока"}</h3>
        <form className="moderator-start-form" onSubmit={submitSteamCheck}>
          <label><span className="sr-only">SteamID64</span><input value={suspectSteamId64} onChange={(event) => setSuspectSteamId64(normalizeSteamId64(event.target.value))} placeholder={language === "en" ? "Enter Steam ID" : "Введите Steam ID"} inputMode="numeric" maxLength={17} disabled={busy} required aria-invalid={Boolean(steamError)} aria-label="SteamID64" /></label>
          <button className="command-button" type="submit" disabled={busy || !suspectSteamId64 || Boolean(steamError)}><ShieldCheck size={18} />{language === "en" ? "Start check" : "Начать проверку"}</button>
          <div className="moderator-key-option"><span>{language === "en" ? "or" : "или"}</span><button className="moderator-key-link" type="button" disabled={busy} onClick={() => void generateOneTimeKey()}><KeyRound size={16} />{language === "en" ? "Generate one-time key" : "Сгенерировать случайный ключ"}</button></div>
        </form>
        {steamError ? <p className="field-error">{steamError}</p> : null}
        {oneTimeKey ? <div className="command-key-box owner-check-key"><p>{language === "en" ? "Give this key to the player. It is shown only for this active check." : "Передайте ключ игроку. Он показан только для этой активной проверки."}</p><code>{oneTimeKey}</code><button className="mini-action" type="button" onClick={() => void copyOneTimeKey()}>{keyCopied ? <CheckCircle2 size={15} /> : <Copy size={15} />}{keyCopied ? (language === "en" ? "Copied" : "Скопировано") : (language === "en" ? "Copy key" : "Скопировать ключ")}</button></div> : null}
      </article>
      <article className="command-card moderator-active-card">
        <div className="command-card-head"><div><h3>{language === "en" ? "Active checks" : "Активные проверки"}</h3><p>{language === "en" ? "All active sessions of this project are shown." : "Показаны все активные сессии этого проекта."}</p></div></div>
        {activeRows.length ? <div className="moderator-active-table"><div className="moderator-active-head"><span>{language === "en" ? "Player" : "Игрок"}</span><span>Steam ID</span><span>{language === "en" ? "Started" : "Начало"}</span><span>{language === "en" ? "Duration" : "Длительность"}</span><span>{language === "en" ? "Actions" : "Действия"}</span></div>{activeRows.map((row) => <div className="moderator-active-row" key={row.id}><span className="moderator-player"><strong>{row.target}</strong></span><span>{row.steamId}</span><span>{row.startedAt}</span><span>{formatActiveDuration(row.startedAt, activeNow, row.duration)}</span><span className="moderator-row-actions"><button className="mini-action is-clean" type="button" disabled={busy} onClick={() => void finishClean(row.id)}>{language === "en" ? "Clean" : "Чист"}</button><button className="mini-action is-danger" type="button" disabled={busy} onClick={() => { setBanSession(row); setBanReason("review_result"); setBanCustomReason(""); setBanDuration("forever"); setBanCustomDays(""); }}>{language === "en" ? "Ban" : "Бан"}</button><button className="mini-action" type="button" disabled={busy} onClick={() => void cancelActive(row.id)}>{language === "en" ? "Cancel" : "Отмена"}</button></span></div>)}</div> : <p className="muted-line">{language === "en" ? "No active checks." : "Активных проверок нет."}</p>}
      </article>
    </div>
    {banSession ? <ModeratorBanDialog copy={copy} busy={busy} reason={banReason} setReason={setBanReason} customReason={banCustomReason} setCustomReason={setBanCustomReason} duration={banDuration} setDuration={setBanDuration} customDays={banCustomDays} setCustomDays={setBanCustomDays} onCancel={() => setBanSession(null)} onConfirm={confirmBan} /> : null}
  </div>;
}

function CommandModerators({
  busy,
  dashboard,
  language,
  moderatorForm,
  moderatorSteamError,
  updateModeratorField,
  createModerator,
  renameModerator,
  removeModerator,
  getModeratorStats,
}: {
  busy: boolean;
  dashboard: SiteDashboard;
  language: Lang;
  moderatorForm: ModeratorForm;
  moderatorSteamError: string;
  updateModeratorField: (field: keyof ModeratorForm, value: string) => void;
  createModerator: (event: FormEvent<HTMLFormElement>) => void;
  renameModerator: (moderatorId: string, currentName: string) => void;
  removeModerator: (moderatorId: string, currentName: string) => void;
  getModeratorStats: (moderatorId: string) => Promise<SiteOwnerModeratorStats>;
}) {
  const [statsModerator, setStatsModerator] = useState<SiteDashboard["moderators"][number] | null>(null);
  const moderatorLimit = dashboard.project?.maxModerators ?? 5;
  const moderatorCount = dashboard.moderators.length;
  const moderatorLimitReached = moderatorCount >= moderatorLimit;
  return (
    <div className="command-section">
      <div className="command-section-title">
        <div>
          <h2>{language === "en" ? "Moderators" : "Модераторы"}</h2>
        </div>
      </div>

      <div className="command-grid is-moderators">
        <article className="command-card">
          <div className="command-card-head">
            <div>
              <h3>{language === "en" ? "Add moderator" : "Добавить модератора"}</h3>
              <p className="muted-line">{language === "en" ? `${moderatorCount} of ${moderatorLimit} moderator slots used` : `Использовано мест: ${moderatorCount} из ${moderatorLimit}`}</p>
            </div>
          </div>
          <form className="command-form" onSubmit={createModerator}>
            <label>
              <span>{language === "en" ? "Name" : "Имя"}</span>
              <input
                value={moderatorForm.name}
                onChange={(event) => updateModeratorField("name", event.target.value)}
                disabled={busy}
                placeholder={language === "en" ? "Nickname or name" : "Ник или имя"}
                maxLength={MAX_MODERATOR_NAME_LENGTH}
              />
            </label>
            <label>
              <span>SteamID</span>
              <input
                value={moderatorForm.steamId64}
                onChange={(event) => updateModeratorField("steamId64", event.target.value)}
                disabled={busy}
                placeholder="7656..."
                inputMode="numeric"
                maxLength={17}
                required
                aria-invalid={Boolean(moderatorSteamError)}
              />
              {moderatorSteamError ? <p className="field-error">{moderatorSteamError}</p> : null}
            </label>
            <button className="command-button" type="submit" disabled={busy || moderatorLimitReached || !moderatorForm.name.trim() || Boolean(moderatorSteamError)}>
              {language === "en" ? "Add" : "Добавить"}
            </button>
            {moderatorLimitReached ? <p className="field-error">{language === "en" ? "The moderator limit for the current plan has been reached." : "Достигнут лимит модераторов по текущему тарифу."}</p> : null}
          </form>
        </article>

        <article className="command-card">
          <div className="command-card-head">
            <div>
              <h3>{language === "en" ? "Moderator list" : "Список модераторов"}</h3>
            </div>
          </div>
          <div className="command-moderator-list">
            {dashboard.moderators.length ? dashboard.moderators.map((moderator) => (
              <div className="command-moderator-row" key={moderator.id}>
                <div className="moderator-person">
                  <div className="moderator-name-line">
                    <strong>{moderator.displayName || moderator.id}</strong>
                    <button
                      className="moderator-rename-action"
                      type="button"
                      disabled={busy}
                      aria-label={language === "en" ? "Rename moderator" : "Переименовать модератора"}
                      title={language === "en" ? "Rename moderator" : "Переименовать модератора"}
                      onClick={() => renameModerator(moderator.id, moderator.displayName || moderator.id)}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      className="moderator-rename-action"
                      type="button"
                      disabled={busy}
                      aria-label={language === "en" ? "Moderator statistics" : "Статистика модератора"}
                      title={language === "en" ? "Moderator statistics" : "Статистика модератора"}
                      onClick={() => setStatsModerator(moderator)}
                    >
                      <BarChart3 size={14} />
                    </button>
                    <button
                      className="moderator-rename-action is-danger"
                      type="button"
                      disabled={busy}
                      aria-label={language === "en" ? "Remove moderator" : "Удалить модератора"}
                      title={language === "en" ? "Remove moderator" : "Удалить модератора"}
                      onClick={() => removeModerator(moderator.id, moderator.displayName || moderator.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <span>{moderator.steamId64 || (language === "en" ? "SteamID not set" : "SteamID не указан")}</span>
                </div>
              </div>
            )) : <p className="muted-line">{language === "en" ? "Add the first moderator." : "Добавьте первого модератора."}</p>}
          </div>
        </article>
      </div>
      {statsModerator ? <ModeratorStatsDialog moderator={statsModerator} language={language} getModeratorStats={getModeratorStats} onClose={() => setStatsModerator(null)} /> : null}
    </div>
  );
}

function ModeratorStatsDialog({ moderator, language, getModeratorStats, onClose }: {
  moderator: SiteDashboard["moderators"][number];
  language: Lang;
  getModeratorStats: (moderatorId: string) => Promise<SiteOwnerModeratorStats>;
  onClose: () => void;
}) {
  const [data, setData] = useState<SiteOwnerModeratorStats | null>(null);
  const [error, setError] = useState("");
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;
    void getModeratorStats(moderator.id)
      .then((result) => { if (!cancelled) setData(result); })
      .catch((reason) => { if (!cancelled) setError(authErrorText(reason, language)); });
    return () => { cancelled = true; };
  }, [getModeratorStats, language, moderator.id]);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const rows = data?.recentChecks || [];
  return <div className="action-modal moderator-stats-modal" role="presentation" onMouseDown={onClose}>
    <section className="moderator-stats-dialog" role="dialog" aria-modal="true" aria-labelledby="moderator-stats-title" onMouseDown={(event) => event.stopPropagation()}>
      <div className="moderator-stats-dialog-head"><div><h2 id="moderator-stats-title">{language === "en" ? "Moderator statistics" : "Статистика модератора"}</h2><p>{data?.moderator.displayName || moderator.displayName || moderator.id}</p></div><button ref={closeButtonRef} className="moderator-rename-action" type="button" aria-label={language === "en" ? "Close" : "Закрыть"} onClick={onClose}><X size={16} /></button></div>
      {!data && !error ? <p className="muted-line">{language === "en" ? "Loading statistics…" : "Загружаем статистику…"}</p> : null}
      {error ? <p className="field-error">{error}</p> : null}
      {data ? <><div className="command-metrics moderator-stats-metrics"><StatTile icon={<ClipboardList size={18} />} label={language === "en" ? "Checks" : "Проверок"} value={data.stats.totalChecks} /><StatTile icon={<ShieldCheck size={18} />} label={language === "en" ? "Bans" : "Банов"} value={data.stats.totalBans} tone="danger" /><StatTile icon={<CalendarDays size={18} />} label={language === "en" ? "Today" : "Сегодня"} value={data.stats.todayChecks} /><StatTile icon={<FileClock size={18} />} label={language === "en" ? "Average" : "Средняя"} value={data.stats.averageDuration || "—"} /></div><div className="moderator-stats-summary"><span>{language === "en" ? "Active" : "Активных"}: <strong>{data.stats.activeSessions}</strong></span><span>{language === "en" ? "Completed" : "Завершено"}: <strong>{data.stats.completedSessions}</strong></span><span>{language === "en" ? "Bans today" : "Банов сегодня"}: <strong>{data.stats.todayBans}</strong></span></div><div className="moderator-stats-recent"><h3>{language === "en" ? "Recent checks" : "Последние проверки"}</h3>{rows.length ? <div className="check-table-wrap"><table className="check-table is-personal"><thead><tr><th>{language === "en" ? "Player" : "Игрок"}</th><th>Steam ID</th><th>{language === "en" ? "Verdict" : "Вердикт"}</th><th>{language === "en" ? "Started" : "Начало"}</th></tr></thead><tbody>{rows.map((row) => <tr key={String(sessionValue(row, ["id"], "—"))}><td>{sessionValue(row, ["suspectName", "targetName", "playerName"], "—")}</td><td>{sessionValue(row, ["suspectSteamId64", "steamId64"], "—")}</td><td>{sessionValue(row, ["verdict", "status"], "—")}</td><td>{sessionValue(row, ["startedAt", "createdAt"], "—")}</td></tr>)}</tbody></table></div> : <p className="muted-line">{language === "en" ? "No checks yet." : "Проверок пока нет."}</p>}</div></> : null}
    </section>
  </div>;
}

function CommandSettings({ busy, dashboard, language, projectForm, updateProjectField, updateProject, steamLinked, startSteamLink, requestProjectDeletion, confirmProjectDeletion, getSecuritySettings, startTwoFactorSetup, confirmTwoFactorSetup, disableTwoFactor, requestRecentPasswordLogin }: {
  busy: boolean;
  dashboard: SiteDashboard;
  language: Lang;
  projectForm: PurchaseForm;
  updateProjectField: (field: keyof PurchaseForm, value: string) => void;
  updateProject: (event: FormEvent<HTMLFormElement>) => void;
  steamLinked: boolean;
  startSteamLink: () => void;
  requestProjectDeletion: () => void;
  confirmProjectDeletion?: () => void;
  getSecuritySettings: () => Promise<SiteSecuritySettings>;
  startTwoFactorSetup: () => Promise<SiteTwoFactorSetup>;
  confirmTwoFactorSetup: (code: string) => Promise<boolean>;
  disableTwoFactor: (code: string) => Promise<boolean>;
  requestRecentPasswordLogin: () => void;
}) {
  const project = dashboard.project;
  const [security, setSecurity] = useState<SiteSecuritySettings | null>(null);
  const [securityLoading, setSecurityLoading] = useState(true);
  const [setup, setSetup] = useState<SiteTwoFactorSetup | null>(null);
  const [code, setCode] = useState("");
  const [securityMessage, setSecurityMessage] = useState("");
  const [securityError, setSecurityError] = useState("");

  useEffect(() => {
    let cancelled = false;
    void getSecuritySettings()
      .then((result) => {
        if (!cancelled) setSecurity(result);
      })
      .catch((error) => {
        if (!cancelled) setSecurityError(twoFactorErrorText(error, language));
      })
      .finally(() => {
        if (!cancelled) setSecurityLoading(false);
      });
    return () => { cancelled = true; };
  }, [getSecuritySettings]);

  async function beginSetup() {
    setSecurityError("");
    setSecurityMessage("");
    try {
      const result = await startTwoFactorSetup();
      setSetup(result);
      setCode("");
      setSecurityMessage("");
    } catch (error) {
      if (error instanceof Error && error.message === "reauthentication_required") requestRecentPasswordLogin();
      setSecurityError(twoFactorErrorText(error, language));
    }
  }

  async function submitTwoFactor(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (code.length !== 6) return;
    setSecurityError("");
    try {
      const enabled = await disableTwoFactor(code);
      if (!enabled) return;
      setCode("");
      const refreshed = await getSecuritySettings();
      setSecurity(refreshed);
      setSecurityMessage(language === "en" ? "Two-factor authentication is disabled." : "Двухфакторная аутентификация выключена.");
    } catch (error) {
      if (error instanceof Error && error.message === "reauthentication_required") requestRecentPasswordLogin();
      setSecurityError(twoFactorErrorText(error, language));
      return;
    }
  }
  async function completeSetup(codeValue: string) {
    const enabled = await confirmTwoFactorSetup(codeValue);
    if (!enabled) throw new Error("two_factor_setup_failed");
    setSetup(null);
    const refreshed = await getSecuritySettings();
    setSecurity(refreshed);
    setSecurityMessage(language === "en" ? "Two-factor authentication is enabled." : "Двухфакторная аутентификация включена.");
  }

  return <>
    <div className="command-section">
    <div className="command-section-title"><div><h2>{language === "en" ? "Settings" : "Настройки"}</h2></div></div>
    <div className="settings-grid">
      <article className="command-card settings-card">
        <div className="settings-card-header"><span className="settings-icon"><UserRound size={30} /></span><div><h3>{language === "en" ? "Project owner" : "Владелец проекта"}</h3><p>{language === "en" ? "This name is shown for the project." : "Это имя отображается в проекте."}</p></div></div>
        {project ? <form className="command-form" onSubmit={updateProject}><label><span>{language === "en" ? "Owner name" : "Имя владельца"}</span><input value={projectForm.ownerDisplayName} onChange={(event) => updateProjectField("ownerDisplayName", event.target.value)} disabled={busy} required /></label><button className="command-button" type="submit" disabled={busy || !projectForm.ownerDisplayName.trim()}><CheckCircle2 size={18} />{language === "en" ? "Save name" : "Сохранить имя"}</button></form> : <p className="muted-line">{language === "en" ? "Create a project to set its owner name." : "Создайте проект, чтобы указать имя владельца."}</p>}
      </article>
      <article className="command-card settings-card">
        <div className="settings-card-header"><span className="settings-icon"><img src="/brand_steam.png" alt="" aria-hidden="true" /></span><div><h3>Steam</h3><p>{steamLinked ? (language === "en" ? "Steam is connected to this account." : "Steam подключён к этому аккаунту.") : (language === "en" ? "Connect Steam to use Steam-based access." : "Подключите Steam для доступа через Steam.")}</p></div></div>
        {!steamLinked ? <button className="command-button" type="button" disabled={busy} onClick={startSteamLink}><img className="button-brand-icon" src="/brand_steam.png" alt="" aria-hidden="true" />{language === "en" ? "Connect Steam" : "Подключить Steam"}</button> : <p className="field-hint">{language === "en" ? "Connected" : "Подключён"}</p>}
      </article>
      <article className="command-card settings-card">
        <div className="settings-card-header"><span className="settings-icon"><LockKeyhole size={30} /></span><div><h3>{language === "en" ? "Two-factor authentication" : "Двухфакторная аутентификация"}</h3><p>{language === "en" ? "Protect password sign-ins with an authenticator app." : "Защитите входы по паролю приложением-аутентификатором."}</p></div></div>
        {securityLoading ? <p className="muted-line">{language === "en" ? "Loading security settings…" : "Загружаем настройки безопасности…"}</p> : security?.twoFactor.enabled ? <>
          <p className="field-hint">{language === "en" ? "Enabled: authenticator app (TOTP)." : "Включено: приложение-аутентификатор (TOTP)."}</p>
          <form className="command-form" onSubmit={submitTwoFactor}><label><span>{language === "en" ? "Authenticator code" : "Код из приложения"}</span><input value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" maxLength={6} disabled={busy} placeholder="000000" /></label><button className="command-button is-danger" type="submit" disabled={busy || code.length !== 6}>{language === "en" ? "Disable 2FA" : "Отключить 2FA"}</button></form>
        </> : <button className="command-button" type="button" disabled={busy || securityLoading} onClick={beginSetup}><LockKeyhole size={18} />{language === "en" ? "Set up 2FA" : "Настроить 2FA"}</button>}
        {securityMessage ? <p className="command-feedback" role="status">{securityMessage}</p> : null}
        {securityError ? <p className="field-error">{securityError}</p> : null}
      </article>
      {project ? <article className="command-card settings-card project-danger-zone"><div className="settings-card-header"><span className="settings-icon is-danger"><Trash2 size={30} /></span><div><h3>{language === "en" ? "Delete project" : "Удалить проект"}</h3><p>{language === "en" ? "We will send a confirmation link to the owner's email." : "Отправим ссылку для подтверждения на почту владельца."}</p></div></div>{confirmProjectDeletion ? <button className="command-button is-danger" type="button" disabled={busy} onClick={confirmProjectDeletion}><Trash2 size={17} />{language === "en" ? "Delete permanently" : "Удалить безвозвратно"}</button> : <button className="command-button is-danger" type="button" disabled={busy} onClick={requestProjectDeletion}><Mail size={17} />{language === "en" ? "Send confirmation email" : "Отправить письмо"}</button>}</article> : null}
    </div>
    </div>
    {setup ? <TwoFactorSetupDialog setup={setup} language={language} busy={busy} onClose={() => { if (!busy) setSetup(null); }} onConfirm={completeSetup} /> : null}
  </>;
}

function CommandStats({
  dashboard,
  rows,
  stats,
  language,
}: {
  dashboard: SiteDashboard;
  rows: CheckRow[];
  stats: ReturnType<typeof getDashboardStats>;
  language: Lang;
}) {
  const [dailyMode, setDailyMode] = useState<"7" | "30">("7");
  const verdictTotal = Math.max(1, stats.total);
  const banPercent = Math.round((stats.bans / verdictTotal) * 100);
  const cleanPercent = Math.max(0, 100 - banPercent);
  const dailyBars = getDailyBars(rows, dailyMode === "7" ? 7 : 30);

  return (
    <div className="command-section">
      <div className="command-section-title">
        <div>
          <h2>{language === "en" ? "Check statistics" : "Статистика проверок"}</h2>
        </div>
      </div>

      <div className="command-metrics">
        <StatTile icon={<ClipboardList size={18} />} label={language === "en" ? "All checks" : "Всего проверок"} value={stats.total} />
        <StatTile icon={<ShieldCheck size={18} />} label={language === "en" ? "Bans" : "Банов"} value={stats.bans} tone="danger" />
        <StatTile icon={<CheckCircle2 size={18} />} label={language === "en" ? "Clean" : "Чистых"} value={stats.clean} />
        <StatTile icon={<FileClock size={18} />} label={language === "en" ? "Avg. duration" : "Средняя длит."} value={dashboard.stats.averageDuration || "—"} />
      </div>

      <article className="command-card chart-card daily-chart-card">
        <div className="chart-title-row">
          <h3>{language === "en" ? "Checks by day" : "Проверки по дням"}</h3>
          <div className="chart-switch" role="group" aria-label={language === "en" ? "Chart period" : "Период графика"}>
            <button className={dailyMode === "7" ? "is-active" : ""} type="button" onClick={() => setDailyMode("7")}>7</button>
            <button className={dailyMode === "30" ? "is-active" : ""} type="button" onClick={() => setDailyMode("30")}>30</button>
          </div>
        </div>
        <DailyChecksChart bars={dailyBars} language={language} />
      </article>

      <div className="command-grid is-stats">
        <article className="command-card chart-card">
          <h3>{language === "en" ? "Verdicts" : "Вердикты"}</h3>
          <div
            className="verdict-donut"
            style={{ background: `radial-gradient(circle at center, rgba(12, 20, 34, 1) 0 56%, transparent 57%), conic-gradient(#ff6d88 0 ${banPercent}%, #62b1ff ${banPercent}% 100%)` }}
          >
            <strong>{stats.total}</strong>
            <span>{language === "en" ? "checks" : "проверок"}</span>
          </div>
          <div className="chart-legend">
            <span><i className="is-ban" />{language === "en" ? "Ban" : "Бан"} {banPercent}%</span>
            <span><i />{language === "en" ? "Clean" : "Чист"} {cleanPercent}%</span>
          </div>
        </article>

        <article className="command-card chart-card">
          <h3>{language === "en" ? "Activity by time" : "Активность по времени"}</h3>
          <div className="activity-bars">
            {stats.timeBars.map((item) => (
              <div className="activity-bar" key={item.label}>
                <span style={{ height: `${item.percent}%` }} />
                <em>{item.label}</em>
              </div>
            ))}
          </div>
        </article>

        <article className="command-card chart-card">
          <h3>{language === "en" ? "Moderators" : "Модераторы"}</h3>
          <div className="rank-bars">
            {stats.moderatorBars.map((item) => (
              <div className="rank-row" key={item.label}>
                <span>{item.label}</span>
                <i><b style={{ width: `${item.percent}%` }} /></i>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
        </article>

        <article className="command-card chart-card">
          <h3>{language === "en" ? "Top reasons" : "Топ-причины"}</h3>
          <div className="reason-list">
            {stats.reasonBars.length ? stats.reasonBars.map((item) => (
              <div key={item.label}>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            )) : <p className="muted-line">{language === "en" ? "No bans with a reason yet." : "Пока нет банов с причиной."}</p>}
          </div>
        </article>
      </div>

      {!rows.length ? <p className="muted-line">{language === "en" ? "Statistics will become richer after the first sessions." : "Статистика станет подробнее после первых сессий."}</p> : null}
    </div>
  );
}

function CommandHistory({
  rows,
  language,
  search,
  setSearch,
  verdict,
  setVerdict,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
}: {
  rows: CheckRow[];
  language: Lang;
  search: string;
  setSearch: (value: string) => void;
  verdict: VerdictFilter;
  setVerdict: (value: VerdictFilter) => void;
  dateFrom: string;
  setDateFrom: (value: string) => void;
  dateTo: string;
  setDateTo: (value: string) => void;
}) {
  return (
    <div className="command-section">
      <div className="command-section-title">
        <div>
          <h2>{language === "en" ? "Check history" : "История проверок"}</h2>
        </div>
      </div>

      <article className="command-card history-card">
        <div className="history-filters">
          <label className="date-control">
            <span>{language === "en" ? "From" : "От"}</span>
            <div>
              <CalendarDays size={17} />
              <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
            </div>
          </label>
          <label className="date-control">
            <span>{language === "en" ? "To" : "До"}</span>
            <div>
              <CalendarDays size={17} />
              <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
            </div>
          </label>
          <div className="verdict-switch" role="group" aria-label={language === "en" ? "Verdict" : "Вердикт"}>
            {(["all", "clean", "ban"] as VerdictFilter[]).map((item) => (
              <button key={item} className={verdict === item ? "is-active" : ""} type="button" onClick={() => setVerdict(item)}>
                {item === "all" ? language === "en" ? "All" : "Все" : item === "clean" ? language === "en" ? "Clean" : "Чист" : language === "en" ? "Ban" : "Бан"}
              </button>
            ))}
          </div>
          <label className="search-control">
            <span>{language === "en" ? "Search" : "Поиск"}</span>
            <div>
              <Search size={17} />
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={language === "en" ? "Name, SteamID, moderator" : "Ник, SteamID, модератор"} />
            </div>
          </label>
          <button className="command-button is-muted" type="button" onClick={() => { setSearch(""); setVerdict("all"); setDateFrom(""); setDateTo(""); }}>
            <X size={17} />
            {language === "en" ? "Reset" : "Сбросить"}
          </button>
        </div>

        <CheckTable rows={rows} language={language} />
      </article>
    </div>
  );
}

function CheckTable({ rows, language, compact = false, hideModerator = false }: { rows: CheckRow[]; language: Lang; compact?: boolean; hideModerator?: boolean }) {
  if (!rows.length) {
    return <p className="muted-line">{language === "en" ? "No checks yet." : "Проверок пока нет."}</p>;
  }

  return (
    <div className="check-table-wrap">
      <table className={`check-table${hideModerator ? " is-personal" : ""}`}>
        <thead>
          <tr>
            <th>{language === "en" ? "Player" : "Кого проверяли"}</th>
            <th>Steam ID</th>
            <th>{language === "en" ? "Moderator" : "Кто проверял"}</th>
            <th>{language === "en" ? "Start" : "Начало (МСК)"}</th>
            {!compact ? <th>{language === "en" ? "End" : "Конец (МСК)"}</th> : null}
            <th>{language === "en" ? "Duration" : "Длит."}</th>
            <th>{language === "en" ? "Verdict" : "Вердикт"}</th>
            {!compact ? <th>{language === "en" ? "Reason" : "Причина"}</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{row.target}</td>
              <td>{row.steamId}</td>
              <td>{row.moderator}</td>
              <td>{row.startedAt}</td>
              {!compact ? <td>{row.endedAt}</td> : null}
              <td>{row.duration}</td>
              <td><span className={`verdict-label is-${row.verdict}`}>{row.verdictText}</span></td>
              {!compact ? <td>{row.reason}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatTile({
  icon,
  label,
  value,
  tone = "default",
}: {
  icon: ReactNode;
  label: string;
  value: string | number;
  tone?: "default" | "danger";
}) {
  return (
    <div className={`metric-card stat-tile is-${tone}`}>
      <div className="stat-icon">{icon}</div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function DailyChecksChart({
  bars,
  language,
}: {
  bars: Array<{ label: string; total: number; bans: number; totalPercent: number; banPercent: number }>;
  language: Lang;
}) {
  const width = 720;
  const height = 250;
  const padX = 42;
  const padY = 28;
  const chartWidth = width - padX * 2;
  const chartHeight = height - padY * 2;
  const maxValue = Math.max(1, ...bars.flatMap((bar) => [bar.total, bar.bans]));
  const labelEvery = bars.length > 14 ? 5 : bars.length > 8 ? 2 : 1;

  function point(value: number, index: number) {
    const x = bars.length <= 1 ? width / 2 : padX + (index / (bars.length - 1)) * chartWidth;
    const y = padY + (1 - value / maxValue) * chartHeight;
    return { x, y };
  }

  const totalPoints = bars.map((bar, index) => point(bar.total, index));
  const banPoints = bars.map((bar, index) => point(bar.bans, index));
  const toPolyline = (points: Array<{ x: number; y: number }>) =>
    points.map(({ x, y }) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");

  return (
    <div className="daily-chart">
      <svg
        className="daily-line-chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={language === "en" ? "Checks and bans by day" : "Проверки и баны по дням"}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((step) => {
          const y = padY + step * chartHeight;
          const value = maxValue < 4
            ? Number(((1 - step) * maxValue).toFixed(1))
            : Math.round((1 - step) * maxValue);
          return (
            <g key={step}>
              <line className="line-grid" x1={padX} x2={width - padX} y1={y} y2={y} />
              <text className="line-axis-label" x={padX - 10} y={y + 4} textAnchor="end">
                {value}
              </text>
            </g>
          );
        })}
        <polyline className="line-series is-total" points={toPolyline(totalPoints)} />
        <polyline className="line-series is-bans" points={toPolyline(banPoints)} />
        {totalPoints.map((dot, index) => (
          <circle className="line-point is-total" cx={dot.x} cy={dot.y} r="4.5" key={`total-${bars[index].label}`}>
            <title>{`${bars[index].label}: ${bars[index].total}`}</title>
          </circle>
        ))}
        {banPoints.map((dot, index) => (
          <circle className="line-point is-bans" cx={dot.x} cy={dot.y} r="4.5" key={`ban-${bars[index].label}`}>
            <title>{`${bars[index].label}: ${bars[index].bans}`}</title>
          </circle>
        ))}
        {bars.map((bar, index) => {
          if (index % labelEvery !== 0 && index !== bars.length - 1) {
            return null;
          }
          const dot = point(0, index);
          return (
            <text className="line-x-label" x={dot.x} y={height - 8} textAnchor="middle" key={`label-${bar.label}`}>
              {bar.label}
            </text>
          );
        })}
      </svg>
      <div className="chart-legend">
        <span><i />{language === "en" ? "Checks" : "Проверки"}</span>
        <span><i className="is-ban" />{language === "en" ? "Bans" : "Баны"}</span>
        <strong>{language === "en" ? "Max" : "Макс."}: {maxValue}</strong>
      </div>
    </div>
  );
}

function formatActiveDuration(startedAt: string, now: number, fallback: string) {
  const match = startedAt.match(/(\d{2})\.(\d{2})\.(\d{4})[^\d]+(\d{2}):(\d{2}):(\d{2})/);
  if (!match) return fallback;
  const [, day, month, year, hours, minutes, seconds] = match;
  const started = Date.parse(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}+03:00`);
  if (!Number.isFinite(started) || started > now) return fallback;
  const totalSeconds = Math.floor((now - started) / 1000);
  const parts = [Math.floor(totalSeconds / 3600), Math.floor((totalSeconds % 3600) / 60), totalSeconds % 60]
    .map((part) => String(part).padStart(2, "0"));
  return parts.join(":");
}

function getCheckRows(dashboard: Pick<SiteDashboard, "activeSessions" | "completedSessions">, language: Lang): CheckRow[] {
  const sessions = [...dashboard.activeSessions, ...dashboard.completedSessions];
  return sessions.map((session, index) => {
    const startedAt = sessionValue(session, ["startedAt", "startAt", "createdAt", "started", "startTime", "beginAt", "start"], "—");
    const endedAt = sessionValue(session, ["endedAt", "finishedAt", "completedAt", "endAt", "endTime", "finish"], "—");
    const verdict = normalizeVerdict(session);
    return {
      id: sessionValue(session, ["id", "sessionId", "checkId"], `check-${index}`),
      status: sessionValue(session, ["status", "state"], ""),
      target: sessionValue(session, ["suspectName", "targetName", "playerName", "player", "subjectName", "key"], language === "en" ? "Check key" : "Ключ проверки"),
      steamId: sessionValue(session, ["suspectSteamId64", "steamId64", "steamId", "targetSteamId64"], "—"),
      moderator: sessionValue(session, ["moderatorName", "moderator", "checkedBy", "operatorName"], "—"),
      startedAt,
      endedAt,
      duration: sessionValue(session, ["duration", "durationText", "elapsed", "elapsedText"], "—"),
      verdict,
      verdictText: verdict === "ban" ? language === "en" ? "Ban" : "Бан" : verdict === "clean" ? language === "en" ? "Clean" : "Чист" : "—",
      reason: sessionValue(session, ["reason", "verdictReason", "banReason", "comment"], "—"),
      dateInput: dateInputFromText(startedAt),
    };
  });
}

function sessionValue(session: Record<string, string | string[]>, keys: string[], fallback: string) {
  for (const key of keys) {
    const value = session[key];
    if (Array.isArray(value) && value.length) {
      return value.join(", ");
    }
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }
  return fallback;
}

function normalizeVerdict(session: Record<string, string | string[]>): VerdictKind {
  const raw = sessionValue(session, ["verdictLabel", "verdict", "result", "decision", "status"], "").toLowerCase();
  if (raw.includes("ban") || raw.includes("бан")) {
    return "ban";
  }
  if (raw.includes("clean") || raw.includes("чист")) {
    return "clean";
  }
  return "unknown";
}

function dateInputFromText(value: string) {
  const ruDate = value.match(/(\d{2})\.(\d{2})\.(\d{4})/);
  if (ruDate) {
    return `${ruDate[3]}-${ruDate[2]}-${ruDate[1]}`;
  }
  const isoDate = value.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) {
    return isoDate[0];
  }
  return "";
}

function filterCheckRow(row: CheckRow, search: string, verdict: VerdictFilter, dateFrom: string, dateTo: string) {
  const query = search.trim().toLowerCase();
  if (query && !`${row.target} ${row.steamId} ${row.moderator} ${row.reason}`.toLowerCase().includes(query)) {
    return false;
  }
  if (verdict !== "all" && row.verdict !== verdict) {
    return false;
  }
  if (dateFrom && row.dateInput && row.dateInput < dateFrom) {
    return false;
  }
  if (dateTo && row.dateInput && row.dateInput > dateTo) {
    return false;
  }
  return true;
}

function getDashboardStats(dashboard: SiteDashboard, rows: CheckRow[]) {
  const total = dashboard.stats.totalChecks || rows.length;
  const bans = dashboard.stats.totalBans || rows.filter((row) => row.verdict === "ban").length;
  const clean = Math.max(0, total - bans);
  const hourCounts = new Map<string, number>();
  rows.forEach((row) => {
    const match = row.startedAt.match(/\b(\d{2}):\d{2}\b/);
    if (match) {
      hourCounts.set(match[1], (hourCounts.get(match[1]) || 0) + 1);
    }
  });
  const timeValues = hourCounts.size
    ? Array.from(hourCounts.entries()).sort(([left], [right]) => left.localeCompare(right)).slice(0, 8).map(([label, value]) => ({ label, value }))
    : ["00", "04", "08", "12", "16", "20"].map((label) => ({ label, value: 0 }));
  const maxTime = Math.max(1, ...timeValues.map((item) => item.value), dashboard.stats.todayChecks || 0);
  const moderatorCounts = new Map<string, number>();
  rows.forEach((row) => moderatorCounts.set(row.moderator, (moderatorCounts.get(row.moderator) || 0) + 1));
  if (!moderatorCounts.size) {
    dashboard.moderators.forEach((moderator) => moderatorCounts.set(moderator.displayName || moderator.id, 0));
  }
  const maxModerator = Math.max(1, ...Array.from(moderatorCounts.values()));
  const reasonCounts = new Map<string, number>();
  rows.forEach((row) => {
    const reason = row.reason.trim();
    if (row.verdict !== "ban" || !reason || reason === "—" || reason === "вЂ”") {
      return;
    }
    reasonCounts.set(reason, (reasonCounts.get(reason) || 0) + 1);
  });

  return {
    total,
    bans,
    clean,
    timeBars: timeValues.map((item) => ({ ...item, percent: Math.max(8, Math.round((item.value / maxTime) * 100)) })),
    moderatorBars: Array.from(moderatorCounts.entries()).slice(0, 5).map(([label, value]) => ({ label, value, percent: Math.max(4, Math.round((value / maxModerator) * 100)) })),
    reasonBars: Array.from(reasonCounts.entries()).sort(([, left], [, right]) => right - left).slice(0, 4).map(([label, value]) => ({ label, value })),
  };
}

function AccountDashboard({
  currentUser,
  dashboard,
  busy,
  error,
  purchaseKey,
  showOwnerKey,
  setShowOwnerKey,
  purchaseForm,
  projectForm,
  moderatorForm,
  steamEmail,
  steamEmailSent,
  setSteamEmail,
  updatePurchaseField,
  updateProjectField,
  updateModeratorField,
  attachSteamEmail,
  resendVerification,
  startSteamLink,
  createTestPurchase,
  updateProject,
  createModerator,
  renameModerator,
  removeModerator,
  refreshDashboard,
  logout,
  language,
}: {
  currentUser: SiteUser | null;
  dashboard: SiteDashboard | null;
  busy: boolean;
  error: string;
  purchaseKey: string;
  showOwnerKey: boolean;
  setShowOwnerKey: (value: boolean) => void;
  purchaseForm: PurchaseForm;
  projectForm: PurchaseForm;
  moderatorForm: ModeratorForm;
  steamEmail: string;
  steamEmailSent: boolean;
  setSteamEmail: (value: string) => void;
  updatePurchaseField: (field: keyof PurchaseForm, value: string) => void;
  updateProjectField: (field: keyof PurchaseForm, value: string) => void;
  updateModeratorField: (field: keyof ModeratorForm, value: string) => void;
  attachSteamEmail: (event: FormEvent<HTMLFormElement>) => void;
  resendVerification: () => void;
  startSteamLink: () => void;
  createTestPurchase: (event: FormEvent<HTMLFormElement>) => void;
  updateProject: (event: FormEvent<HTMLFormElement>) => void;
  createModerator: (event: FormEvent<HTMLFormElement>) => void;
  renameModerator: (moderatorId: string, currentName: string) => void;
  removeModerator: (moderatorId: string, currentName: string) => void;
  refreshDashboard: () => void;
  logout: () => void;
  language: Lang;
}) {
  const copy = siteCopy[language];
  const account = dashboard?.account || currentUser;
  const verified = account?.emailVerified;
  const project = dashboard?.project;
  const ownerKey = purchaseKey || dashboard?.ownerKey?.key || "";
  const keyDisplay = ownerKey
    ? showOwnerKey ? ownerKey : `${ownerKey.slice(0, 15)}...${ownerKey.slice(-4)}`
    : dashboard?.ownerKey?.displayPrefix || (language === "en" ? "Key is not created yet" : "Ключ ещё не создан");
  const checks = dashboard ? [...dashboard.activeSessions, ...dashboard.completedSessions].slice(0, 8) : [];
  const moderatorSteamError = steamId64Error(moderatorForm.steamId64, language);
  const steamLinked = Boolean(account?.steamId64);
  const needsSteamEmail = account?.authProvider === "steam" && !account?.email;
  const ownerSteamBinding = account?.steamId64 || (language === "en" ? "not set" : "не указан");
  const [keyCopied, setKeyCopied] = useState(false);

  async function copyKey() {
    if (ownerKey) {
      await navigator.clipboard.writeText(ownerKey);
      setKeyCopied(true);
      window.setTimeout(() => setKeyCopied(false), 1800);
    }
  }

  const accountHeader = (
    <div className="account-hero">
      <div>
        <p className="account-kicker">{account?.name || account?.email || "CheatChecker"}</p>
        <h1>{language === "en" ? "Account" : "Личный кабинет"}</h1>
        <div className="account-badges" aria-label={language === "en" ? "Account status" : "Состояние аккаунта"}>
          <span className={verified ? "status-pill is-good" : "status-pill is-warn"}>
            {verified ? language === "en" ? "Email confirmed" : "Почта подтверждена" : language === "en" ? "Confirm email" : "Подтвердите почту"}
          </span>
          <span className={project ? "status-pill is-good" : "status-pill"}>
            {project
              ? language === "en" ? "Project ready" : "Проект готов"
              : dashboard ? language === "en" ? "No project" : "Проект не создан" : language === "en" ? "Project loading" : "Проект загружается"}
          </span>
          <span className={ownerKey ? "status-pill is-good" : "status-pill"}>
            {ownerKey
              ? language === "en" ? "Key active" : "Ключ активен"
              : dashboard ? language === "en" ? "No key" : "Ключ не создан" : language === "en" ? "Key loading" : "Ключ загружается"}
          </span>
          <span className={steamLinked ? "status-pill is-good" : "status-pill"}>
            {steamLinked ? language === "en" ? "Steam linked" : "Steam подключён" : language === "en" ? "Steam not linked" : "Steam не подключён"}
          </span>
        </div>
      </div>
      <div className="account-actions">
        {!steamLinked ? (
          <button className="panel-action is-secondary" type="button" disabled={busy} onClick={startSteamLink}>
            {language === "en" ? "Link Steam" : "Привязать Steam"}
          </button>
        ) : null}
        <button className="panel-action is-secondary" type="button" disabled={busy} onClick={refreshDashboard}>
          {language === "en" ? "Refresh" : "Обновить"}
        </button>
        <button className="panel-action is-ghost" type="button" onClick={logout}>
          {language === "en" ? "Sign out" : "Выйти"}
        </button>
      </div>
    </div>
  );

  return (
    <section className="account-workspace" id="account">
      {accountHeader}

      {error ? <p className="auth-note is-error">{error}</p> : null}

      {!dashboard ? (
        <div className="dashboard-skeleton-grid" aria-hidden="true">
          <div />
          <div />
          <div />
        </div>
      ) : null}

      {dashboard && needsSteamEmail ? (
        <article className="dashboard-card account-notice steam-email-card">
          <div>
            <h3>{language === "en" ? "Add email" : "Добавьте email"}</h3>
            <p>{language === "en" ? "Steam does not share email. Add one and confirm it to create a project." : "Steam не передаёт почту. Укажите email и подтвердите письмо, чтобы создать проект."}</p>
          </div>
          <form className="steam-email-form" onSubmit={attachSteamEmail}>
            <label>
              <span>Email</span>
              <input
                value={steamEmail}
                onChange={(event) => setSteamEmail(event.target.value)}
                disabled={busy}
                placeholder="you@example.com"
                type="email"
                autoComplete="email"
              />
            </label>
            <button className="panel-action is-secondary" type="submit" disabled={busy || !steamEmail.trim()}>
              {language === "en" ? "Send email" : "Отправить письмо"}
            </button>
            {steamEmailSent ? <p className="field-hint">{language === "en" ? "Email sent. After confirmation, refresh the account." : "Письмо отправлено. После подтверждения обновите кабинет."}</p> : null}
          </form>
        </article>
      ) : dashboard && !verified ? (
        <article className="dashboard-card account-notice">
          <div>
            <h3>{language === "en" ? "Confirm email" : "Подтвердите почту"}</h3>
            <p>{language === "en" ? "After confirmation, you can create a project and issue access." : "После подтверждения можно создать проект и выдать доступы."}</p>
          </div>
          <button className="panel-action is-secondary" type="button" disabled={busy} onClick={resendVerification}>
            {language === "en" ? "Send email" : "Отправить письмо"}
          </button>
        </article>
      ) : null}

      {dashboard ? <div className="dashboard-grid dashboard-grid-main">
        <article className="dashboard-card project-card">
          <div className="card-title-row">
            <div>
              <h3>{language === "en" ? "Project" : "Проект"}</h3>
            </div>
            {project ? <span className="status-pill is-good">{project.game}</span> : null}
          </div>
          {project ? (
            <>
              <form className="stack-form" onSubmit={updateProject}>
                <div className="form-grid">
                  <label>
                    <span>{language === "en" ? "Name" : "Название"}</span>
                    <input
                      value={projectForm.displayName}
                      onChange={(event) => updateProjectField("displayName", event.target.value)}
                      disabled={busy}
                      placeholder={language === "en" ? "Project name" : "Название проекта"}
                    />
                  </label>
                  <label>
                    <span>{language === "en" ? "Game" : "Игра"}</span>
                    <select value={projectForm.game} onChange={(event) => updateProjectField("game", event.target.value)} disabled={busy}>
                      {GAME_OPTIONS.map((game) => <option key={game} value={game}>{game}</option>)}
                    </select>
                  </label>
                  <label>
                    <span>{language === "en" ? "Owner" : "Владелец"}</span>
                    <input
                      value={projectForm.ownerDisplayName}
                      onChange={(event) => updateProjectField("ownerDisplayName", event.target.value)}
                      disabled={busy}
                      placeholder={language === "en" ? "Owner name" : "Имя владельца"}
                    />
                  </label>
                </div>
                <button className="panel-action" type="submit" disabled={busy || !projectForm.displayName.trim() || !projectForm.ownerDisplayName.trim()}>
                  {language === "en" ? "Save" : "Сохранить"}
                </button>
              </form>
            </>
          ) : (
            <form className="stack-form" onSubmit={createTestPurchase}>
              <div className="form-grid">
                <label>
                  <span>{language === "en" ? "Name" : "Название"}</span>
                  <input
                    value={purchaseForm.displayName}
                    onChange={(event) => updatePurchaseField("displayName", event.target.value)}
                    disabled={busy || !verified}
                    placeholder="Project"
                  />
                </label>
                <label>
                  <span>{language === "en" ? "Game" : "Игра"}</span>
                  <GamePicker value={purchaseForm.game} onChange={(game) => updatePurchaseField("game", game)} disabled={busy || !verified} />
                </label>
                <label>
                  <span>{language === "en" ? "Owner" : "Владелец"}</span>
                  <input
                    value={purchaseForm.ownerDisplayName}
                    onChange={(event) => updatePurchaseField("ownerDisplayName", event.target.value)}
                    disabled={busy || !verified}
                    placeholder="Owner"
                  />
                </label>
              </div>
              <button className="panel-action" type="submit" disabled={busy || !verified || !purchaseForm.displayName.trim() || !purchaseForm.ownerDisplayName.trim()}>
                {language === "en" ? "Create project" : "Создать проект"}
              </button>
            </form>
          )}
        </article>

        <article className="dashboard-card key-card">
          <div className="card-title-row">
            <div>
              <h3>{language === "en" ? "Key" : "Ключ"}</h3>
              <p>{ownerKey ? language === "en" ? "Project owner key." : "Ключ владельца проекта." : language === "en" ? "Appears after project creation." : "Появится после создания проекта."}</p>
            </div>
          </div>
          <div className="key-box">
            <code>{keyDisplay}</code>
            <div className="key-actions">
              <button className="mini-action" type="button" disabled={!ownerKey} onClick={() => setShowOwnerKey(!showOwnerKey)}>
                {showOwnerKey ? language === "en" ? "Hide" : "Скрыть" : language === "en" ? "Show" : "Показать"}
              </button>
              <button className="mini-action" type="button" disabled={!ownerKey} onClick={copyKey}>
                {keyCopied ? <CheckCircle2 size={15} /> : <Copy size={15} />}
                {keyCopied ? language === "en" ? "Copied" : "Скопировано" : language === "en" ? "Copy" : "Скопировать"}
              </button>
            </div>
          </div>
          {dashboard?.ownerKey ? (
            <dl className="compact-dl">
              <div><dt>{language === "en" ? "Uses" : "Использований"}</dt><dd>{dashboard.ownerKey.uses} / {dashboard.ownerKey.maxUses}</dd></div>
              <div><dt>{language === "en" ? "Binding" : "Привязка"}</dt><dd>{dashboard.ownerKey.hwidBound ? ownerSteamBinding : language === "en" ? "free" : "свободен"}</dd></div>
              <div><dt>{language === "en" ? "Until" : "До"}</dt><dd>{dashboard.ownerKey.expiresAt || "—"}</dd></div>
            </dl>
          ) : null}
        </article>
      </div> : null}

      {project ? (
        <>
          <section className="dashboard-band" aria-label={language === "en" ? "Project stats" : "Статистика проекта"}>
            <Metric label={language === "en" ? "Checks" : "Проверок"} value={dashboard.stats.totalChecks} />
            <Metric label={language === "en" ? "Today" : "Сегодня"} value={dashboard.stats.todayChecks} />
            <Metric label={language === "en" ? "Active" : "Активные"} value={dashboard.stats.activeSessions} />
            <Metric label={language === "en" ? "Moderators" : "Модераторы"} value={dashboard.stats.moderators} />
          </section>

          <div className="dashboard-grid">
            <article className="dashboard-card">
              <div className="card-title-row">
                <div>
                  <h3>{language === "en" ? "Add moderator" : "Добавить модератора"}</h3>
                  <p>{language === "en" ? "Name is required. SteamID can be added later." : "Имя обязательно. SteamID можно указать позже."}</p>
                </div>
              </div>
              <form className="stack-form" onSubmit={createModerator}>
                <label>
                  <span>{language === "en" ? "Name" : "Имя"}</span>
                  <input
                    value={moderatorForm.name}
                    onChange={(event) => updateModeratorField("name", event.target.value)}
                    disabled={busy}
                    placeholder={language === "en" ? "Nickname or name" : "Ник или имя"}
                    maxLength={MAX_MODERATOR_NAME_LENGTH}
                  />
                </label>
                <label>
                  <span>SteamID</span>
                  <input
                    value={moderatorForm.steamId64}
                    onChange={(event) => updateModeratorField("steamId64", event.target.value)}
                    disabled={busy}
                    placeholder="7656..."
                    inputMode="numeric"
                    maxLength={17}
                    aria-invalid={Boolean(moderatorSteamError)}
                  />
                  {moderatorSteamError ? <p className="field-error">{moderatorSteamError}</p> : null}
                </label>
                <button className="panel-action" type="submit" disabled={busy || !moderatorForm.name.trim() || Boolean(moderatorSteamError)}>
                  {language === "en" ? "Add" : "Добавить"}
                </button>
              </form>
            </article>

            <article className="dashboard-card dashboard-card-wide">
              <div className="card-title-row">
                <div>
                  <h3>{language === "en" ? "Moderators" : "Модераторы"}</h3>
                  <p>{dashboard.moderators.length ? language === "en" ? "Project access." : "Доступы проекта." : language === "en" ? "Add the first moderator." : "Добавьте первого модератора."}</p>
                </div>
              </div>
              <div className="moderator-list">
                {dashboard.moderators.length ? dashboard.moderators.map((moderator) => (
                  <div className="moderator-row" key={moderator.id}>
                    <div>
                      <strong>{moderator.displayName || moderator.id}</strong>
                      <span>{moderator.steamId64 || (language === "en" ? "SteamID not set" : "SteamID не указан")}</span>
                    </div>
                    <span>{moderator.steamId64 || (language === "en" ? "SteamID not set" : "SteamID не указан")}</span>
                    <div className="row-actions">
                      <button
                        className="mini-action"
                        type="button"
                        disabled={busy}
                        onClick={() => renameModerator(moderator.id, moderator.displayName || moderator.id)}
                      >
                        {language === "en" ? "Rename" : "Переименовать"}
                      </button>
                      <button
                        className="mini-action is-danger"
                        type="button"
                        disabled={busy}
                        onClick={() => removeModerator(moderator.id, moderator.displayName || moderator.id)}
                      >
                        {language === "en" ? "Remove" : "Удалить"}
                      </button>
                    </div>
                  </div>
                )) : <p className="muted-line">{language === "en" ? "No moderators yet." : "Модераторы ещё не добавлены."}</p>}
              </div>
            </article>
          </div>

          <article className="dashboard-card dashboard-card-full">
            <div className="card-title-row">
              <div>
                <h3>{language === "en" ? "Latest checks" : "Последние проверки"}</h3>
              </div>
            </div>
            <div className="data-list">
              {checks.map((session) => (
                <div className="data-row" key={String(session.id)}>
                  <strong>{String(session.suspectName || session.suspectSteamId64 || session.id)}</strong>
                  <span>{String(session.moderatorName || (language === "en" ? "moderator not set" : "модератор не указан"))}</span>
                  <span>{String(session.status || "")}</span>
                  <em>{String(session.verdictLabel || session.duration || "")}</em>
                </div>
              ))}
              {!checks.length ? <p className="muted-line">{language === "en" ? "Checks will appear after the first sessions." : "Проверки появятся после первых сессий."}</p> : null}
            </div>
          </article>
        </>
      ) : null}
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="metric-card">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function LegalPage({
  document,
  copy,
}: {
  document: SiteCopy["legalDocuments"][keyof SiteCopy["legalDocuments"]];
  copy: SiteCopy;
}) {
  return (
    <article className="legal-page">
      <header className="legal-hero">
        <FileText size={28} />
        <p>{document.updated}</p>
        <h1>{document.title}</h1>
        <span>{document.lead}</span>
      </header>

      <div className="legal-sections">
        {document.sections.map((section) => (
          <section className="legal-section" key={section.title}>
            <h2>{section.title}</h2>
            <p>{section.text}</p>
          </section>
        ))}
      </div>

      <section className="legal-contact">
        <h2>{copy.legalContact.title}</h2>
        <a href="mailto:support@cheatchecker.ru">
          <Mail size={18} />
          {copy.legalContact.support}
        </a>
      </section>
    </article>
  );
}

function PasswordRecoveryPage({ language, form, updateField, showPassword, setShowPassword, submit, busy, error, message, state, retryExchange, requestNewLink, continueToLogin }: {
  language: Lang; form: AuthForm; updateField: (field: keyof AuthForm, value: string) => void; showPassword: boolean; setShowPassword: (value: boolean) => void; submit: (event: FormEvent<HTMLFormElement>) => void; busy: boolean; error: string; message: string; state: "idle" | "exchanging" | "ready" | "retryable_error" | "terminal_error" | "completed"; retryExchange: () => void; requestNewLink: () => void; continueToLogin: () => void;
}) {
  const [confirmation, setConfirmation] = useState("");
  const [confirmationError, setConfirmationError] = useState("");
  const confirmLabel = language === "en" ? "Confirm password" : "Подтвердите пароль";

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (confirmation !== form.password) {
      setConfirmationError(language === "en" ? "Passwords do not match." : "Пароли не совпадают.");
      return;
    }
    setConfirmationError("");
    submit(event);
  }

  return (
    <main className="recovery-page">
      <section className="recovery-card">
        <img src="/cc_logo.png" alt="CheatChecker" />
        <h1>{state === "completed" ? language === "en" ? "Password changed" : "Пароль изменён" : language === "en" ? "New password" : "Новый пароль"}</h1>
        <p>{state === "completed" ? language === "en" ? "Sign in with your new password." : "Войдите в аккаунт с новым паролем." : state === "exchanging" ? language === "en" ? "Preparing a secure recovery session." : "Подготавливаем безопасную сессию восстановления." : language === "en" ? "Set a new password for your account." : "Установите новый пароль для аккаунта."}</p>
        {state === "exchanging" ? <div className="recovery-status" aria-live="polite"><RefreshCw size={18} />{language === "en" ? "Please wait…" : "Пожалуйста, подождите…"}</div> : null}
        {state === "retryable_error" ? <div className="recovery-actions"><button className="submit-auth" type="button" onClick={retryExchange}>{language === "en" ? "Try again" : "Повторить"}<RefreshCw size={18} /></button></div> : null}
        {state === "terminal_error" ? <div className="recovery-actions"><button className="submit-auth" type="button" onClick={requestNewLink}>{language === "en" ? "Request a new link" : "Запросить новую ссылку"}<ArrowRight size={18} /></button></div> : null}
        {state === "completed" ? <div className="recovery-actions"><button className="submit-auth" type="button" onClick={continueToLogin}>{language === "en" ? "Sign in" : "Войти"}<ArrowRight size={18} /></button></div> : null}
        {state === "ready" ? <form className="auth-form" onSubmit={handleSubmit}>
          <label>
            <span>{language === "en" ? "New password" : "Новый пароль"}</span>
            <div className="field">
              <LockKeyhole size={18} />
              <input autoComplete="new-password" value={form.password} onChange={(event) => updateField("password", event.target.value)} placeholder={language === "en" ? "At least 8 characters" : "Минимум 8 символов"} disabled={busy} type={showPassword ? "text" : "password"} />
              <button className="ghost-icon" type="button" onClick={() => setShowPassword(!showPassword)} aria-label={language === "en" ? "Show password" : "Показать пароль"}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </label>
          <label>
            <span>{confirmLabel}</span>
            <div className="field">
              <LockKeyhole size={18} />
              <input aria-label={confirmLabel} autoComplete="new-password" value={confirmation} onChange={(event) => { setConfirmation(event.target.value); setConfirmationError(""); }} placeholder={language === "en" ? "Repeat password" : "Повторите пароль"} disabled={busy} type={showPassword ? "text" : "password"} />
            </div>
          </label>
          {confirmationError ? <p className="auth-note is-error">{confirmationError}</p> : null}
          <button className="submit-auth" type="submit" disabled={busy}>
            {busy ? language === "en" ? "Checking..." : "Проверяем..." : language === "en" ? "Change password" : "Сменить пароль"}
            <ArrowRight size={18} />
          </button>
        </form> : null}
        {error ? <p className="auth-note is-error">{error}</p> : null}
        {message ? <p className="auth-note is-success">{message}</p> : null}
      </section>
    </main>
  );
}

function AuthPanel({
  authMode,
  setAuthMode,
  form,
  updateField,
  showPassword,
  setShowPassword,
  submitAuth,
  startProviderAuth,
  startSteamAuth,
  user,
  authBusy,
  authMessage,
  authError,
  logout,
  language,
  developerReauthentication,
  recoveryMode,
}: {
  authMode: AuthMode;
  setAuthMode: (mode: AuthMode) => void;
  form: AuthForm;
  updateField: (field: keyof AuthForm, value: string) => void;
  showPassword: boolean;
  setShowPassword: (value: boolean) => void;
  submitAuth: (event: FormEvent<HTMLFormElement>) => void;
  startProviderAuth: (provider: AuthProvider) => void;
  startSteamAuth: () => void;
  user: SiteUser | null;
  authBusy: boolean;
  authMessage: string;
  authError: string;
  logout: () => void;
  language: Lang;
  developerReauthentication: boolean;
  recoveryMode: boolean;
}) {
  const isRegister = authMode === "register";
  const isForgot = authMode === "forgot";
  const isReset = authMode === "reset";
  const copy = siteCopy[language];
  const signedIn = Boolean(user) && !developerReauthentication && !recoveryMode;
  const authSubtitle = signedIn
    ? language === "en" ? "The account area is ready for projects, members and check history." : "Кабинет готовится. Здесь появятся проекты, участники и история проверок."
    : "";

  return (
    <section className="auth-panel" id="auth" aria-label={language === "en" ? "Sign in and registration" : "Вход и регистрация"}>
      <div className="auth-tabs" role="tablist" aria-label={language === "en" ? "Auth mode" : "Режим входа"}>
        <button
          className={authMode === "login" ? "is-active" : ""}
          type="button"
          onClick={() => setAuthMode("login")}
        >
          {language === "en" ? "Sign in" : "Вход"}
        </button>
        {!developerReauthentication ? <button
          className={authMode === "register" ? "is-active" : ""}
          type="button"
          onClick={() => setAuthMode("register")}
        >
          {language === "en" ? "Register" : "Регистрация"}
        </button> : null}
      </div>

      <div className="auth-heading">
        <KeyRound size={23} />
        <div>
          <h2>{signedIn ? language === "en" ? "Signed in" : "Вы вошли" : developerReauthentication ? language === "en" ? "Sign in again" : "Войдите заново" : isRegister ? language === "en" ? "Create account" : "Создать аккаунт" : isForgot ? language === "en" ? "Reset password" : "Восстановить пароль" : isReset ? language === "en" ? "New password" : "Новый пароль" : language === "en" ? "Sign in" : "Войти в аккаунт"}</h2>
          {signedIn || developerReauthentication || isForgot || isReset ? (
            <p>{signedIn ? authSubtitle : developerReauthentication ? language === "en" ? "Sign in again to continue to the developer dashboard." : "Войдите ещё раз, чтобы продолжить в разраб-кабинет." : isForgot ? language === "en" ? "We will send a link if the account exists." : "Отправим ссылку на почту, если аккаунт существует." : language === "en" ? "Enter a new password." : "Введите новый пароль."}</p>
          ) : null}
        </div>
      </div>

      {signedIn ? (
        <div className="signed-card">
          <div>
            <span>{copy.account}</span>
            <strong>{user?.name || user?.email}</strong>
            <p>{user?.email}</p>
          </div>
          <button type="button" onClick={logout}>
            {language === "en" ? "Sign out" : "Выйти"}
          </button>
        </div>
      ) : (
        <>
          {!isForgot && !isReset ? (
            <>
              <div className="social-grid">
                <button type="button" disabled={authBusy} onClick={() => startProviderAuth("google")}>
                  <GoogleMark />
                  Google
                </button>
                <button type="button" disabled={authBusy} onClick={() => startProviderAuth("yandex")}>
                  <img
                    className="provider-icon provider-icon-yandex"
                    src="/yandex-auth-v2.png"
                    alt=""
                    aria-hidden="true"
                  />
                  {copy.providerNames.yandex}
                </button>
                <button className="steam-provider" type="button" disabled={authBusy} onClick={startSteamAuth}>
                  <img className="provider-icon provider-icon-steam" src="/brand_steam.png" alt="" aria-hidden="true" />
                  Steam
                </button>
              </div>

              <div className="divider">
                <span />
                <p>{language === "en" ? "or use email" : "или через email"}</p>
                <span />
              </div>
            </>
          ) : null}

          <form className="auth-form" onSubmit={submitAuth}>
            {!isReset ? (
              <label>
                <span>Email</span>
                <div className="field">
                  <Mail size={18} />
                  <input
                    autoComplete="email"
                    value={form.email}
                    onChange={(event) => updateField("email", event.target.value)}
                    placeholder="you@example.com"
                    disabled={authBusy}
                    type="email"
                  />
                </div>
              </label>
            ) : null}

            {!isForgot ? (
              <label>
                <span>{isReset ? language === "en" ? "New password" : "Новый пароль" : language === "en" ? "Password" : "Пароль"}</span>
                <div className="field">
                  <LockKeyhole size={18} />
                  <input
                    autoComplete={isRegister || isReset ? "new-password" : "current-password"}
                    value={form.password}
                    onChange={(event) => updateField("password", event.target.value)}
                    placeholder={isRegister || isReset ? language === "en" ? "At least 8 characters" : "Минимум 8 символов" : language === "en" ? "Enter password" : "Введите пароль"}
                    disabled={authBusy}
                    type={showPassword ? "text" : "password"}
                  />
                  <button
                    className="ghost-icon"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? language === "en" ? "Hide password" : "Скрыть пароль" : language === "en" ? "Show password" : "Показать пароль"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </label>
            ) : null}
            {isRegister ? <p className="auth-register-note">{language === "en" ? "The account will be created after email confirmation." : "Аккаунт будет создан только после подтверждения почты."}</p> : null}

            <button className="submit-auth" type="submit" disabled={authBusy}>
              {authBusy ? language === "en" ? "Checking..." : "Проверяем..." : isRegister ? language === "en" ? "Send email" : "Отправить письмо" : isForgot ? language === "en" ? "Recover" : "Восстановить" : isReset ? language === "en" ? "Change password" : "Сменить пароль" : language === "en" ? "Sign in" : "Войти"}
              <ArrowRight size={18} />
            </button>
          </form>
          {authMode === "login" ? (
            <button className="link-button" type="button" onClick={() => setAuthMode("forgot")}>
              {language === "en" ? "Forgot password?" : "Забыли пароль?"}
            </button>
          ) : null}
          {isReset ? <button className="link-button" type="button" onClick={() => setAuthMode("forgot")}>
            {language === "en" ? "Request a new link" : "Запросить новую ссылку"}
          </button> : null}
        </>
      )}

      {authError ? <p className="auth-note is-error">{authError}</p> : null}
      {authMessage ? (
        <p className="auth-note is-success">
          <CheckCircle2 size={17} />
          {authMessage}
        </p>
      ) : null}
    </section>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M21.6 12.23c0-.74-.07-1.45-.19-2.13H12v4.03h5.38a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.9-1.75 2.98-4.32 2.98-7.43Z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.7 0 4.97-.9 6.62-2.43l-3.24-2.51c-.9.6-2.04.96-3.38.96-2.6 0-4.8-1.75-5.58-4.11H3.08v2.59A10 10 0 0 0 12 22Z"
      />
      <path
        fill="#FBBC05"
        d="M6.42 13.9A6 6 0 0 1 6.1 12c0-.66.11-1.3.31-1.9V7.5H3.08A10 10 0 0 0 2 12c0 1.61.39 3.14 1.08 4.5l3.34-2.6Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.98c1.47 0 2.8.51 3.84 1.5l2.86-2.86A9.6 9.6 0 0 0 12 2a10 10 0 0 0-8.92 5.5l3.34 2.6C7.2 7.73 9.4 5.98 12 5.98Z"
      />
    </svg>
  );
}
